// FlashTools - Web Serial connection + KWP-style ECU flashing protocol.
// Command frames ported from the original flasher WorkerThread logic.

import { lookupEcm, findEcmIds } from './ecmDatabase';
import { probeYamahaKwp } from './yamahaKwp2000';
import { BridgeClient } from './bridge';
import { computeKey, computeKeyHonda, findWriteConstants, findRecoveryConstants, detectMcu } from '../../lib/shindengenKey';

export const CMD = {
  WAKEUP:     [0xFE, 0x04, 0x72, 0x8C],
  COLD_WAKE:  [0xFE, 0x04, 0xFF, 0xFF],
  INIT_ECM:   [0x72, 0x05, 0x00, 0xF0, 0x99],
  READ_ID:    [0x72, 0x07, 0x72, 0x00, 0x00, 0x05, 0x10],
  FLASH_CNT:   [0x7D, 0x06, 0x01, 0x01, 0x03, 0x78],
  FLASH_CNT_B: [0x7B, 0x06, 0x02, 0x01, 0x03, 0x79],
  KEEPALIVE:  [0x72, 0x05, 0x00, 0xF0, 0x99],
  KA_KEIHIN:  [0x7E, 0x06, 0x01, 0x01, 0x00, 0x7A],
  KA_SHIN:    [0x7C, 0x06, 0x02, 0x01, 0x00, 0x7B],
  ERASE:      [0x71, 0x05, 0x00, 0x00, 0x8A],
};

export function isWebSerialSupported() {
  return typeof navigator !== 'undefined' && !!navigator.serial;
}

// ISO-9141 / KWP2000 K-Line timing (ms) for the passive FTDI FT232R interface.
// Because the interface has no init chip, all timing is enforced in software.
export const KLINE = {
  W0: 300,   // bus idle before init
  P2: 25,    // request -> response gap
  P3: 55,    // response -> next request gap (min)
  BLOCK_GAP: 8, // gap between consecutive read blocks (kept small for speed)
  TURBO: 1,  // Fast Write: near-zero inter-command gap (MoToTan-style low latency)
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const hex = (b) => b.toString(16).toUpperCase().padStart(2, '0');
// Cap noisy byte dumps (e.g. an idle-high K-Line flood) so real frames stay readable.
const hexCap = (arr, max = 48) => {
  const a = Array.from(arr);
  const head = a.slice(0, max).map(hex).join(' ');
  return a.length > max ? `${head} …(+${a.length - max} more)` : head;
};
const sameBytes = (a, b, n) => {
  for (let i = 0; i < n; i++) if (a[i] !== b[i]) return false;
  return true;
};

// Extract the 5-byte ECM ID from a raw KWP reply (echo + response).
// Honda ECM IDs are always 5 bytes beginning with 0x01. Prefer a window that
// resolves in the database; otherwise the first 0x01-led window; else offset 5.
export function extractEcmId(reply) {
  const b = Array.from(reply);
  let firstMatch = null;
  for (let i = 0; i + 5 <= b.length; i++) {
    if (b[i] === 0x01) {
      const cand = b.slice(i, i + 5).map(hex).join('');
      if (firstMatch === null) firstMatch = cand;
      if (lookupEcm(cand)?.known) return cand;
    }
  }
  if (firstMatch) return firstMatch;
  return b.length >= 10 ? b.slice(5, 10).map(hex).join('') : '-';
}

// Best-effort ECM ID from a loaded BIN. Uses the Honda ID record scanner (0x00 marker +
// 01 xx …) and accepts ONLY an EXACT database match — never a 6-char prefix guess, which
// used to mis-read e.g. a K25-901 image (0101340F01) as KVK-902 because a stray
// `01 00 6E ..` window prefix-matched `01006E0801`. Returns null when no exact ID is found.
export function ecmIdFromBin(bytes) {
  if (!bytes || !bytes.length) return null;
  const ids = findEcmIds(bytes);
  const pick = ids.find((x) => x.exact && !x.recovery) || ids.find((x) => x.exact) || null;
  if (!pick) return null;
  const info = lookupEcm(pick.id);
  return { ecmId: pick.id, part: info.part, maker: info.maker, mcu: info.mcu, start: info.start, checksum: info.checksum };
}

// ------------------------------------------------------------------ Shindengen EEPROM (K1AA-N13)
// Cold read/write of the 512-byte (256-word) immobilizer EEPROM. The tight cold wake + Security
// Access (level DF) can ONLY run inside the NATIVE BRIDGE (same reason Cold Read and Clear Flash
// Count are native) — a JS/WebSocket framed path can't hit the sub-second boot window, so it never
// wakes the bus. These call the bridge's eepromRead/eepromWrite (v1.9.15+), which reuse the proven
// ClearCount session: wake → INIT → READ_ID → flash-count → SEC(DF) → word loop.
//   Word read  : 07 40 <addr> 00 <cs>            Word write : 09 41 <addr> 00 <d0> <d1> <cs>
// The DF Security frames are the SAME ones proven-accepted on K1AA hardware by Clear Flash Count.
const SHIN_EE_SEC_DF = [
  [0x91, 0x91, 0x0d, 0xdf, 0x9e, 0x8d, 0x9a, 0x86, 0x90, 0x8a, 0x8c, 0x9b, 0x88],
  [0x91, 0x91, 0x0d, 0xdf, 0x92, 0x9e, 0x86, 0x96, 0x8b, 0x8d, 0x86, 0xc0, 0x6a],
];
const eeSecHex = () => SHIN_EE_SEC_DF.map((f) => f.map((b) => b.toString(16).padStart(2, '0')).join(''));
const eeVerGE = (v, a, b, c) => { const p = String(v || '').split('.').map((n) => parseInt(n, 10) || 0); return p[0] > a || (p[0] === a && (p[1] > b || (p[1] === b && (p[2] || 0) >= c))); };

export async function runReadEepromShindengen(manager, cb, sizeBytes = 512) {
  const { onLog, onProgress, onPhase } = cb;
  manager.abort = false;
  const t0 = Date.now();
  const words = Math.max(1, Math.floor(sizeBytes / 2));
  const need = sizeBytes > 512 ? [1, 9, 33] : [1, 9, 20];
  if (!(manager.mode === 'bridge' && manager.bridge && manager.bridge.eepromRead && eeVerGE(manager.bridgeVersion, need[0], need[1], need[2]))) {
    onLog('ERROR', `EEPROM Cold Read needs native bridge v${need.join('.')}+. Update TRMD Engine.`);
    return { result: 'failed', duration_ms: 0 };
  }
  onPhase && onPhase('reading');
  onLog('WARN', `EEPROM cold read: ${words} words (${sizeBytes} B). Keep ECU ON.`);
  const [sec1, sec2] = eeSecHex();
  try {
    let secLogged = false, idLogged = false;
    const res = await manager.bridge.eepromRead({ sec1, sec2, words }, (m) => {
      if (m.boot) {
        if (m.waiting) { const fx = (v) => (v ? String(v).replace(/(..)/g, '$1 ').trim() : '(none)'); onLog('INFO', `⚡ Waiting… ${m.left_s}s · wake=${m.wake ? '✓' : '✗'} init=${m.init ? '✓' : '✗'} id=${m.idok ? '✓' : '✗'} sec1=${m.sec1 ? '✓' : '✗'} sec2=${m.sec2 ? '✓' : '✗'} · wkRX ${fx(m.wkrx)} · initRX ${fx(m.initrx)}`); return; }
        if (m.idok && !idLogged) { onLog('INFO', 'Session OK.'); idLogged = true; }
        if ((m.sec1 !== undefined || m.sec) && !secLogged) { onLog(m.sec ? 'INFO' : 'WARN', `Security Access (DF) — SEC1 ${m.sec1 ? '✓' : '✗'} · SEC2 ${m.sec2 ? '✓' : '✗'}`); secLogged = true; }
        return;
      }
      if (m.progress != null) onProgress && onProgress('reading', Math.round((m.progress / m.total) * 100));
    });
    if (Array.isArray(res.samples)) res.samples.forEach((s, i) => onLog('UART', `RD word 0x${i.toString(16)} RX ${s ? s.replace(/(..)/g, '$1 ').trim() : '(none)'}`));
    const hexstr = res.data || '';
    const out = new Uint8Array(sizeBytes);
    for (let i = 0; i < sizeBytes; i++) out[i] = (i * 2 + 1 < hexstr.length) ? parseInt(hexstr.substr(i * 2, 2), 16) : 0xff;
    onPhase && onPhase('done');
    if (res.empty) onLog('WARN', `${res.empty}/${words} words returned no reply.`);
    if (res.empty >= words) {
      onLog('ERROR', `EEPROM read FAILED: 0/${words} words — no data.`);
      return { result: 'failed', bytes: null, duration_ms: Date.now() - t0 };
    }
    onLog('SUCCESS', `EEPROM read complete: ${sizeBytes} bytes · CRC32 ${crc32(out)} (${((Date.now() - t0) / 1000).toFixed(1)}s)`);
    return { result: res.empty > 16 ? 'partial' : 'success', bytes: out, duration_ms: Date.now() - t0 };
  } catch (e) {
    onPhase && onPhase('done');
    onLog('ERROR', `EEPROM read failed: ${e.message}`);
    if (e.why) onLog('WARN', e.why);
    return { result: 'failed', duration_ms: Date.now() - t0 };
  }
}

// ------------------------------------------------------------------ Keihin K93 EEPROM (SPC560)
// Cold read of the 512-byte (256-word) EEPROM. Reuses the SAME native boot-window security/open
// engine proven for the K93 256KB flash Cold Read (HelloHo / wAreYou -> 07 08), then walks the
// EEPROM byte-address space with 82 82 10 06 <addr> -> 92 92 10 07 <d0> <d1> (2 bytes/address).
// Requires bridge v1.9.42+.
export async function runReadEepromKeihin(manager, opts, cb) {
  const { onLog, onProgress, onPhase, onSpeed } = cb;
  manager.abort = false;
  const t0 = Date.now();
  const pw = opts.password || 'HelloHowAreYou';
  const total = 512;
  if (!(manager.mode === 'bridge' && manager.bridge && manager.bridge.keihinEepromRead && eeVerGE(manager.bridgeVersion, 1, 9, 42))) {
    onLog('ERROR', 'K93 EEPROM Cold Read needs native bridge v1.9.42+. Update TRMD Engine.');
    return { result: 'failed', duration_ms: 0 };
  }
  onPhase && onPhase('reading');
  const pwHex = Array.from(pw).map((c) => (c.charCodeAt(0) & 0xff).toString(16).padStart(2, '0')).join('');
  onLog('WARN', `EEPROM read (512 B) — IGNITION OFF, turn ON when told.`);
  let openShown = false, lastRound = 0, onPrompted = false, lastReadLog = 0;
  try {
    const nr = await manager.bridge.keihinEepromRead({ pw: pwHex }, (m) => {
      if (m.boot) {
        if (m.opened) { if (!openShown) { openShown = true; onLog('SUCCESS', `Security OK (round ${m.round}). Reading…`); } return; }
        if (!onPrompted && m.round >= 1) { onPrompted = true; onLog('WARN', '🔑 IGNITION ON NOW!'); }
        if (m.round && m.round !== lastRound) { lastRound = m.round; if (m.wake || m.round % 15 === 0) onLog('INFO', `Round ${m.round} · wake ${m.wake ? '✓' : '✗'} · sess ${m.sess ? '✓' : '✗'} · sec ${m.sec ? '✓' : '✗'} · RX ${m.secr || 'none'} · probe ${m.probe || 'none'}`); }
        return;
      }
      if (m.progress != null) {
        const pct = Math.round((m.progress / total) * 100);
        onProgress && onProgress('read', pct);
        const kbps = m.elapsed_ms ? (m.progress / 1024) / (m.elapsed_ms / 1000) : 0;
        if (kbps) onSpeed && onSpeed(kbps);
        const now = Date.now();
        if (now - lastReadLog > 1000 || m.progress >= total) { lastReadLog = now; onLog('READ', `EEPROM 0x${m.progress.toString(16).toUpperCase().padStart(3, '0')} (${pct}%)`); }
      }
    }).catch((e) => { onLog('ERROR', `K93 EEPROM read failed: ${e.why || e.message}`); return null; });
    onPhase && onPhase('done');
    if (!nr || !nr.ok) return { result: 'failed', bytes: null, duration_ms: Date.now() - t0 };
    if (Array.isArray(nr.samples)) nr.samples.forEach((s, i) => onLog('UART', `RD addr 0x${i.toString(16)} RX ${s ? s.replace(/(..)/g, '$1 ').trim() : '(none)'}`));
    const hexstr = nr.data || '';
    const out = new Uint8Array(total);
    for (let i = 0; i < total; i++) out[i] = (i * 2 + 1 < hexstr.length) ? parseInt(hexstr.substr(i * 2, 2), 16) : 0xff;
    if (nr.empty >= total / 2) { onLog('ERROR', 'EEPROM read FAILED: 0/256 words — no data.'); return { result: 'failed', bytes: null, duration_ms: Date.now() - t0 }; }
    if (nr.empty) onLog('WARN', `${nr.empty}/256 word(s) returned no reply.`);
    onLog('SUCCESS', `K93 EEPROM read complete: ${total} bytes · CRC32 ${crc32(out)} (${((Date.now() - t0) / 1000).toFixed(1)}s)`);
    return { result: nr.empty > 8 ? 'partial' : 'success', bytes: out, duration_ms: Date.now() - t0 };
  } catch (e) {
    onPhase && onPhase('done');
    onLog('ERROR', `K93 EEPROM read failed: ${e.why || e.message}`);
    return { result: 'failed', bytes: null, duration_ms: Date.now() - t0 };
  }
}

// Keihin K93 EEPROM FORMAT — erase to 0xFF or 0x00, then read back 512 B for verification.
export async function runFormatEepromKeihin(manager, opts, cb) {
  const { onLog, onProgress, onPhase, onSpeed } = cb;
  manager.abort = false;
  const t0 = Date.now();
  const pw = opts.password || 'HelloHowAreYou';
  const fill = opts.fill === '00' ? '00' : 'ff';
  const total = 512;
  if (!(manager.mode === 'bridge' && manager.bridge && manager.bridge.keihinEepromFormat && eeVerGE(manager.bridgeVersion, 1, 9, 43))) {
    onLog('ERROR', 'K93 EEPROM Format needs native bridge v1.9.43+. Update TRMD Engine.');
    return { result: 'failed', duration_ms: 0 };
  }
  onPhase && onPhase('reading');
  const pwHex = Array.from(pw).map((c) => (c.charCodeAt(0) & 0xff).toString(16).padStart(2, '0')).join('');
  onLog('WARN', `⚠️ K93 EEPROM FORMAT → 0x${fill.toUpperCase()} — IGNITION OFF, turn ON when told.`);
  let openShown = false, lastRound = 0, onPrompted = false, lastReadLog = 0;
  try {
    const nr = await manager.bridge.keihinEepromFormat({ pw: pwHex, fill }, (m) => {
      if (m.boot) {
        if (m.opened) { if (!openShown) { openShown = true; onLog('SUCCESS', `Security OK (round ${m.round}). Formatting…`); } return; }
        if (!onPrompted && m.round >= 1) { onPrompted = true; onLog('WARN', '🔑 IGNITION ON NOW!'); }
        if (m.round && m.round !== lastRound) { lastRound = m.round; if (m.wake || m.round % 15 === 0) onLog('INFO', `Round ${m.round} · wake ${m.wake ? '✓' : '✗'} · sess ${m.sess ? '✓' : '✗'} · sec ${m.sec ? '✓' : '✗'} · RX ${m.secr || 'none'} · probe ${m.probe || 'none'}`); }
        return;
      }
      if (m.fmt) { onLog(m.done ? 'SUCCESS' : 'WARN', `FORMAT → 0x${(m.fill || fill).toUpperCase()} command ${m.done ? 'ACK ✓' : 'sent (no ack)'} — RX ${m.ack ? m.ack.replace(/(..)/g, '$1 ').trim() : '(none)'}. Reading back for verify…`); return; }
      if (m.progress != null) {
        const pct = Math.round((m.progress / total) * 100);
        onProgress && onProgress('read', pct);
        const kbps = m.elapsed_ms ? (m.progress / 1024) / (m.elapsed_ms / 1000) : 0;
        if (kbps) onSpeed && onSpeed(kbps);
        const now = Date.now();
        if (now - lastReadLog > 1000 || m.progress >= total) { lastReadLog = now; onLog('READ', `verify 0x${m.progress.toString(16).toUpperCase().padStart(3, '0')} (${pct}%)`); }
      }
    }).catch((e) => { onLog('ERROR', `K93 EEPROM format failed: ${e.why || e.message}`); return null; });
    onPhase && onPhase('done');
    if (!nr || !nr.ok) return { result: 'failed', bytes: null, duration_ms: Date.now() - t0 };
    if (Array.isArray(nr.samples)) nr.samples.forEach((sx, i) => onLog('UART', `verify addr 0x${i.toString(16)} RX ${sx ? sx.replace(/(..)/g, '$1 ').trim() : '(none)'}`));
    const hexstr = nr.data || '';
    const out = new Uint8Array(total);
    for (let i = 0; i < total; i++) out[i] = (i * 2 + 1 < hexstr.length) ? parseInt(hexstr.substr(i * 2, 2), 16) : 0xff;
    if (!nr.formatDone) onLog('WARN', 'Format: no ACK — check read-back.');
    onLog(nr.formatDone ? 'SUCCESS' : 'WARN', `K93 EEPROM format → 0x${fill.toUpperCase()} done. Read-back ${total} B · CRC32 ${crc32(out)} (${((Date.now() - t0) / 1000).toFixed(1)}s)`);
    return { result: nr.formatDone ? 'success' : 'partial', bytes: out, formatDone: !!nr.formatDone, duration_ms: Date.now() - t0 };
  } catch (e) {
    onPhase && onPhase('done');
    onLog('ERROR', `K93 EEPROM format failed: ${e.why || e.message}`);
    return { result: 'failed', bytes: null, duration_ms: Date.now() - t0 };
  }
}

// Keihin K93 EEPROM WRITE — GUIDED cold flow. Writes 256/512 B (128/256 words), then reads back to verify.
export async function runWriteEepromKeihin(manager, bytes, opts, cb) {
  const { onLog, onProgress, onPhase } = cb;
  manager.abort = false;
  const t0 = Date.now();
  const pw = (opts && opts.password) || 'HelloHowAreYou';
  if (!bytes || (bytes.length !== 256 && bytes.length !== 512)) { onLog('ERROR', `EEPROM file must be 256/512 B (got ${bytes ? bytes.length : 0} B).`); return { result: 'failed', duration_ms: 0 }; }
  const total = bytes.length;
  if (!(manager.mode === 'bridge' && manager.bridge && manager.bridge.keihinEepromWrite && eeVerGE(manager.bridgeVersion, 1, 9, 47))) {
    onLog('ERROR', 'Keihin EEPROM Write needs native bridge v1.9.47+. Update TRMD Engine.');
    return { result: 'failed', duration_ms: 0 };
  }
  onPhase && onPhase('writing');
  const pwHex = Array.from(pw).map((c) => (c.charCodeAt(0) & 0xff).toString(16).padStart(2, '0')).join('');
  const dataHex = Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
  onLog('WARN', `⚠️ EEPROM WRITE ${total} B — IGNITION OFF, turn ON when told.`);
  let openShown = false, lastRound = 0, onPrompted = false, lastLog = 0, verifyShown = false;
  try {
    const nr = await manager.bridge.keihinEepromWrite(dataHex, { pw: pwHex, diff: !!(opts && opts.k2f && eeVerGE(manager.bridgeVersion, 2, 0, 99)) }, (m) => {
      if (m.boot) {
        if (m.opened) { if (!openShown) { openShown = true; onLog('SUCCESS', `Security OK (round ${m.round}). Writing…`); } return; }
        if (!onPrompted && m.round >= 1) { onPrompted = true; onLog('WARN', '🔑 IGNITION ON NOW!'); }
        if (m.round && m.round !== lastRound) { lastRound = m.round; if (m.wake || m.round % 15 === 0) onLog('INFO', `Round ${m.round} · wake ${m.wake ? '✓' : '✗'} · sess ${m.sess ? '✓' : '✗'} · sec ${m.sec ? '✓' : '✗'} · RX ${m.secr || 'none'} · probe ${m.probe || 'none'}`); }
        return;
      }
      if (m.diffwords != null) { onLog('INFO', `Write: ${m.diffwords} word berbeda, ${m.same} sama (dilewati).`); return; }
      if (m.progress != null) {
        const isVerify = m.phase === 'verify';
        if (isVerify && !verifyShown) { verifyShown = true; onLog('INFO', 'All words written — reading back to verify…'); }
        const pct = Math.round((m.progress / m.total) * 100);
        onProgress && onProgress(isVerify ? 'read' : 'writing', pct);
        const now = Date.now();
        if (now - lastLog > 1000 || m.progress >= m.total) { lastLog = now; onLog(isVerify ? 'READ' : 'WRITE', `${isVerify ? 'verify ' : 'write '}0x${m.progress.toString(16).toUpperCase().padStart(3, '0')} (${pct}%)`); }
      }
    }).catch((e) => { onLog('ERROR', `Keihin EEPROM write failed: ${e.why || e.message}`); return null; });
    onPhase && onPhase('done');
    if (!nr || !nr.ok) return { result: 'failed', bytes: null, duration_ms: Date.now() - t0 };
    if (nr.failed) onLog('WARN', `${nr.failed}/${nr.words - (nr.skipped || 0)} written word(s) had no write-ACK.${nr.nack ? ` First reject: ${nr.nack}` : ''}`);
    if (nr.verifyEmpty) onLog('WARN', `${nr.verifyEmpty}/${nr.words} word(s) returned no read-back reply.`);
    if (nr.mismatch) onLog('ERROR', `Verify FAILED: ${nr.mismatch}/${nr.words} words mismatch.`);
    let out = null;
    if (nr.verifyData) { const h = nr.verifyData; out = new Uint8Array(total); for (let i = 0; i < total; i++) out[i] = (i * 2 + 1 < h.length) ? parseInt(h.substr(i * 2, 2), 16) : 0xff; }
    onLog(nr.verified ? 'SUCCESS' : 'WARN', `Keihin EEPROM write ${nr.verified ? 'DONE ✓' : 'completed with issues'}: ${nr.skipped ? `${nr.written - nr.skipped} ditulis, ${nr.skipped} sama` : `${nr.written}/${nr.words} words ACKed`} (${((Date.now() - t0) / 1000).toFixed(1)}s)`);
    return { result: nr.verified ? 'success' : 'partial', bytes: out, duration_ms: Date.now() - t0 };
  } catch (e) {
    onPhase && onPhase('done');
    onLog('ERROR', `Keihin EEPROM write failed: ${e.why || e.message}`);
    return { result: 'failed', bytes: null, duration_ms: Date.now() - t0 };
  }
}

// Keihin CLEAR FLASH COUNT — zeroes the write-history region (page 0x14, addr 0x00..0x7F) via the
// SAME boot-window security as the Keihin EEPROM tools. Replays the user capture 1:1 in the bridge.
export async function runClearCountKeihin(manager, opts, cb) {
  const { onLog, onProgress, onPhase } = cb;
  manager.abort = false;
  const t0 = Date.now();
  const pw = (opts && opts.password) || 'HelloHowAreYou';
  onLog('INFO', '=== CLEAR FLASH COUNT (Keihin) START ===');
  if (manager.simulated) { onLog('SUCCESS', 'Simulated: Keihin flash count cleared.'); return { ok: true }; }
  if (!(manager.mode === 'bridge' && manager.bridge && manager.bridge.keihinClearCount && eeVerGE(manager.bridgeVersion, 1, 9, 48))) {
    onLog('ERROR', 'Keihin Clear Flash Count needs native bridge v1.9.48+. Update TRMD Engine.');
    return { ok: false };
  }
  onPhase && onPhase('writing');
  const pwHex = Array.from(pw).map((c) => (c.charCodeAt(0) & 0xff).toString(16).padStart(2, '0')).join('');
  if (opts && opts.k2f && !eeVerGE(manager.bridgeVersion, 2, 0, 98)) {
    onLog('ERROR', 'K2F Clear Flash Count needs TRMD Engine v2.0.98+ (targeted words 3-4 only). Update TRMD Engine.');
    return { ok: false };
  }
  if (opts && opts.k93 && !eeVerGE(manager.bridgeVersion, 2, 1, 17)) { onLog('ERROR', 'K93 Clear Flash Count needs TRMD Engine v2.1.17+. Update TRMD Engine.'); return { ok: false }; }
  if (opts && opts.k2f) onLog('INFO', `Clearing ONLY flash-count words 3-4 (EEPROM 0x06-0x09)${opts.k93 ? ' + word 0 commit marker' : ''}.`);
  else onLog('WARN', 'Clearing FULL EEPROM words 0x00-0x7F (0x00-0xFF).');
  onLog('WARN', `⚠️ CLEAR FLASH COUNT — IGNITION OFF, turn ON when told.`);
  let openShown = false, lastRound = 0, onPrompted = false, lastLog = 0;
  if (opts && opts.k93) {
    const nr = await manager.bridge.keihinEepromWrite('00'.repeat(512), { pw: pwHex, zeroWords: '3,4', marker: 1 }, (m) => {
      if (m.boot && m.opened && !openShown) { openShown = true; onLog('SUCCESS', `Security OK (round ${m.round}). Clearing…`); }
      if (m.zstep) onLog(m.alive ? 'INFO' : 'WARN', `${m.zstep}: ECU ${m.alive ? `menjawab lagi setelah ${(m.ms / 1000).toFixed(1)}s` : 'diam 20s'}`);
    }).catch((e) => { onLog('ERROR', `Keihin clear flash count failed: ${e.why || e.message}`); return null; });
    onPhase && onPhase('done');
    if (!nr || !nr.ok) return { ok: false };
    onLog('INFO', `Sebelum: ${nr.before} · word0=${nr.word0}`);
    onLog(nr.failed ? 'WARN' : 'SUCCESS', `Sesudah: ${nr.readback} (target 3,4=0000 · 0=${nr.word0})`);
    if (nr.failed) onLog('WARN', `${nr.failed} write tanpa ACK.`);
    onLog(nr.failed ? 'WARN' : 'SUCCESS', `Keihin flash count clear ${nr.failed ? 'completed with issues' : 'DONE ✓'} (${((Date.now() - t0) / 1000).toFixed(1)}s). Matikan lalu nyalakan kunci kontak, lalu Connect.`);
    return { ok: !nr.failed };
  }
  try {
    const nr = await manager.bridge.keihinClearCount({ pw: pwHex, ...(opts && opts.k2f ? { words: '3,4' } : { fast: eeVerGE(manager.bridgeVersion, 2, 1, 18) }) }, (m) => {
      if (m.boot) {
        if (m.opened) { if (!openShown) { openShown = true; onLog('SUCCESS', `Security OK (round ${m.round}). Clearing…`); } return; }
        if (!onPrompted && m.round >= 1) { onPrompted = true; onLog('WARN', '🔑 IGNITION ON NOW!'); }
        if (m.round && m.round !== lastRound) { lastRound = m.round; if (m.wake || m.round % 15 === 0) onLog('INFO', `Round ${m.round} · wake ${m.wake ? '✓' : '✗'} · sess ${m.sess ? '✓' : '✗'} · sec ${m.sec ? '✓' : '✗'} · RX ${m.secr || 'none'} · probe ${m.probe || 'none'}`); }
        return;
      }
      if (m.progress != null) {
        const pct = Math.round((m.progress / m.total) * 100);
        onProgress && onProgress('writing', pct);
        const now = Date.now();
        if (now - lastLog > 800 || m.progress >= m.total) { lastLog = now; onLog('WRITE', `clear ${m.progress}/${m.total} (${pct}%)`); }
      }
    }).catch((e) => { onLog('ERROR', `Keihin clear flash count failed: ${e.why || e.message}`); return null; });
    onPhase && onPhase('done');
    if (!nr || !nr.ok) return { ok: false };
    if (nr.readback) onLog(nr.failed ? 'WARN' : 'SUCCESS', `Read-back words ${nr.readback.split(',').map((x) => { const [w, v] = x.split(':'); return `${w}=${v}`; }).join(' · ')} (expect 3,4=0000${nr.word0 ? ` · 0=${nr.word0}` : ''})`);
    // Full clear: K93/K84 never ACK the upper words; success = marker + words 0..4 (count words) ACKed (contiguous from 0).
    const full = (nr.cleared || 128) === 128;
    const good = full ? (129 - (nr.failed || 0)) >= 6 : !nr.failed;
    if (nr.failed && !good) onLog('WARN', `${nr.failed}/${(nr.cleared || 128) + 1} words no ACK — power-cycle & retry if needed.`);
    onLog(good ? 'SUCCESS' : 'WARN', `Keihin flash count clear ${good ? 'DONE ✓' : 'completed with issues'} (${((Date.now() - t0) / 1000).toFixed(1)}s). Matikan lalu nyalakan kunci kontak, lalu Connect.`);
    return { ok: good };
  } catch (e) {
    onPhase && onPhase('done');
    onLog('ERROR', `Keihin clear flash count failed: ${e.why || e.message}`);
    return { ok: false };
  }
}



export async function runWriteEepromShindengen(manager, bytes, cb) {
  const { onLog, onProgress, onPhase } = cb;
  manager.abort = false;
  const t0 = Date.now();
  if (!bytes || (bytes.length !== 512 && bytes.length !== 1024)) { onLog('ERROR', `EEPROM file must be 512/1024 B (got ${bytes ? bytes.length : 0} B).`); return { result: 'failed', duration_ms: 0 }; }
  const words = bytes.length / 2;
  const need = bytes.length > 512 ? [1, 9, 34] : [1, 9, 21];
  if (!(manager.mode === 'bridge' && manager.bridge && manager.bridge.eepromWrite && eeVerGE(manager.bridgeVersion, need[0], need[1], need[2]))) {
    onLog('ERROR', `EEPROM Cold Write needs native bridge v${need.join('.')}+. Update TRMD Engine.`);
    return { result: 'failed', duration_ms: 0 };
  }
  onPhase && onPhase('writing');
  onLog('WARN', `EEPROM cold write: ${words} words (${bytes.length} B).`);
  const [sec1, sec2] = eeSecHex();
  const dataHex = Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
  try {
    let secLogged = false, idLogged = false;
    await manager.bridge.eepromWrite(dataHex, { sec1, sec2, words }, (m) => {
      if (m.boot) {
        if (m.waiting) { const fx = (v) => (v ? String(v).replace(/(..)/g, '$1 ').trim() : '(none)'); onLog('INFO', `⚡ Waiting… ${m.left_s}s · wake=${m.wake ? '✓' : '✗'} init=${m.init ? '✓' : '✗'} id=${m.idok ? '✓' : '✗'} sec1=${m.sec1 ? '✓' : '✗'} sec2=${m.sec2 ? '✓' : '✗'} · wkRX ${fx(m.wkrx)} · initRX ${fx(m.initrx)}`); return; }
        if (m.idok && !idLogged) { onLog('INFO', 'Session OK.'); idLogged = true; }
        if ((m.sec1 !== undefined || m.sec) && !secLogged) { onLog(m.sec ? 'INFO' : 'WARN', `Security Access (DF) — SEC1 ${m.sec1 ? '✓' : '✗'} · SEC2 ${m.sec2 ? '✓' : '✗'}`); secLogged = true; }
        return;
      }
      if (m.progress != null) onProgress && onProgress('writing', Math.round((m.progress / m.total) * 100));
    });
    onPhase && onPhase('done');
    onLog('SUCCESS', `EEPROM write OK (${words} words, ${((Date.now() - t0) / 1000).toFixed(1)}s)`);
    return { result: 'success', duration_ms: Date.now() - t0 };
  } catch (e) {
    onPhase && onPhase('done');
    onLog('ERROR', `EEPROM write failed${e.word != null ? ` @word 0x${Number(e.word).toString(16)}` : ''}: ${e.message}`);
    if (e.why) onLog('WARN', e.why);
    return { result: 'failed', duration_ms: Date.now() - t0 };
  }
}

// Shared, module-level SerialManager so the bridge connection survives when the
// Flash Tools modal is closed — this lets the Map Editor poll live data DIRECTLY
// from the bridge while the map stays visible (on-bike live tracking).
let _sharedManager = null;
export function getSharedManager() {
  if (!_sharedManager) _sharedManager = new SerialManager();
  return _sharedManager;
}

// Renesas R8C/M16C bench read (FTDI TTL RX/TX/5V/GND, Renesas serial boot mode). Needs bridge
// v1.9.50+. The chip must ALREADY be in boot mode (MODE pin + reset via the user's board); the
// bridge only does UART: auto-baud (0x00 -> 0xB0) -> version (FB) -> page read (FF) across range.
export async function runRenesasRead(manager, opts, cb) {
  const { onLog, onProgress, onPhase } = cb;
  const toBytes = (h) => { const s = (h || '').replace(/[^0-9a-fA-F]/g, ''); const a = new Uint8Array(Math.floor(s.length / 2)); for (let i = 0; i < a.length; i++) a[i] = parseInt(s.substr(i * 2, 2), 16); return a; };
  if (!(manager.mode === 'bridge' && manager.bridge && manager.bridge.renesasRead && eeVerGE(manager.bridgeVersion, 1, 9, 50))) {
    onLog('ERROR', `Needs bridge v1.9.50+ (now v${manager.bridgeVersion || '-'}).`);
    return { result: 'error', bytes: null };
  }
  const { baud = 9600, start = 0x8000, size = 0x10000, idhex = '', chip = 'R5F21548G', blocks = [] } = opts || {};
  const hex8 = (n) => '0x' + ((n >>> 0).toString(16).toUpperCase().padStart(8, '0'));
  const kbLabel = (n) => `${Math.round(n / 1024)} K`;
  onPhase && onPhase('reading');
  onLog('INFO', `RENESAS ${chip} READ · ${baud} · 0x${start.toString(16).toUpperCase()} · ${size} B`);
  onLog('INFO', `Reading ${kbLabel(size)} from device (${hex8(start)} - ${hex8(start + size - 1)})`);
  onLog('WARN', 'Chip must be in boot mode; check RX/TX/5V/GND.');
  const started = Date.now();
  let r;
  try {
    r = await manager.bridge.renesasRead({ baud, start, size, idhex }, (m) => {
      if (m.log) onLog(m.level || 'INFO', m.log);
      if (m.progress != null && m.total) onProgress && onProgress('read', Math.round((m.progress / m.total) * 100));
    });
  } catch (e) {
    onLog('ERROR', `Renesas read failed: ${e.message}`);
    onPhase && onPhase('idle');
    return { result: 'error', bytes: null };
  }
  const bytes = toBytes(r.data || '');
  onLog(r.locked ? 'SUCCESS' : 'WARN', `Auto-baud ${r.locked ? 'LOCKED (0xB0) ✓' : 'GAGAL — tidak ada 0xB0. Cek boot-mode / wiring / baud.'}`);
  if (r.version) onLog('INFO', `Version: ${r.version || '(empty)'}`);
  if (r.idsr) onLog('INFO', `ID check status (70): ${r.idsr}`);
  onLog(r.allff_pages === r.pages ? 'WARN' : 'INFO', `Pages semua 0xFF: ${r.allff_pages}/${r.pages}${r.allff_pages === r.pages ? ' → chip kemungkinan ID-protected (isi ID) atau kosong.' : ''}`);
  const secs = Math.max(1, Math.round((Date.now() - started) / 1000));
  const rawChecksum = bytes.reduce((a, v) => (a + v) >>> 0, 0) >>> 0;
  onLog('SUCCESS', `Successfully read ${bytes.length} Bytes from device`);
  onLog('INFO', `${kbLabel(bytes.length)} read from device in ${secs} seconds`);
  onLog('SUCCESS', `  [Raw Checksum: ${hex8(rawChecksum)}]`);
  const idClean = (idhex || '').replace(/[^0-9a-fA-F]/g, '');
  if (idClean.length >= 2) {
    const idBytes = idClean.match(/.{1,2}/g).map((h) => '0x' + h.toUpperCase()).join(' ');
    onLog('INFO', `ID Code = ${idBytes} `);
  }
  for (const blk of blocks) {
    onLog('INFO', `Block ${blk.name} (${hex8(blk.start)} - ${hex8(blk.start + blk.size - 1)}, ${kbLabel(blk.size)}) marked as written`);
  }
  onPhase && onPhase('idle');
  return { result: r.locked ? 'success' : 'partial', bytes, duration_ms: Date.now() - started, locked: !!r.locked };
}

// Renesas R8C/M16C WRITE (bridge v1.9.51+): erase-all -> page program -> optional verify. DESTRUCTIVE.
export async function runRenesasWrite(manager, opts, cb) {
  const { onLog, onProgress, onPhase } = cb;
  const toHexStr = (u8) => Array.from(u8).map((b) => b.toString(16).padStart(2, '0')).join('');
  if (!(manager.mode === 'bridge' && manager.bridge && manager.bridge.renesasWrite && eeVerGE(manager.bridgeVersion, 1, 9, 51))) {
    onLog('ERROR', `Needs bridge v1.9.51+ (now v${manager.bridgeVersion || '-'}).`);
    return { result: 'error' };
  }
  const { baud = 9600, start = 0x8000, bytes, erase = true, verify = true, idhex = '', chip = 'R5F21548G', blocks = [], skipFF = true } = opts || {};
  if (!bytes || !bytes.length) { onLog('ERROR', 'Load a .bin file before Write.'); return { result: 'error' }; }
  onPhase && onPhase('flashing');
  onLog('WARN', `⚠️ RENESAS ${chip} WRITE (UART ${baud} · start 0x${start.toString(16).toUpperCase()} · ${bytes.length} B · erase=${erase} verify=${verify})`);
  const started = Date.now();
  let r;
  try {
    r = await manager.bridge.renesasWrite({ baud, start, data: toHexStr(bytes), erase, verify, idhex, skipBlank: skipFF }, (m) => {
      if (m.log) onLog(m.level || 'INFO', m.log);
      if (m.progress != null && m.total) onProgress && onProgress(m.phase || 'write', Math.round((m.progress / m.total) * 100));
    });
  } catch (e) {
    onLog('ERROR', `Renesas write failed: ${e.message}`);
    onPhase && onPhase('idle');
    return { result: 'error' };
  }
  onLog(r.locked ? 'SUCCESS' : 'WARN', `Auto-baud ${r.locked ? 'LOCKED (0xB0) ✓' : 'GAGAL — cek boot-mode / wiring / baud.'}`);
  if (r.erasesr) onLog(r.erased ? 'SUCCESS' : 'WARN', `Erase status: ${r.erasesr}${r.erased ? ' ✓' : ' (cek)'}`);
  if (r.error) onLog('ERROR', r.error);
  if (verify) onLog(r.verifyOk ? 'SUCCESS' : 'ERROR', `Verify: ${r.verifyOk ? 'OK ✓' : `mismatch @0x${r.mismatchAt >= 0 ? r.mismatchAt.toString(16).toUpperCase() : '?'}`}`);
  onLog(r.ok ? 'SUCCESS' : 'ERROR', `Write ${r.ok ? 'BERHASIL' : 'GAGAL'} — ${r.written}/${r.pages} page ditulis${r.skipped != null ? `, ${r.skipped} page blank (0xFF) dilewati` : ''} dalam ${((Date.now() - started) / 1000).toFixed(1)}s`);
  if (r.ok) {
    const hex8 = (n) => '0x' + ((n >>> 0).toString(16).toUpperCase().padStart(8, '0'));
    const secs = Math.max(1, Math.round((Date.now() - started) / 1000));
    if (erase && blocks.length) {
      onLog('INFO', `Erasing ${blocks.length} blocks from device`);
      for (const blk of blocks) onLog('INFO', `Erased block ${blk.name} (${hex8(blk.start)} - ${hex8(blk.start + blk.size - 1)})`);
      onLog('SUCCESS', 'Erase complete');
    }
    const PAGE = 0x100;
    const runs = [];
    if (skipFF) {
      let rs = -1;
      for (let off = 0; off < bytes.length; off += PAGE) {
        const end = Math.min(off + PAGE, bytes.length);
        let blank = true;
        for (let i = off; i < end; i++) if (bytes[i] !== 0xFF) { blank = false; break; }
        if (!blank) { if (rs < 0) rs = off; }
        else if (rs >= 0) { runs.push([rs, off - 1]); rs = -1; }
      }
      if (rs >= 0) runs.push([rs, bytes.length - 1]);
    } else {
      runs.push([0, bytes.length - 1]);
    }
    for (const [a, b] of runs) onLog('INFO', `Writing image to device... [${hex8(start + a)} - ${hex8(start + b)}]`);
    onLog('INFO', 'Data programmed at the following positions:');
    let programmed = 0;
    for (const [a, b] of runs) { const len = b - a + 1; programmed += len; onLog('INFO', ` ${hex8(start + a)} - ${hex8(start + b)}      Length : ${hex8(len)}`); }
    onLog('SUCCESS', `${(programmed / 1024).toFixed(2)} K programmed in ${secs} seconds`);
    onLog('SUCCESS', 'Image written to device');
  }
  onPhase && onPhase('idle');
  return { result: r.ok ? 'success' : 'error', ...r };
}

// Tactrix OpenPort 2.0 / J2534 CAN-UDS write for Honda Keihin CRF300 (bridge v1.9.52+, Windows only).
// Protocol is derived from a real Honda-tool write capture: 29-bit CAN ISO15765, key = seed + 0x0200,
// write region 0x10000..0xFFFFF (0xF0000) in 1024-byte blocks. DESTRUCTIVE — arm before calling.
export async function runTactrixCrf300Write(manager, opts, cb) {
  const { onLog, onProgress, onPhase } = cb;
  const toHexStr = (u8) => Array.from(u8).map((b) => b.toString(16).padStart(2, '0')).join('');
  if (!(manager.mode === 'bridge' && manager.bridge && manager.bridge.tactrixWrite && eeVerGE(manager.bridgeVersion, 1, 9, 52))) {
    onLog('ERROR', `Needs bridge v1.9.52+ (now v${manager.bridgeVersion || '-'}).`);
    return { result: 'error' };
  }
  const { bytes, addr = 0x10000, size = 0xF0000, block = 1024, keydef = '', offset = 0x200 } = opts || {};
  if (!bytes || !bytes.length) { onLog('ERROR', 'Muat file .bin CRF300 dulu.'); return { result: 'error' }; }
  if (addr + size > bytes.length) { onLog('ERROR', `File too small (${bytes.length} B).`); return { result: 'error' }; }
  onPhase && onPhase('flashing');
  onLog('WARN', `⚠️ CRF300 WRITE · ${size} B · ${Math.ceil(size / block)} blocks. Do not unplug.`);
  const phaseMsg = {
    open: 'Membuka Tactrix (PassThruOpen → Connect ISO15765 500k → filter 18DAF110)…',
    session: 'Extended diagnostic session (10 03)…',
    security: 'Security access — seed 27 01 → key 27 02 (key = seed + 0x0200)…',
    'program-session': 'Programming session (10 02)…',
    erase: 'Erase routine (31 01 FF 00) — bisa beberapa detik…',
    transfer: 'Transfer data (36) — flashing…',
    finalize: 'Transfer exit (37) → verify (31 01 FF 01) → reset (11 01)…',
  };
  const started = Date.now();
  let r;
  try {
    r = await manager.bridge.tactrixWrite({ data: toHexStr(bytes), addr, size, block, arm: true, keydef, offset }, (m) => {
      if (m.phase && phaseMsg[m.phase] && m.progress == null) onLog('INFO', phaseMsg[m.phase]);
      if (m.seedInfo) onLog('INFO', `Seed 0x${m.seedInfo} → Key 0x${m.keyInfo}`);
      if (m.progress != null && m.total) onProgress && onProgress(m.phase || 'transfer', Math.round((m.progress / m.total) * 100));
    });
  } catch (e) {
    onLog('ERROR', `Tactrix write gagal: ${e.message}`);
    onPhase && onPhase('idle');
    return { result: 'error' };
  }
  if (!r || !r.ok) {
    const em = (r && r.error) || 'unknown';
    onLog('ERROR', `Tactrix write GAGAL: ${em}`);
    if (em.includes('nrc=0x36')) onLog('WARN', 'ECU locked (NRC 36) — IGNITION OFF 20s, then retry once.');
    else if (em.includes('nrc=0x35')) onLog('WARN', 'Key rejected (NRC 35) — send log to developer.');
    else if (em.includes('nrc=0x37')) onLog('WARN', 'requiredTimeDelayNotExpired — tunggu beberapa detik lalu coba lagi.');
    onPhase && onPhase('idle');
    return { result: 'error', ...(r || {}) };
  }
  if (r.ecuId) onLog('INFO', `ECU ID (22 F1 81): ${r.ecuId}`);
  onLog('INFO', `Seed 0x${r.seed} → Key 0x${r.key}`);
  onLog('SUCCESS', `✓ CRF300 WRITE OK — ${r.written} B in ${((Date.now() - started) / 1000).toFixed(1)}s.`);
  onPhase && onPhase('idle');
  return { result: 'success', ...r };
}

// Kawasaki ZX-25R CAN-UDS write via Tactrix Openport (bridge v2.0.23+, Windows only).
// Byte-perfect from a real Kawasaki-tool capture: 11-bit CAN 0x764/0x746, KWP2000-over-ISO15765,
// seed(6B)->key(5B)=top5 of (seed<<1), region 0x30000..0x200000 S-box encoded in 2048B blocks.
export async function runKawasakiZx25rWrite(manager, opts, cb) {
  const { onLog, onProgress, onPhase } = cb;
  const toHexStr = (u8) => Array.from(u8).map((b) => b.toString(16).padStart(2, '0')).join('');
  if (!(manager.mode === 'bridge' && manager.bridge && manager.bridge.kawasakiZxWrite && eeVerGE(manager.bridgeVersion, 2, 0, 24))) {
    onLog('ERROR', `Needs bridge v2.0.28+ (now v${manager.bridgeVersion || '-'}).`);
    return { result: 'error' };
  }
  const { bytes, addr = 0x30000, size = 0x1D0000, block = 2048, cks = '93C7' } = opts || {};
  if (!bytes || !bytes.length) { onLog('ERROR', 'Muat file .bin ZX-25R (ROM 2 MB) dulu.'); return { result: 'error' }; }
  if (addr + size > bytes.length) { onLog('ERROR', `File too small (${bytes.length} B) — need 2 MB.`); return { result: 'error' }; }
  onPhase && onPhase('flashing');
  onLog('WARN', `⚠️ ZX-25R WRITE · ${size} B · ${Math.ceil(size / block)} blocks. Do not unplug.`);
  const phaseMsg = {
    open: 'Membuka Tactrix (PassThruOpen → Connect ISO15765 500k 11-bit → filter 0x764/0x746)…',
    session: 'StartDiagnosticSession 10 80 → baca ECU ID (1A) → programming session 10 82…',
    security: 'Security access — seed 27 05 → key 27 06 (key = top 5 byte dari seed >> 1)…',
    'program-session': 'Tulis stamp (3B 81)…',
    erase: 'Erase routine (31 01 01) — bisa beberapa detik…',
    transfer: 'Transfer data (36) — flashing 928 blok…',
    finalize: 'Transfer exit (37) → checksum (34/36) → verify (31 01 02) → reset (11)…',
  };
  const started = Date.now();
  let r;
  try {
    r = await manager.bridge.kawasakiZxWrite({ data: toHexStr(bytes), addr, size, block, arm: true, cks }, (m) => {
      if (m.phase && phaseMsg[m.phase] && m.progress == null && m.wait == null) onLog('INFO', phaseMsg[m.phase]);
      if (m.wait != null) onLog('WARN', `ECU delay (NRC ${m.nrc || '37'}) — waiting ${m.wait}/12…`);
      if (m.seedInfo) onLog('INFO', `Seed (27 05): 0x${m.seedInfo} → Key (27 06): 0x${m.keyInfo}`);
      if (m.progress != null && m.total) onProgress && onProgress(m.phase || 'transfer', Math.round((m.progress / m.total) * 100));
    });
  } catch (e) {
    onLog('ERROR', `ZX-25R write gagal: ${e.message}`);
    onPhase && onPhase('idle');
    return { result: 'error' };
  }
  if (!r || !r.ok) {
    const em = (r && r.error) || 'unknown';
    onLog('ERROR', `ZX-25R write GAGAL: ${em}`);
    if (em.includes('nrc=0x35')) onLog('WARN', 'Key rejected (NRC 35) — send log to developer.');
    else if (em.includes('nrc=0x36')) onLog('WARN', 'ECU locked (NRC 36) — IGNITION OFF 20s, then retry.');
    else if (em.includes('nrc=0x37')) onLog('WARN', 'requiredTimeDelayNotExpired — tunggu beberapa detik lalu coba lagi.');
    onPhase && onPhase('idle');
    return { result: 'error', ...(r || {}) };
  }
  if (r.ecuId) onLog('INFO', `ECU ID (1A): ${r.ecuId}`);
  onLog('INFO', `Seed 0x${r.seed} → Key 0x${r.key} · Checksum 0x${r.cks}`);
  onLog('SUCCESS', `✓ ZX-25R WRITE OK — ${r.written} B in ${((Date.now() - started) / 1000).toFixed(1)}s. Cycle ignition.`);
  onPhase && onPhase('idle');
  return { result: 'success', ...r };
}

// Kawasaki ZX-25R SAFE unlock test (bridge v2.0.31+): session + security access only, no erase/write.
// Confirms the seed->key formula on real hardware. Returns { result, unlocked, seed, key, nrc }.
export async function runKawasakiZx25rUnlock(manager, cb) {
  const { onLog } = cb;
  if (!(manager.mode === 'bridge' && manager.bridge && manager.bridge.kawasakiZxUnlock && eeVerGE(manager.bridgeVersion, 2, 0, 31))) {
    onLog('ERROR', `Needs bridge v2.0.31+ (now v${manager.bridgeVersion || '-'}).`);
    return { result: 'error' };
  }
  onLog('INFO', 'TEST UNLOCK (safe, no write)…');
  let r;
  try {
    r = await manager.bridge.kawasakiZxUnlock((m) => {
      if (m.wait != null) onLog('WARN', `ECU minta delay (nrc 0x${m.nrc || '37'}) — menunggu ${m.wait}/12…`);
    });
  } catch (e) { onLog('ERROR', `Test unlock gagal: ${e.message}`); return { result: 'error' }; }
  if (!r || !r.ok) { onLog('ERROR', `Test unlock FAILED: ${(r && r.error) || 'unknown'}`); return { result: 'error', ...(r || {}) }; }
  onLog('INFO', `Seed 0x${r.seed} → Key 0x${r.key}`);
  if (r.unlocked) onLog('SUCCESS', '✓ UNLOCK OK — safe to WRITE.');
  else onLog('ERROR', `✗ UNLOCK rejected (NRC ${r.nrc || '?'}).`);
  return { result: r.unlocked ? 'success' : 'error', ...r };
}

// Kawasaki ZX-25R read-only ECU info over CAN (bridge v2.0.30+). Safe: diag session only (10 80),
// reads HW (1A), SW (1A 81), VIN (1A 80). No security, no erase/write. Returns { result, hw, sw, vin }.
export async function runKawasakiZx25rInfo(manager, cb) {
  const { onLog } = cb;
  if (!(manager.mode === 'bridge' && manager.bridge && manager.bridge.kawasakiZxInfo && eeVerGE(manager.bridgeVersion, 2, 0, 30))) {
    onLog('ERROR', `Needs bridge v2.0.30+ (now v${manager.bridgeVersion || '-'}).`);
    return { result: 'error' };
  }
  onLog('INFO', 'Tactrix CAN 500k · reading HW/SW/VIN…');
  let r;
  try { r = await manager.bridge.kawasakiZxInfo(); }
  catch (e) { onLog('ERROR', `Baca info ZX-25R gagal: ${e.message}`); return { result: 'error' }; }
  if (!r || !r.ok) { onLog('ERROR', `ZX-25R info FAILED: ${(r && r.error) || 'unknown'}`); return { result: 'error', ...(r || {}) }; }
  onLog('SUCCESS', `ZX-25R · HW ${r.hw || '-'} · SW ${r.sw || '-'} · VIN ${r.vin || '-'}`);
  return { result: 'success', hw: r.hw || '', sw: r.sw || '', vin: r.vin || '' };
}

// Tactrix read-only ECU identification over CAN-UDS (bridge v1.9.52+). Safe: no session change
// beyond 10 03, no erase/write. Returns { result, ecuId, f100, seed }.
// Denso Sahara 300 (K3H-B61) CAN read via Tactrix — read-only BA 01 (bridge v2.0.69+).
export async function runSaharaRead(manager, { probe = false, variant = 0 } = {}, cb) {
  const { onLog, onProgress = () => {} } = cb;
  if (!(manager.mode === 'bridge' && manager.bridge && manager.bridge.saharaRead && eeVerGE(manager.bridgeVersion, 2, 0, 69))) {
    onLog('ERROR', `Sahara 300 CAN read needs bridge v2.0.69+ with Tactrix (now v${manager.bridgeVersion || '-'}).`);
    return { result: 'error' };
  }
  if (!eeVerGE(manager.bridgeVersion, 2, 0, 75)) { onLog('ERROR', `Sahara read needs bridge v2.0.75+ (auto Denso/Keihin). Now v${manager.bridgeVersion || '-'}.`); return { result: 'error' }; }
  const vlabel = ['k0 (base)', 'byte-swapped', 'k0 +1', 'k0 \u2212\u00a01'][variant] || `v${variant}`;
  onLog('INFO', probe ? `Sahara 300 CAN · test read (session 03 + Security Access key [${vlabel}], 3 samples, read-only)…` : `Sahara 300 CAN · READ 768 KB (session 03 + Security Access key [${vlabel}], each block read twice)…`);
  const t0 = Date.now();
  let r;
  try {
    r = await manager.bridge.saharaRead({ probe, variant }, (m) => {
      if (m.phase) { onLog('UART', `Sahara: ${m.phase}`); return; }
      if (m.seedInfo) { onLog('INFO', `Security Access: seed 0x${m.seedInfo} → key 0x${m.keyInfo} (variant ${variant + 1}/4)`); return; }
      if (m.progress != null) {
        const pct = Math.round((m.progress / m.total) * 100);
        onProgress('read', pct);
        const kbps = m.elapsed_ms > 0 ? (m.progress / 1024) / (m.elapsed_ms / 1000) : 0;
        onLog('READ', `read 0x${m.progress.toString(16).toUpperCase().padStart(6, '0')} (${pct}%) ${kbps.toFixed(2)} KB/s`);
      }
    });
  } catch (e) { onLog('ERROR', `Sahara read failed: ${e.why || e.message}`); return { result: 'error' }; }
  if (!r || !r.ok) { onLog('ERROR', `Sahara read failed: ${(r && r.error) || 'unknown'}`); return { result: 'error' }; }
  onLog('INFO', `ECU ID (22 F1 81): ${r.ecuId || '-'} · session ${r.session}`);
  if (probe) {
    onLog('SUCCESS', `BA 01 OK · 0x000000: ${r.s0} · 0x010000: ${r.s1} · 0x0BFFF0: ${r.s2}`);
    return { result: 'ok' };
  }
  const s = (r.data || '').replace(/[^0-9a-fA-F]/g, '');
  const bytes = new Uint8Array(s.length / 2);
  for (let i = 0; i < bytes.length; i++) bytes[i] = parseInt(s.substr(i * 2, 2), 16);
  onLog('SUCCESS', `Sahara READ OK · ${bytes.length} B in ${((Date.now() - t0) / 1000).toFixed(1)}s`);
  return { result: 'ok', bytes };
}

const UDS_NRC = { 0x11: 'serviceNotSupported', 0x12: 'subFunctionNotSupported', 0x13: 'incorrectLength', 0x22: 'conditionsNotCorrect', 0x24: 'requestSequenceError', 0x31: 'requestOutOfRange', 0x33: 'securityAccessDenied', 0x35: 'invalidKey', 0x36: 'exceededAttempts', 0x37: 'timeDelayNotExpired', 0x7E: 'subFunctionNotSupportedInActiveSession', 0x7F: 'serviceNotSupportedInActiveSession' };
const describeRx = (rx) => {
  const m = /^7F([0-9A-F]{2})([0-9A-F]{2})/.exec((rx || '').replace(/\s/g, '').toUpperCase());
  return m ? `${rx}  (NRC 0x${m[2]} ${UDS_NRC[parseInt(m[2], 16)] || ''})` : rx;
};

// Sahara 300 staged read-only diagnosis (bridge v2.0.70+). Never sends a key, never erases/writes.
export async function runSaharaDiag(manager, cb) {
  const { onLog } = cb;
  if (!(manager.mode === 'bridge' && manager.bridge && manager.bridge.saharaDiag && eeVerGE(manager.bridgeVersion, 2, 0, 70))) {
    onLog('ERROR', `Sahara diagnose needs bridge v2.0.70+ with Tactrix (now v${manager.bridgeVersion || '-'}).`);
    return { result: 'error' };
  }
  onLog('INFO', 'Sahara 300 CAN · diagnose (read-only: sessions 01/03/02, 27 01 seed only, BA 01)…');
  let r;
  try {
    r = await manager.bridge.saharaDiag((m) => { if (m.step) onLog('UART', `TX ${m.step.tx}  →  RX ${describeRx(m.step.rx)}`); });
  } catch (e) { onLog('ERROR', `Sahara diagnose failed: ${e.why || e.message}`); return { result: 'error' }; }
  if (!r || !r.ok) { onLog('ERROR', `Sahara diagnose failed: ${(r && r.error) || 'unknown'}`); return { result: 'error' }; }
  // Denso/Sahara K3H seed→key candidate: key = ((seed+k0)*k1 + (seed % k2)) & 0xFFFF (k0=18883 k1=1787 k2=9301)
  const norm = (s) => (s || '').replace(/[^0-9a-fA-F]/g, '').toUpperCase();
  const seeds = (r.steps || []).filter((x) => norm(x.tx) === '2701' && /^6701/.test(norm(x.rx))).map((x) => norm(x.rx));
  for (const rx of seeds) {
    const b = rx.match(/../g).map((h) => parseInt(h, 16));
    const seed = b.length >= 4 ? ((b[2] << 8) | b[3]) : 0; // bytes after 67 01
    const key = (((seed + 18883) * 1787) + (seed % 9301)) & 0xFFFF;
    onLog('INFO', `Seed 0x${seed.toString(16).toUpperCase().padStart(4, '0')} → candidate key 0x${key.toString(16).toUpperCase().padStart(4, '0')} (Denso K3H k0=18883 k1=1787 k2=9301) — NOT sent (read-only).`);
  }
  if (r.baOk) onLog('SUCCESS', 'BA 01 accepted — read path is open in one of the tested sessions.');
  else onLog('WARN', 'BA 01 still rejected (Diagnose sends no key). Use Akses Read / Test Read — it sends Security Access (27 02) in session 03 first (bridge v2.0.72+).');
  return { result: 'ok', ...r };
}

// Sahara 300 session-03 Security Access (27) + BA 01 read test (bridge v2.0.73+). ONE key attempt per power-cycle.
// variant 0=k0, 1=byte-swap, 2=k0+1, 3=k0-1. Read-only: no erase/write. For your own ECU.
export async function runSaharaUnlock(manager, { variant = 0 } = {}, cb) {
  const { onLog } = cb;
  if (!(manager.mode === 'bridge' && manager.bridge && manager.bridge.saharaUnlock && eeVerGE(manager.bridgeVersion, 2, 0, 75))) {
    onLog('ERROR', `Sahara Security Access needs bridge v2.0.75+ with Tactrix (now v${manager.bridgeVersion || '-'}).`);
    return { result: 'error' };
  }
  const vlabel = ['k0 (base)', 'byte-swapped', 'k0 +1', 'k0 \u2212\u00a01'][variant] || `v${variant}`;
  onLog('INFO', `Sahara 300 CAN · Security Access key variant ${variant + 1}/4 [${vlabel}] + ~12s dwell + BA 01 read test (read-only)…`);
  let r;
  try {
    r = await manager.bridge.saharaUnlock(variant, (m) => { if (m.step) onLog('UART', `TX ${m.step.tx}  →  RX ${describeRx(m.step.rx)}`); });
  } catch (e) { onLog('ERROR', `Sahara Security Access failed: ${e.why || e.message}`); return { result: 'error' }; }
  if (!r || !r.ok) { onLog('ERROR', `Sahara Security Access failed: ${(r && r.error) || 'unknown'}`); return { result: 'error' }; }
  if (r.locked) { onLog('WARN', 'ECU LOCKED (exceededAttempts / timeDelay). Power-cycle ignition OFF then ON before trying the next key variant.'); return { result: 'locked', ...r }; }
  if (r.unlocked) onLog('SUCCESS', `Security Access GRANTED (27 02 accepted) — variant ${variant + 1} [${vlabel}].`);
  else onLog('WARN', `Security Access denied for variant ${variant + 1} [${vlabel}]. Power-cycle, then try the next variant.`);
  if (r.baOk) onLog('SUCCESS', 'BA 01 read is OPEN — full Read 768 KB should work now.');
  else if (r.unlocked) onLog('WARN', 'Access granted but BA 01 read still refused by the ECU.');
  return { result: r.unlocked ? 'ok' : 'denied', ...r };
}

export async function runTactrixEcuInfo(manager, cb) {
  const { onLog } = cb;
  if (!(manager.mode === 'bridge' && manager.bridge && manager.bridge.tactrixInfo && eeVerGE(manager.bridgeVersion, 1, 9, 52))) {
    onLog('ERROR', `Needs bridge v1.9.52+ (now v${manager.bridgeVersion || '-'}).`);
    return { result: 'error' };
  }
  onLog('INFO', 'Tactrix ECU info (read-only)…');
  let r;
  try { r = await manager.bridge.tactrixInfo(); }
  catch (e) { onLog('ERROR', `ECU Info gagal: ${e.message}`); return { result: 'error' }; }
  if (!r || !r.ok) { onLog('ERROR', `ECU info FAILED: ${(r && r.error) || 'unknown'}`); return { result: 'error', ...(r || {}) }; }
  const hexToBytes = (h) => { const s = (h || '').replace(/[^0-9a-fA-F]/g, ''); const a = []; for (let i = 0; i < s.length; i += 2) a.push(parseInt(s.substr(i, 2), 16)); return a; };
  const p = hexToBytes(r.ecuId);
  let hw = '', sw = '', appSwId = '';
  if (p.length >= 9 && p[0] === 0x62 && p[1] === 0xF1 && p[2] === 0x81) {
    hw = [p[4], p[5], p[6]].map((b) => b.toString(16).toUpperCase().padStart(2, '0')).join(' ');
    sw = [p[7], p[8]].map((b) => b.toString(16).toUpperCase().padStart(2, '0')).join(' ');
    const len = p[3] || (p.length - 4);
    appSwId = p.slice(4, 4 + len).map((b) => b.toString(16).toUpperCase().padStart(2, '0')).join('');
  }
  const parseDid = (h) => { const b = hexToBytes(h); if (b.length < 4 || b[0] !== 0x62) return ''; let s = ''; for (let i = 3; i < b.length; i++) { const c = b[i]; if (c === 0) continue; if (c >= 0x20 && c < 0x7f) s += String.fromCharCode(c); } return s.trim(); };
  const partNumber = parseDid(r.f191) || parseDid(r.f187);
  const model = (partNumber.includes('K99') || appSwId.startsWith('0106')) ? 'CRF300F' : '';
  const recovery = /^0+$/.test((r.ecuId || '').slice(-4)) ? true : false;
  onLog('SUCCESS', `ECU ID (22 F1 81): ${r.ecuId || '(kosong)'}`);
  if (appSwId) onLog('INFO', `applicationSoftwareIdentification: ${appSwId}`);
  if (hw) onLog('INFO', `HW : ${hw}  ·  SW : ${sw}`);
  if (partNumber) onLog('INFO', `ECU HW Number: ${partNumber}`);
  if (model) onLog('INFO', `Model: ${model}`);
  if (r.f802) onLog('INFO', `22 F8 02: ${r.f802}`);
  return { result: 'success', hw, sw, appSwId, partNumber, model, recovery, ...r };
}

// Tactrix SAFE security test — verify seed→key WITHOUT erasing/writing (bridge v1.9.57+).
export async function runTactrixSecTest(manager, opts, cb) {
  const { onLog } = cb;
  if (!(manager.mode === 'bridge' && manager.bridge && manager.bridge.tactrixSecTest && eeVerGE(manager.bridgeVersion, 1, 9, 57))) {
    onLog('ERROR', `Needs bridge v1.9.57+ (now v${manager.bridgeVersion || '-'}).`);
    return { result: 'error' };
  }
  const { offset = 0x200, keyhex = '', seedonly = false, keydef = '' } = opts || {};
  onLog('INFO', seedonly ? 'Read Seed only (27 01) — cek ECU, tanpa kirim key…' : (keydef ? `Test Security — key via HondaAlgo1 def 0x${keydef.toUpperCase()} (tanpa erase/write)…` : (keyhex ? `Test Security — key manual 0x${keyhex.toUpperCase()} (tanpa erase/write)…` : `Test Security — key = seed + 0x${offset.toString(16).toUpperCase()} (tanpa erase/write)…`)));
  let r;
  try { r = await manager.bridge.tactrixSecTest({ offset, keyhex, seedonly, keydef }); }
  catch (e) { onLog('ERROR', `Test Security gagal: ${e.message}`); return { result: 'error' }; }
  if (!r || !r.ok) {
    const em = (r && r.error) || 'unknown';
    onLog('ERROR', `Test Security GAGAL: ${em}`);
    if (em.includes('nrc=0x37')) onLog('WARN', 'ECU delay (NRC 37) — IGNITION OFF 20s, ON, wait 10s, retry.');
    else if (em.includes('nrc=0x36')) onLog('WARN', 'ECU locked (NRC 36) — IGNITION OFF 20s, then ON.');
    return { result: 'error', ...(r || {}) };
  }
  if (seedonly) { onLog('SUCCESS', `Seed: 0x${r.seed} (no key sent).`); return { result: 'success', ...r }; }
  onLog('INFO', `Seed: 0x${r.seed} · Key dicoba: 0x${r.key}`);
  if (r.unlocked) {
    onLog('SUCCESS', `✓ UNLOCKED · seed 0x${r.seed} → key 0x${r.key}`);
  } else {
    const nrcHex = r.nrc >= 0 ? '0x' + r.nrc.toString(16).toUpperCase().padStart(2, '0') : '?';
    onLog('ERROR', `Key 0x${r.key} rejected (NRC ${nrcHex}).`);
    if (r.nrc === 0x35) onLog('WARN', 'Key rejected (NRC 35).');
    else if (r.nrc === 0x36) onLog('WARN', 'ECU locked (NRC 36) — IGNITION OFF 20s, then ON.');
    else if (r.nrc === 0x37) onLog('WARN', 'ECU delay (NRC 37) — wait 10s, retry.');
  }
  return { result: 'success', ...r };
}

export class SerialManager {
  constructor() {
    this.port = null;
    this.reader = null;
    this.writer = null;
    this.connected = false;
    this.simulated = false;
    this.abort = false;
    this._rx = [];
    this._pumping = false;
    this.mode = 'webserial'; // 'webserial' | 'bridge' | 'sim'
    this.bridge = null;
    this.vbattMv = 0;
    this.keepAlive = CMD.KEEPALIVE;
  }

  // -------- connection --------
  async connect(baudRate = 10400) {
    this.mode = 'webserial';
    if (!isWebSerialSupported()) throw new Error('WEB_SERIAL_UNSUPPORTED');
    this.port = await navigator.serial.requestPort();
    await this.port.open({ baudRate, dataBits: 8, stopBits: 1, parity: 'none', flowControl: 'none' });
    this.writer = this.port.writable.getWriter();
    this.reader = this.port.readable.getReader();
    this.connected = true;
    this.simulated = false;
    this.baudRate = baudRate;
    this._startPump();
    await sleep(120); // let the line settle
    this._rx = []; // drain any power-on / stale bytes (mimics a D2XX purge)
    const info = this.port.getInfo ? this.port.getInfo() : {};
    return info;
  }

  connectSimulated() {
    this.mode = 'sim';
    this.connected = true;
    this.simulated = true;
    return { simulated: true };
  }

  // Connect through the local native FTDI K-Line bridge (ws://localhost:8765).
  async connectBridge({ url, device, baud = 10400, dll, protocol }) {
    this.mode = 'bridge';
    this.simulated = false;
    // Close any stale socket left over from a previous (possibly failed) attempt —
    // a lingering half-open WS can make the browser drop the next handshake before
    // it ever reaches the bridge (bridge never prints "client connected").
    if (this.bridge) { try { await this.bridge.close(); } catch (_) {} this.bridge = null; }
    // The first WS handshake to the native bridge occasionally drops (socket race /
    // bridge thread momentarily busy). Retry a few times with a fresh socket so an
    // intermittent miss auto-recovers instead of surfacing as a hard BRIDGE_UNREACHABLE.
    let lastErr = null;
    for (let attempt = 1; attempt <= 3; attempt++) {
      this.bridge = new BridgeClient(url);
      try { await this.bridge.connect(); lastErr = null; break; }
      catch (e) {
        lastErr = e;
        try { await this.bridge.close(); } catch (_) {}
        this.bridge = null;
        if (attempt < 3) await sleep(500);
      }
    }
    if (lastErr) throw lastErr;
    try { const pong = await this.bridge.ping(); this.bridgeVersion = (pong && pong.version) || 'unknown'; }
    catch (_) { this.bridgeVersion = 'unknown'; }
    if (dll === 'sm3wifi' || dll === 'sm2') {
      if (!eeVerGE(this.bridgeVersion, 2, 1, 22)) throw new Error(`TRMD Engine v${this.bridgeVersion} belum mendukung ${dll === 'sm2' ? 'SM2 USB' : 'SM3 Wi-Fi'} — jalankan TRMD Engine v2.1.22 terbaru.`);
    }
    if (dll === 'sm3') {
      const v = String(this.bridgeVersion).split('.').map((n) => parseInt(n, 10) || 0);
      if (v.length < 3 || v[0] * 1e6 + v[1] * 1e3 + v[2] < 2000086) throw new Error(`TRMD Engine v${this.bridgeVersion} belum mendukung SM3 USB — keluar dari TRMD Engine lama (ikon tray → Keluar), lalu jalankan TRMD Engine v2.0.87 terbaru.`);
    }
    this._bridgeOpen = { device, baud, dll, protocol };
    const r = await this.bridge.open({ device, baud, dll, protocol });
    this.connected = true;
    this.vbattMv = r.vbatt_mv || 0;
    return r;
  }

  // Connect the WebSocket to the native bridge WITHOUT opening any FTDI/serial port.
  // Used by transports that talk their own device stack inside the bridge (Tactrix J2534),
  // where the normal serial `open` (K-Line wake / COM) must NOT run.
  async connectBridgeWsOnly({ url }) {
    this.mode = 'bridge';
    this.simulated = false;
    if (this.bridge) { try { await this.bridge.close(); } catch (_) {} this.bridge = null; }
    let lastErr = null;
    for (let attempt = 1; attempt <= 3; attempt++) {
      this.bridge = new BridgeClient(url);
      try { await this.bridge.connect(); lastErr = null; break; }
      catch (e) { lastErr = e; try { await this.bridge.close(); } catch (_) {} this.bridge = null; if (attempt < 3) await sleep(500); }
    }
    if (lastErr) throw lastErr;
    try { const pong = await this.bridge.ping(); this.bridgeVersion = (pong && pong.version) || 'unknown'; }
    catch (_) { this.bridgeVersion = 'unknown'; }
    this.connected = true;
    return { version: this.bridgeVersion };
  }

  async _startPump() {
    this._pumping = true;
    try {
      while (this._pumping && this.reader) {
        const { value, done } = await this.reader.read();
        if (done) break;
        if (value) for (const b of value) this._rx.push(b);
        // Cap the buffer so long write phases (which echo on the K-Line) don't grow it unbounded.
        if (this._rx.length > 16384) this._rx.splice(0, this._rx.length - 8192);
      }
    } catch (_) { /* stream closed */ }
  }

  async disconnect() {
    this._pumping = false;
    if (this.mode === 'bridge' && this.bridge) {
      await this.bridge.close();
      this.bridge = null;
    }
    try {
      if (this.reader) { await this.reader.cancel().catch(() => {}); this.reader.releaseLock(); }
      if (this.writer) { this.writer.releaseLock(); }
      if (this.port) await this.port.close();
    } catch (_) {}
    this.reader = null; this.writer = null; this.port = null;
    this.connected = false; this.simulated = false; this._rx = [];
    this.mode = 'webserial';
  }

  // Abort the current operation. Per-block loops poll this.abort, but a native batch
  // (flashblocks/readblocks) blocks the JS on ONE long RPC, so the flag alone can't
  // stop it — we also force-close the bridge socket to interrupt it immediately.
  requestAbort() {
    this.abort = true;
    if (this.mode === 'bridge' && this.bridge && this.bridge.abort) {
      try { this.bridge.abort(); } catch (_) {}
    }
  }

  // -------- low level --------
  // Honda K-Line hardware wake: hold the bus LOW ~70ms then release, matching the
  // native tool's InitFtdiIso (FT_SetBitMode bit-bang: write 0x00 -> TXD low, wait
  // 70ms, write 0x01 -> TXD high, then 130ms recovery before comms). The browser
  // equivalent of driving TXD low is a UART BREAK condition.
  async klineWake(lowMs = 70, highMs = 140) {
    if (this.mode === 'bridge' && this.bridge) {
      try { await this.bridge.wake(lowMs, highMs); this._breakOk = true; return true; }
      catch (_) { this._breakOk = false; return false; }
    }
    if (this.mode !== 'webserial' || !this.port || !this.port.setSignals) { this._breakOk = false; return false; }
    try {
      await this.port.setSignals({ break: true });
      await sleep(lowMs);
      await this.port.setSignals({ break: false });
      await sleep(highMs);
      this._breakOk = true;
      return true;
    } catch (_) {
      this._breakOk = false;
      return false;
    }
  }

  async write(bytes) {
    if (this.mode === 'sim') return;
    if (this.mode === 'bridge') {
      if (!this.bridge) throw new Error('bridge socket lost — reconnect');
      return this.bridge.write(bytes);
    }
    if (!this.writer) throw new Error('serial port not open — reconnect');
    await this.writer.write(new Uint8Array(bytes));
  }

  // Change baud on the fly. Bridge (native FTDI VCP) only — Web Serial can't
  // re-baud without close/reopen (which resets the K-Line). Returns true on success.
  async setBaud(baud) {
    if (this.mode === 'bridge' && this.bridge && this.bridge.setBaud) {
      try { await this.bridge.setBaud(baud); this.baudRate = baud; return true; }
      catch (_) { return false; }
    }
    return false;
  }

  async _waitFor(len, timeout) {
    const start = Date.now();
    while (Date.now() - start < timeout) {
      if (this._rx.length >= len) break;
      await sleep(6);
    }
    const out = this._rx.splice(0, this._rx.length);
    return new Uint8Array(out);
  }

  // Command exchange. Web Serial K-Line is half-duplex (reply = echo + response);
  // the native FTDI K-Line bridge returns only the ECU response (no TX echo).
  async sendCommand(cmd, { timeout = 400, expect = cmd.length + 6, pre = KLINE.P3 } = {}) {
    if (this.mode === 'sim') return new Uint8Array(0);
    if (this.mode === 'bridge') {
      if (!this.bridge) throw new Error('bridge socket lost — reconnect');
      if (pre) await sleep(Math.min(pre, KLINE.BLOCK_GAP));
      const rx = await this.bridge.writeRead(cmd, timeout);
      this.echoOk = true; // J2534 manages the line; echo not applicable
      return rx;
    }
    if (pre) await sleep(pre);
    this._rx = [];
    await this.write(cmd);
    await sleep(KLINE.P2); // request -> response gap
    const reply = await this._waitFor(expect, timeout);
    if (reply.length) {
      this.echoOk = reply.length >= cmd.length && sameBytes(reply, cmd, cmd.length);
    }
    return reply;
  }

  // Robust command exchange for the FTDI K-Line. Some cables echo the TX bytes
  // back (RXD sees the shared bus), others are transmit-only and RXD carries ONLY
  // the ECU reply. We therefore scan the whole RX buffer for a checksum-valid
  // Honda frame, skipping the TX echo if it happens to be present. Returns
  // { ok, reply, raw }; `reply` is the ECU frame (echo excluded).
  async sendFramed(cmd, { timeout = 500, pre = KLINE.P2 } = {}) {
    if (this.mode === 'sim') return { ok: false, reply: [], raw: [] };
    if (this.mode === 'bridge') {
      if (!this.bridge) throw new Error('bridge socket lost — reconnect');
      // Inter-command pacing (matches the reference tool's per-block sleepMs). In
      // bridge mode this was previously ignored, so SAFE write (Fast Write OFF) sent
      // blocks back-to-back with no page-program time. Fast write keeps pre=0.
      if (pre) await sleep(pre);
      // The Windows J2534 bridge (Program.cs v2.0.5+) RE-PREPENDS our TX as a synthetic echo to mimic
      // the FTDI path; the Android/FTDI bridge returns the pure ECU reply. Strip a FULL leading TX echo
      // so `reply` is ALWAYS the pure ECU frame — matching the non-bridge scan() contract that every
      // direct-index parser (poll / erase / cmd-04 / cmd-05 / block-ACK / ID slice) relies on. Without
      // this, on Tactrix reply[3] read the 4th TX byte, not the ECU status (false "cmd-04 status 0x04").
      const raw = Array.from(await this.bridge.writeRead(cmd, timeout));
      const hasEcho = raw.length > cmd.length && cmd.every((b, i) => raw[i] === b);
      const rx = hasEcho ? raw.slice(cmd.length) : raw;
      const okc = rx.length >= 4 && ((rx.slice(0, rx[1] - 1).reduce((s, x) => s + x, 0) ^ 0xff) + 1 & 0xff) === rx[rx[1] - 1];
      return { ok: okc, reply: rx, raw };
    }
    if (pre) await sleep(pre);
    this._rx = [];
    await this.write(cmd);
    const scan = (buf) => {
      let start = 0;
      // Detect a leading TX echo (cable mirrors our whole command). While the
      // echo is still arriving, WAIT — never scan inside it, or a coincidental
      // checksum-valid frame in the echoed firmware payload is matched as a reply.
      const n = Math.min(buf.length, cmd.length);
      let echoing = n > 0;
      for (let j = 0; j < n; j++) if (buf[j] !== cmd[j]) { echoing = false; break; }
      if (echoing) {
        this.echoOn = true;
        if (buf.length < cmd.length + 2) return null; // wait for full echo + reply header
        start = cmd.length;
      }
      for (let i = start; i + 2 <= buf.length; i++) {
        const len = buf[i + 1];
        if (len < 4 || len > 200 || i + len > buf.length) continue;
        const frame = buf.slice(i, i + len);
        const calc = (((frame.slice(0, len - 1).reduce((s, x) => s + x, 0)) ^ 0xff) + 1) & 0xff;
        if (calc === frame[len - 1]) return frame;
      }
      return null;
    };
    const start = Date.now();
    while (Date.now() - start < timeout) {
      const frame = scan(this._rx);
      if (frame) return { ok: true, reply: frame, raw: this._rx.slice() };
      await sleep(2);
    }
    return { ok: false, reply: [], raw: this._rx.slice() };
  }

  // Like sendFramed, but tuned for the multi-frame ECM-ID reply: accumulate the
  // WHOLE response window and return the checksum-valid frame that carries the ID
  // (read-by-address positive response, starts with 0x02), so a short status frame
  // (72 07 ...) arriving first never short-circuits the read. This removes the
  // dependency on the FTDI latency timer being pre-lowered by MoToTan.
  async readIdFramed(cmd, { timeout = 900, pre = KLINE.P3 } = {}) {
    if (this.mode === 'sim') return { ok: false, reply: [], raw: [] };
    if (this.mode === 'bridge') return this.sendFramed(cmd, { timeout, pre });
    if (pre) await sleep(pre);
    this._rx = [];
    await this.write(cmd);
    const validAt = (raw, i, want02) => {
      const len = raw[i + 1];
      if (len < 4 || len > 200 || i + len > raw.length) return null;
      if (want02 && raw[i] !== 0x02) return null;
      const frame = raw.slice(i, i + len);
      const calc = (((frame.slice(0, len - 1).reduce((s, x) => s + x, 0)) ^ 0xff) + 1) & 0xff;
      return calc === frame[len - 1] ? frame : null;
    };
    const findFrame = (want02) => {
      for (let i = 0; i + 2 <= this._rx.length; i++) { const f = validAt(this._rx, i, want02); if (f) return f; }
      return null;
    };
    const start = Date.now();
    while (Date.now() - start < timeout) {
      if (findFrame(true)) { await sleep(15); break; } // ID frame seen — let it finish arriving
      await sleep(4);
    }
    const idFrame = findFrame(true) || findFrame(false);
    return { ok: !!idFrame, reply: idFrame ? Array.from(idFrame) : [], raw: this._rx.slice() };
  }

  // Wake-up + StartCommunication handshake is inlined in readDeviceInfoReal so the
  // same proven sequence drives both the Web Serial and native FTDI K-Line bridge.


  // -------- high level (real hardware) --------
  async _setSignals(dtr, rts) {
    if (this.mode !== 'webserial' || !this.port || !this.port.setSignals) return;
    try { await this.port.setSignals({ dataTerminalReady: dtr, requestToSend: rts }); } catch (_) {}
  }

  // One K-Line probe with the CURRENT line signals: fast-init low pulse, wakeup
  // messages, StartCommunication, then read the ECM-ID frame. Returns sendFramed result.
  async _probeOnce() {
    this._rx = [];
    await this.klineWake();
    for (let a = 1; a <= 2; a++) {
      await this.sendCommand(CMD.WAKEUP, { timeout: 250, pre: a === 1 ? 0 : KLINE.P3 });
    }
    await this.sendCommand(CMD.INIT_ECM, { timeout: 300 });
    return this.sendFramed(CMD.READ_ID, { timeout: 700, pre: KLINE.P3 });
  }

  // Read flash count via 7D then 7B; count = reply[6] of the ECU frame.
  async _readFlashCount(log, ecmId) {
    const isDenso = (lookupEcm(ecmId)?.maker || '') === 'Denso';
    if (isDenso) return this._readDensoCounter(log);
    for (const cmd of [CMD.FLASH_CNT, CMD.FLASH_CNT_B]) {
      const r = await this.sendFramed(cmd, { timeout: 900, pre: KLINE.P3 });
      if (r.raw.length) log('UART', `RX ${r.raw.map(hex).join(' ')}`);
      if (r.ok && r.reply.length > 6) { const n = r.reply[6]; log('SUCCESS', `Flash count: ${n}`); return String(n); }
    }
    if (isDenso) {
      log('INFO', 'Flash count: not reported (normal for Denso).');
    } else {
      log('WARN', 'Flash count: no valid response.');
    }
    return '-';
  }

  // Denso (LegacyDenso/K99/Titan) ID selectors — from the XTune 1.0.1 capture right after read-id:
  //   7B 06 03 01 03 → 0B 09 01 01 <4 bytes>   (reprogram counter, 32-bit BE)
  //   7B 06 03 01 02 → 0B 07 01 <3 bytes>      (aux counter)
  // Read-only diagnostic selectors (no auth, no flash mutation).
  async _readDensoCounter(log) {
    const fr = (a) => { const b = a.slice(); b.push(hondaChecksum(b)); return b; };
    let count = null, aux = null;
    for (const sel of [0x03]) {
      const r = await this.sendFramed(fr([0x7b, 0x06, 0x03, 0x01, sel]), { timeout: 900, pre: KLINE.P3 });
      const rep = r.reply || [];
      log('UART', `denso-id/${hex(sel)} RX ${rep.length ? rep.map(hex).join(' ') : '(none)'}`);
      if (rep.length >= 4 && rep[0] === 0x0b && rep[2] === 0x01) {
        const data = rep.slice(sel === 0x03 ? 4 : 3, rep[1] - 1);
        // selector 03 HW-verified 2026-10-19: stock 00 00 00 00 → after 2 writes FF FF 02 02
        // (bytes 0-1 = reprogrammed flag, bytes 2-3 = count + mirror). Count = byte 2.
        if (sel === 0x03) { count = data.length >= 3 ? data[2] : null; if (data.length >= 2) log('INFO', `Denso reprog flag ${hex(data[0])}${hex(data[1])}${data[0] === 0xff && data[1] === 0xff ? ' (sudah pernah di-flash)' : ' (stock)'}`); }
        else aux = data.reduce((a, b) => ((a << 8) | b) >>> 0, 0);
      }
    }
    if (count == null) { log('WARN', 'Flash count: Denso selector tidak menjawab.'); return '-'; }
    log('SUCCESS', `Flash count (Denso): ${count}`);
    return String(count);
  }

  // Denso Titan RH850 RECOVERY probe: when the runtime READ_ID is dead (app erased), the
  // bootloader itself still answers its OWN 7C/7B K-Line frames. Mirrors how Shindengen/Keihin
  // are detected in recovery (cold bootloader wake + bootloader-level probe, not the runtime ID).
  async _probeDensoRecovery(log) {
    if (this.mode !== 'bridge' || !this.bridge) return null;
    const fr = (a) => { const b = a.slice(); b.push(hondaChecksum(b)); return b; };
    try {
      // Fresh cold broadcast wake to (re)open the bootloader window.
      if (this.bridge.klineInit) {
        try { await this.bridge.klineInit({ low: 70, high: 140, bytes: Array.from(CMD.COLD_WAKE), timeout: 400 }); } catch (_) {}
      }
      await this.sendFramed(CMD.INIT_ECM, { timeout: 700, pre: KLINE.P2 });          // 72 05 00 F0 99
      // Bootloader status poll — the Denso write engine's own 7C 06 03 01 00 -> 0C 05 01 <state>.
      let r = await this.sendFramed(fr([0x7c, 0x06, 0x03, 0x01, 0x00]), { timeout: 1200, pre: KLINE.P2 });
      let alive = r.reply.length >= 4 && r.reply[0] === 0x0c && r.reply[2] === 0x01;
      if (!alive) {
        // Secondary confirm: security preamble step-0 -> 0B 05 02 <st>.
        const s0 = await this.sendFramed(fr([0x7b, 0x08, 0x03, 0x02, 0x58, 0x85, 0x79, 0x22]), { timeout: 1500, pre: KLINE.P2 });
        if (s0.reply.length >= 4 && s0.reply[0] === 0x0b) { alive = true; r = s0; }
      }
      if (!alive) return null;
      log('SUCCESS', `✓ Denso bootloader alive (RECOVERY).`);
      log('WARN', '⚠️ Denso RECOVERY — load 384KB BIN, then WRITE.');
      return {
        connection: 'ECU Connected (Recovery · Denso)',
        ecmId: '-', rawHex: r.reply.map(hex).join(''), flashCount: '-',
        part: 'Denso Titan (Recovery)', maker: 'Denso', chip: 'RH850', flash: '384KB',
        model: '', smartKey: '', region: '', displacement: '', year: '',
        startOffset: '10000', checksumOffset: '5EFFC', recovery: true,
      };
    } catch (e) { log('INFO', `Denso recovery probe skipped: ${e.message || e}`); return null; }
  }

  async readDeviceInfoReal(log) {
    const viaBridge = this.mode === 'bridge';
    let idRes = { ok: false, reply: [], raw: [] };
    // Reset live-data locks so a freshly connected ECU re-detects its protocol.
    this._liveTable = null; this._liveMiss = 0;
    this._diagTable = null; this._diagMiss = 0; this._diagOff = false; this._diagLast = null;

    {
      // Same proven handshake for BOTH the browser Web Serial and the native FTDI
      // K-Line bridge. The WAKE frame (FE 04 72 8C) never gets an ECU reply — that
      // is NORMAL and must NOT abort the session. Only the ECM-ID read decides
      // whether an ECU actually answered.
      //   FE 04 72 8C (x3) -> 72 05 00 F0 99 -> detect keep-alive -> keep-alive
      //   -> READ_ID -> flash count.
      log('INFO', viaBridge
        ? 'Native FTDI K-Line bridge: waking bus (message wakeup)...'
        : 'K-Line init: waking bus (message wakeup, 12V pull-up)...');
      await sleep(KLINE.W0);

      // A checksum-valid frame that equals a command we sent is just the K-Line TX
      // echo, NOT an ECU reply. Only a frame that differs from our command counts.
      const isReal = (r, cmd) => r && r.reply && r.reply.length >= 4 &&
        !(r.reply.length === cmd.length && sameBytes(r.reply, cmd, cmd.length));

      // Some ECUs (esp. Shindengen RH850) only answer READ_ID after StartCommunication
      // is actually accepted, which can take a couple of full wake+init cycles. Repeat
      // the WHOLE handshake per attempt (not just keep-alive) and stop only on a real
      // ECU reply — this replaces the "open MoToTan first, then close" workaround.
      // v2.0.42+: native cold-connect runs the whole wake+init+read loop inside the bridge (tight
      // sub-second timing, bit-bang re-wake, NO per-frame WebSocket overhead) — far faster & more
      // reliable on a cold Denso/RH850 than the JS loop below. One native RPC returns the ID frame.
      let fastGotId = false;
      if (this.mode === 'bridge' && this.bridge && this.bridge.coldConnect && eeVerGE(this.bridgeVersion, 2, 0, 42)) {
        log('INFO', 'K-Line native cold-connect…');
        try {
          const r = await this.bridge.coldConnect({ baud: 10400, attempts: 20, reopenEvery: 2, readTo: 800 });
          if (r.rx && r.rx.length >= 10 && r.rx[0] === 0x02) {
            idRes = { ok: true, reply: Array.from(r.rx), raw: Array.from(r.rx) };
            this.keepAlive = CMD.INIT_ECM; this.echoOn = true; fastGotId = true;
            log('UART', `RX ${hexCap(r.rx)}`);
            log('SUCCESS', `Cold-connect OK (${r.attempts}x).`);
          } else {
            log('INFO', `Cold-connect: no reply (${r.attempts}x) — retrying.`);
            if (r.trace) log('UART', `Native trace: ${r.trace.slice(0, 900)}`);
          }
        } catch (e) { log('INFO', `Native cold-connect skipped: ${e.message || e}`); }
      }

      let echoedOnce = false;
      for (let attempt = 1; attempt <= 16 && !fastGotId; attempt++) {
        // Re-arm the AUTHORITATIVE FTDI bit-bang cold wake (what the port `open` does: TXD LOW 70ms /
        // HIGH 120ms via D2XX) every few attempts — this is what actually wakes a dormant Denso/RH850,
        // so a single Connect click self-primes instead of needing a 2nd click. The proven INIT/READ_ID
        // windows below then catch the reply (short fast-shot reads missed it).
        if (attempt >= 2 && attempt % 3 === 2 && this._bridgeOpen && this.bridge && this.bridge.open) {
          log('INFO', 'Re-arming FTDI bit-bang cold wake (port re-open)…');
          try { await this.bridge.open(this._bridgeOpen); } catch (_) {}
        }
        // Cold Denso/RH850 ECUs need a real HARDWARE wake (70ms LOW break + FE 04 FF FF broadcast) and
        // the pulse only "takes" intermittently (field: connects after a few tries). So pulse on EVERY
        // attempt (up to 16) within a single Connect — one click keeps hammering the cold wake until the
        // ECU answers, so the user rarely needs to click twice.
        this._rx = [];
        let wk = new Uint8Array(0);
        if (this.mode === 'bridge' && this.bridge && this.bridge.klineInit) {
          // reference-style TIGHT cold wake: 70ms LOW break + FE 04 72 8C in ONE bridge lock (NO WebSocket
          // RPC gap). A cold Denso/RH850 wake-response window is short; the old klineWake()+sendCommand()
          // two-RPC path let the FE frame hit the wire AFTER the ECU re-slept. Atomic break+frame mirrors
          // the reference capture and wakes the ECU from cold far more consistently.
          for (let a = 1; a <= 3; a++) {
            if (attempt === 1 && a === 1) log('INFO', 'K-Line TIGHT cold wake: 70ms break + FE 04 FF FF broadcast.');
            if (attempt === 1) log('UART', `TX ${CMD.COLD_WAKE.map(hex).join(' ')}  (tight wake ${a}/3)`);
            try { wk = await this.bridge.klineInit({ low: 70, high: 140, bytes: Array.from(CMD.COLD_WAKE), timeout: 400 }); } catch (_) { wk = new Uint8Array(0); }
            if (wk && wk.length) break;
            await sleep(120);
          }
          if (attempt === 1 || attempt % 3 === 0) log('INFO', `K-Line wake${attempt === 1 ? '' : ` (${attempt}/16)`}`);
        } else {
          {
            const pulsed = await this.klineWake();
            if (attempt === 1 || attempt % 3 === 0) log(pulsed ? 'INFO' : 'WARN', pulsed
              ? `K-Line hardware wake: 70ms LOW break pulse sent${attempt === 1 ? ' (cold wake)' : ` (retry ${attempt}/16)`}.`
              : 'Browser blocked the break pulse — use Chrome/Edge desktop.');
          }
          for (let a = 1; a <= 3; a++) {
            if (attempt === 1) log('UART', `TX ${CMD.COLD_WAKE.map(hex).join(' ')}  (wake ${a}/3)`);
            wk = await this.sendCommand(CMD.COLD_WAKE, { timeout: 300, pre: a === 1 ? 0 : KLINE.P3 });
            if (wk.length) break;
            await sleep(120);
          }
        }
        if (wk.length && !echoedOnce) {
          const echoBack = wk.length >= CMD.COLD_WAKE.length && (sameBytes(wk, CMD.COLD_WAKE, CMD.COLD_WAKE.length) || sameBytes(wk, CMD.WAKEUP, CMD.WAKEUP.length));
          this.echoOn = echoBack; echoedOnce = true;
          log(echoBack ? 'SUCCESS' : 'INFO', echoBack
            ? 'K-Line echo detected — interface & 12V live.'
            : `K-Line RX ${hexCap(wk)}`);
        }
        // StartCommunication: standard init, else the Keihin keep-alive.
        const sc = await this.sendFramed(CMD.INIT_ECM, { timeout: 450, pre: KLINE.P2 }); // 72 05 00 F0 99
        this.keepAlive = CMD.INIT_ECM;
        if (!isReal(sc, CMD.INIT_ECM)) {
          const ka = await this.sendFramed(CMD.KA_KEIHIN, { timeout: 450, pre: KLINE.P2 }); // 7E 06 01 01 00 7A
          if (isReal(ka, CMD.KA_KEIHIN) && ka.reply[3] !== 0x0F) { this.keepAlive = CMD.KA_KEIHIN; if (attempt === 1) log('INFO', 'Using Keihin keep-alive (7E 06 01 01 00 7A).'); }
        }
        await this.sendFramed(this.keepAlive, { timeout: 350, pre: KLINE.P2 });
        // READ_ID: wait specifically for the ECU's ID frame (02 0B ...), skipping our echo.
        idRes = await this.readIdFramed(CMD.READ_ID, { timeout: 800, pre: KLINE.P3 });
        if (idRes.raw.length) log('UART', `RX ${hexCap(idRes.raw)}`);
        const idA = (isReal(idRes, CMD.READ_ID) && idRes.reply.length >= 10) ? idRes.reply.slice(5, 10).map(hex).join('') : '-';
        const idB = extractEcmId(idRes.raw);
        if (lookupEcm(idA)?.known || lookupEcm(idB)?.known || (isReal(idRes, CMD.READ_ID) && idRes.reply[0] === 0x02)) break;
        if (attempt < 16) { log('INFO', `No ECU reply — retry ${attempt}/16`); await sleep(100); }
      }
      if (!idRes.raw.length) log('WARN', 'No reply from ECU — check ignition ON.');
      // Yamaha KWP2000 fallback: if Honda's probe got no ID frame, try the Yamaha ISO14230 connect
      // (works on both FTDI and Tactrix J2534 via raw writeRead). Returns early with Yamaha info.
      const hondaId = (idRes.ok && idRes.reply.length >= 2 && idRes.reply[0] === 0x02);
      if (!hondaId) {
        try {
          const y = false ? await probeYamahaKwp(this, log) : null;  // Yamaha hidden for now
          if (y) {
            this._liveTable = null; this._liveMiss = 0;
            log('INFO', `ECM ID resolved: ${y.ecmId} (Yamaha)`);
            log('INFO', `Match: ${y.maker} ${y.part} [${y.chip}]`);
            return y;
          }
        } catch (e) { log('INFO', `Yamaha KWP probe skipped: ${e.message || e}`); }
        // Denso Titan RH850 recovery: erased app = no runtime ID, but the bootloader still
        // answers its own 7C/7B frames over K-Line (same idea as Shindengen/Keihin recovery).
        const densoRec = await this._probeDensoRecovery(log);
        if (densoRec) return densoRec;
      }
    }

    const rawHex = idRes.raw.map(hex).join('');
    // ECM ID is a fixed field in the read-by-address reply: [02, len, addr(3), ID(5), cs].
    let ecmId = '-';
    if (idRes.ok && idRes.reply.length >= 10) ecmId = idRes.reply.slice(5, 10).map(hex).join('');
    if (!lookupEcm(ecmId)?.known) {
      const scanned = extractEcmId(idRes.raw);
      if (lookupEcm(scanned)?.known || ecmId === '-') ecmId = scanned;
    }
    // A real ECU frame whose ID field reads all-00 = the ID was WIPED → RECOVERY / boot mode
    // (a prior flash was interrupted). Distinguish from a floating/absent bus (no frame, or all-FF).
    const gotFrame = (idRes.ok && idRes.reply.length >= 2 && idRes.reply[0] === 0x02) || (idRes.raw && idRes.raw.length > 3);
    const idKnown = lookupEcm(ecmId)?.known;
    let recoveryMode = false;
    if (/^0+$/.test(ecmId) && gotFrame) {
      recoveryMode = true;
      log('WARN', `⚠️ ECU ID ${ecmId} (all-zero) → RECOVERY mode.`);
      ecmId = '-';
    } else if (gotFrame && !idKnown && /^[0-9a-fA-F]{10}$/.test(ecmId) && /0000$/.test(ecmId)) {
      // Valid ECU frame + unknown ID whose LAST 2 bytes are 0000 (e.g. 0100DF0000) = the tail
      // of the ID was wiped by an interrupted flash → RECOVERY / boot mode. A healthy Keihin ID
      // always ends in 0F01/0F03/etc, never 0000, so a 0000 tail is a safe recovery signal.
      recoveryMode = true;
      log('WARN', `⚠️ ECU ID ${ecmId} (tail 0000) → RECOVERY mode.`);
    } else if (/^[fF]+$/.test(ecmId) || /^0+$/.test(ecmId)) {
      log('WARN', `Invalid ID (${ecmId}) — check wiring/ignition.`);
      ecmId = '-';
    }
    const flashCount = (ecmId !== '-' || recoveryMode) ? await this._readFlashCount(log, ecmId) : '-';
    const info = lookupEcm(ecmId);
    let origEcmId = '', origPart = '';
    try {
      if (!recoveryMode && info?.known && !info.recovery) localStorage.setItem('tt_last_ecu', JSON.stringify({ id: ecmId, ts: Date.now() }));
      const last = (recoveryMode || info?.recovery) ? JSON.parse(localStorage.getItem('tt_last_ecu') || 'null') : null;
      const lastInfo = last && Date.now() - last.ts < 12 * 3600 * 1000 ? lookupEcm(last.id) : null;
      if (lastInfo?.known && info?.known && lastInfo.maker === info.maker && lastInfo.mcu === info.mcu && last.id !== ecmId) {
        log('INFO', `Recovery: bootloader ID ${ecmId !== '-' ? ecmId : '0000'}${info?.known ? ` (${info.part})` : ''} — using original part from last connection ${last.id}.`);
        origEcmId = last.id;
        origPart = `${lastInfo.part.split(' · ')[0]} (Recovery)`;
      }
    } catch { /* storage unavailable */ }
    log('INFO', `ECM ID resolved: ${recoveryMode ? `${ecmId !== '-' ? ecmId : '0000'} → RECOVERY` : ecmId}`);
    if (origPart) log('INFO', `Match: ${info.maker} ${origPart} [${info.mcu}] (bootloader ${info.part})`);
    else if (info?.known) log('INFO', `Match: ${info.maker} ${info.part} [${info.mcu}]`);
    else if (recoveryMode && ecmId !== '-') log('WARN', `${ecmId.substring(0, 6)} not in database.`);
    else if (!recoveryMode) log('WARN', `ECM ID ${ecmId} not in database (Unknown ECU)`);
    if (info?.recovery) log('WARN', 'RECOVERY: bootloader seed-key active — try Seed-Key Bypass → Key Type.');
    return {
      connection: recoveryMode ? 'ECU Connected (Recovery)' : (ecmId !== '-' ? 'ECU Connected' : 'No Response'),
      ecmId,
      origEcmId,
      origPart,
      rawHex,
      flashCount,
      part: info?.part || '-',
      maker: info?.maker || (recoveryMode ? 'Unknown (Recovery)' : 'Unknown'),
      chip: info?.mcu || '-',
      flash: info?.flash || '',
      model: info?.model || '',
      smartKey: info?.smartKey || '',
      region: info?.region || '',
      displacement: info?.displacement || '',
      year: info?.year || '',
      startOffset: info?.start || '',
      checksumOffset: info?.checksum || '',
      recovery: info?.recovery || recoveryMode,
    };
  }
}

// Simulation profiles use REAL ECM IDs from the database so lookups resolve.
const SIM_IDS = ['0103790F03', '0104080F01', '01046B0F01', '0100F91103', '0102A40101'];

export function randomSimProfile() {
  const ecmId = SIM_IDS[Math.floor(Math.random() * SIM_IDS.length)];
  const info = lookupEcm(ecmId);
  return {
    ecmId,
    part: info.part,
    maker: info.maker,
    chip: info.mcu,
    startOffset: info.start,
    checksumOffset: info.checksum,
    flashCount: String(1 + Math.floor(Math.random() * 8)),
  };
}

// Unified flashing routine (drives progress + log callbacks in both modes)
export async function runFlash(manager, firmware, opts, cb) {
  const { startOffset = '0', checksumOffset = '0', fastWrite = false, verify = true } = opts;
  const { onLog, onProgress, onPhase } = cb;
  // Recovery-safe routing: a boot-mode ECU returns no ECM ID (maker shows "Unknown (Recovery)"),
  // but the loaded image still identifies the write protocol. Route the dedicated Keihin paths by
  // image signature FIRST so re-flashing works in recovery mode too (canFlash only needs connect).
  if (!manager.simulated) {
    const csU = (opts.checksumOffset || '').toUpperCase();
    const stU = (opts.startOffset || '').toUpperCase();
    const isSPCsig = /SPC/i.test(opts.mcu || '') || opts.protocol === 'keihin-spc560';
    // Shindengen images are marked by a 5A 5A 5A 5A ("ZZZZ") signature at the VERY END of the bin
    // (reference flasher GetEcuType checks this FIRST, before any Keihin size/checksum rule). Detect it
    // here so a Shindengen ECU whose image happens to be 256KB/3FFF8 (e.g. 30400-K81H-N52, even with an
    // Unknown code) is NOT misrouted to the Keihin SPC560 path — it goes to the Shindengen writer.
    {
      const fwB = firmware.bytes || new Uint8Array();
      const endHas = (sig) => fwB.length >= sig.length && sig.every((b, k) => fwB[fwB.length - sig.length + k] === b);
      const anyHas = (sig) => { for (let p = 0; p + sig.length <= fwB.length; p++) { let ok = true; for (let d = 0; d < sig.length; d++) if (fwB[p + d] !== sig[d]) { ok = false; break; } if (ok) return true; } return false; };
      if (endHas([0x5a, 0x5a, 0x5a, 0x5a])) {
        const isSV = anyHas([0x53, 0x56, 0x38, 0x35, 0x30]); // "SV850"
        const isSH = anyHas([0x53, 0x48, 0x38, 0x35, 0x30]); // "SH850"
        const rh850b = isSH && [0xa5, 0xa5, 0xa5, 0xa5, 0x00].every((b, k) => fwB[40960 + k] === b);
        const variant = isSV ? 'V850' : (rh850b ? 'RH850B' : (isSH ? 'RH850' : 'V850'));
        // Reference GetEcuType sets the write base: V850/RH850 → 0x10000, RH850B → 0xA000. The 384KB
        // V850 (KZRM-B21) is hardware-proven at its EXISTING base, so never override that one.
        // startOffset here is a BLOCK-ADDRESS SHIFT (file[0] → flash base), not a skip. 256KB K1AA/K1AL were
        // hardware-GOOD at base 0x0 (identity); the 0x10000 shift (2026-10-03) shifted the image 64KB → finalize 08→FB BAD.
        const shinStart = opts.startOffset;
        onLog('INFO', `Protocol: Shindengen ${variant} @0x${(shinStart || '0').toUpperCase()}`);
        return runFlashShindengen(manager, firmware, { ...opts, maker: 'Shindengen', shinVariant: variant, startOffset: shinStart }, cb);
      }
    }
    // Denso is only supported as Titan 384KB (RH850). Never send Keihin/Shindengen frames to a Denso ECU.
    if (opts.maker === 'Denso' && (firmware.size === 131072 || firmware.size === 98304)) {
      onLog('INFO', `Protocol: Denso ${firmware.size / 1024}KB`);
      return runFlashDenso(manager, firmware, opts, cb);
    }
    // 96KB is ONLY the Denso K99-B02 (CRF250F) profile. Never let it reach the generic 0x77 path (HW log
    // 2026-10-19: ERASE without ACK, stall at block 0). If the DB record says otherwise, abort before sending.
    if (firmware.size === 98304) {
      onLog('ERROR', '96KB hanya untuk Denso K99-B02 — set ECU type Denso (Admin DB) lalu WRITE lagi. Aborted — nothing sent.');
      return { result: 'failed', duration_ms: 0 };
    }
    if (opts.maker === 'Denso' && firmware.size !== 393216) {
      onLog('ERROR', `Denso ${firmware.size / 1024}KB write not supported (Denso 96KB/128KB/384KB only). Aborted — nothing sent.`);
      return { result: 'failed', duration_ms: 0 };
    }
    if (firmware.size === 57344 && (csU === 'DFEF' || csU === 'DFFE')) {
      onLog('INFO', 'Protocol: Keihin 56KB');
      return runFlashKeihin56K(manager, firmware, opts, cb);
    }
    if (firmware.size === 65536 && !isSPCsig) {
      onLog('INFO', 'Protocol: Keihin 64KB');
      return runFlashKeihinR8C(manager, firmware, opts, cb);
    }
    if (firmware.size === 49152 && (csU === '7600' || csU === '7601' || csU === '9FEF') && !isSPCsig) {
      onLog('INFO', 'Protocol: Keihin R8C 48KB');
      return runFlashKeihinR8C(manager, firmware, opts, cb);
    }
    // Keihin 128KB M16C (KWT-712/702, flash @0xE0000): SPC560/M16C 7E-8B protocol, NOT the generic
    // 0x77 H8 frames. Route by image signature so recovery mode (wiped ID, maker!='Keihin') works —
    // otherwise it falls through to the H8 path and the ECU never acks (write stalls at 0 bytes).
    if (firmware.size === 131072 && (csU === 'B000' || csU === '1FFF8' || stU === 'E0000')) {
      onLog('INFO', 'Protocol: Keihin SPC560 128KB');
      return runFlashKeihinSPC560(manager, firmware, opts, cb);
    }
    // Keihin 256KB (KEIHINOTHER): K2F (M16C, checksum @0x9FEF) and K93 (MPC560x, checksum @0x3FFF8).
    // Same 7E-8B 128-byte block protocol (PGM/-FI erase, blank-skip, 08/09/0A/0C/0D finalize). Route by
    // image signature so recovery mode (wiped ID → maker!='Keihin') still re-flashes correctly.
    if (firmware.size === 0x40000 && (csU === '3FFF8' || csU === '9FEF')) {
      onLog('INFO', 'Protocol: Keihin 256KB');
      return runFlashKeihinSPC560(manager, firmware, opts, cb);
    }
    // 384KB (0x60000) splits into TWO distinct Honda families that MUST NOT cross:
    //   • Denso Titan 160 K2G-B12 (RH850, 7C/0C unlock preamble 58 85 79) — image carries "K2G"/"K2GAF105".
    //   • Shindengen (VARIO 125 KZRM etc, V850/RH850, block-program protocol) — image carries "SV850"/"SH850".
    // The old `|| true` forced EVERY 384KB image to the Denso path, so a Shindengen VARIO got the Denso
    // preamble (ECU echoed it, abort). Route by connected maker + image signature so recovery mode works both ways.
    if (firmware.size === 393216) {
      const fw = firmware.bytes || new Uint8Array();
      const hasAscii = (str) => {
        const t = [...str].map((c) => c.charCodeAt(0));
        for (let p = 0; p + t.length <= fw.length; p++) {
          let ok = true;
          for (let d = 0; d < t.length; d++) if (fw[p + d] !== t[d]) { ok = false; break; }
          if (ok) return true;
        }
        return false;
      };
      const shinTag = hasAscii('SV850') || hasAscii('SH850');
      const isShin = opts.maker === 'Shindengen' || shinTag;
      const isDenso = opts.maker === 'Denso' || /denso|titan|k2g|k2k|k3h/i.test(opts.mcu || '')
        || opts.protocol === 'denso-titan'
        || hasAscii('KIGEDB') || hasAscii('KIGFDCA')   // Denso cal block — present in ALL Denso 384KB (K2G/K2K/K3H)
        || hasAscii('K2G') || hasAscii('K2K') || hasAscii('K3H');
      if (isDenso && !isShin) {
        onLog('INFO', 'Protocol: Denso Titan 384KB');
        return runFlashDenso(manager, firmware, opts, cb);
      }
      if (isShin && !isDenso) {
        onLog('INFO', 'Protocol: Shindengen 384KB');
        return runFlashShindengen(manager, firmware, opts, cb);
      }
      onLog('WARN', `384KB: signature unclear — using maker ${opts.maker || 'Unknown'}.`);
    }
  }
  // REAL hardware Shindengen write (K-Line block programming sequence).
  if (!manager.simulated && (opts.maker === 'Shindengen')) {
    return runFlashShindengen(manager, firmware, opts, cb);
  }
  // REAL hardware Keihin write.
  if (!manager.simulated && (opts.maker === 'Keihin')) {
    // SPC560 (Freescale / "KEIHINOTHER") uses a different 7E 8B block protocol.
    // Signal: explicit MCU, manual override, or a 256KB image with the 3FFF8 checksum.
    const cs = (opts.checksumOffset || '').toUpperCase();
    const st = (opts.startOffset || '').toUpperCase();
    const isSPC = /SPC/i.test(opts.mcu || '')
      || opts.protocol === 'keihin-spc560'
      || (firmware.size === 0x40000 && (cs === '3FFF8' || cs === '9FEF'))
      || (firmware.size === 0x20000 && (cs === '1FFF8' || cs === 'B000' || st === 'E0000'));
    if (isSPC) { onLog('INFO', 'Protocol: Keihin SPC560'); return runFlashKeihinSPC560(manager, firmware, opts, cb); }
    // Keihin R8C 48KB (Verza 38770-K18 / Megapro K25 etc): 7E 8B write blocks with the cracked X field.
    // Guarded to 48KB: runFlashKeihinR8C's block map / Cpos is 48KB-only. A 64KB R8C image (e.g. K15-921)
    // uses the 64K block writer below, so an "R8C" mcu label must NOT pull a 64KB image into the 48K path.
    const isR8C = firmware.size === 49152 && (opts.protocol === 'keihin-r8c' || (opts.mcu || '').toUpperCase().includes('R8C')
      || (cs === '7600' || cs === '7601' || cs === '9FEF'));
    if (isR8C) { onLog('INFO', 'Protocol: Keihin R8C 48KB'); return runFlashKeihinR8C(manager, firmware, opts, cb); }
    // Keihin 56KB (R8C/M16C, e.g. 38770-KWT-912 / ECM 01008B1602): 7E 8B lead block + 896 7E 4B 64B blocks.
    const is56K = opts.protocol === 'keihin-56k' || (firmware.size === 57344 && (cs === 'DFEF' || cs === 'DFFE'));
    if (is56K) { onLog('INFO', 'Protocol: Keihin 56KB'); return runFlashKeihin56K(manager, firmware, opts, cb); }
    // Keihin 64KB M16C: SAME 7E 8B lead + 7E 4B 64B block protocol as 56K, 1024 blocks. The old 0x77 H8
    // path never ACKs on these M16C ECUs. A genuine SH7058 ROM is far larger than 64KB, so ANY 64KB Keihin
    // is M16C — do NOT exclude by the DB mcu label (checksum-'0' is mis-mapped to "SH7058" for K15-921 etc).
    const is64K = opts.protocol === 'keihin-64k' || firmware.size === 65536;
    if (is64K) { onLog('INFO', 'Protocol: Keihin 64KB'); return runFlashKeihinR8C(manager, firmware, opts, cb); }
    // Other Keihin (SH705x / H8) use the Honda 0x77 block frames.
    return runFlashKeihin(manager, firmware, opts, cb);
  }
  manager.abort = false;
  const t0 = Date.now();
  const total = firmware.size;
  const chunk = fastWrite ? 4096 : 1024;

  onLog('INFO', `Target start offset 0x${startOffset} | checksum 0x${checksumOffset}`);
  onLog('INFO', `Mode: ${fastWrite ? 'FAST WRITE (4KB blocks)' : 'SAFE WRITE (1KB blocks)'}`);

  // Embed a valid Honda/Keihin 8-bit checksum into the image before writing so the
  // flashed ROM is valid. Scoped to <=64KB H8 Keihin (48K/56K/64K) where the byte at
  // checksumOffset makes the whole-image 8-bit sum == 0.
  let writeBytes = firmware.bytes;
  const csAddr = parseInt(checksumOffset, 16);
  // Honda 8-bit "sum-to-zero" checksum covers the small H8 Keihin (<=64KB), the Keihin M16C 128KB
  // (KWT-712/702 — checksum byte @0x1000F, verified byte-for-byte vs ChecksumCalculator) AND the
  // SH705x 1MB ECUs (checksum @0xFFFF8). Other large offsets (3FFF8/7FFF8 = 32-bit RH850/MPC) are
  // NOT 8-bit, so leave them as-is.
  const is128Keihin = total === 0x20000;
  const cs8Addr = is128Keihin ? 0x1000F : csAddr; // 128K: use the verified 8-bit byte, not the auto 0xB000
  const is8bitCs = total <= 0x10000 || is128Keihin || (checksumOffset || '').toUpperCase() === 'FFFF8';
  if (!manager.simulated && Number.isFinite(cs8Addr) && cs8Addr >= 0 && cs8Addr < total && is8bitCs) {
    writeBytes = new Uint8Array(firmware.bytes);
    const r = embedHonda8bitChecksum(writeBytes, cs8Addr);
    const hx2 = (v) => v.toString(16).toUpperCase().padStart(2, '0');
    onLog('INFO', `Checksum @0x${cs8Addr.toString(16).toUpperCase()}: ${r.changed ? `${hx2(r.before)} → ${hx2(r.corrected)}` : 'OK'}`);
  } else if (!manager.simulated && Number.isFinite(csAddr) && total > 0x10000 && csAddr >= 0 && csAddr + 4 <= total) {
    onLog('INFO', `Checksum @0x${csAddr.toString(16).toUpperCase()}: as-is from file.`);
  }

  // Phase 1: seed / key challenge
  onPhase('handshake');
  onLog('UART', 'Requesting Seed/Key challenge...');
  await sleep(280);
  if (!manager.simulated) await manager.sendCommand(CMD.INIT_ECM, { timeout: 300 });
  onLog('SUCCESS', 'Seed/Key accepted. Security access granted.');

  // Phase 2: erase
  onPhase('erasing');
  onLog('ERASE', 'Erasing flash sectors...');
  if (!manager.simulated) await manager.sendCommand(CMD.ERASE, { timeout: 1500 });
  for (let p = 0; p <= 100; p += 10) {
    if (manager.abort) return _abort(onLog);
    onProgress('erasing', p);
    await sleep(fastWrite ? 40 : 90);
  }
  onLog('ERASE', 'Flash erase complete.');

  // Phase 3: write
  onPhase('writing');
  onLog('WRITE', `Writing ${total} bytes...`);
  let written = 0;
  let lastTick = Date.now();
  let lastWritten = 0;
  while (written < total) {
    if (manager.abort) return _abort(onLog);
    const size = Math.min(chunk, total - written);
    if (!manager.simulated) {
      const block = writeBytes.slice(written, written + size);
      // 24-bit block address for images >64KB (e.g. SH705x 1MB); 16-bit for smaller
      // H8 Keihin (48K/56K/64K) so the proven small-ECU behavior is unchanged.
      const frame = total > 0x10000
        ? [0x77, size & 0xff, (written >> 16) & 0xff, (written >> 8) & 0xff, written & 0xff, ...block]
        : [0x77, size & 0xff, (written >> 8) & 0xff, written & 0xff, ...block];
      frame.push(hondaChecksum(frame));
      await manager.write(frame);
    } else {
      await sleep(fastWrite ? 6 : 14);
    }
    written += size;
    const pct = Math.round((written / total) * 100);
    onProgress('writing', pct);
    const now = Date.now();
    if (now - lastTick > 250) {
      const kbps = ((written - lastWritten) / 1024) / ((now - lastTick) / 1000);
      cb.onSpeed && cb.onSpeed(kbps);
      if (Math.random() > 0.6) onLog('WRITE', `0x${written.toString(16).toUpperCase().padStart(6, '0')}  (${pct}%)  ${kbps.toFixed(1)} KB/s`);
      lastTick = now; lastWritten = written;
    }
  }
  onLog('WRITE', 'All blocks written.');

  // Phase 4: verify + checksum
  if (verify) {
    onPhase('verifying');
    onLog('INFO', 'Verifying flash & recalculating checksum...');
    for (let p = 0; p <= 100; p += 20) {
      if (manager.abort) return _abort(onLog);
      onProgress('verifying', p);
      await sleep(60);
    }
    onLog('SUCCESS', `Checksum written @ 0x${checksumOffset}. Verification OK.`);
  }

  onPhase('done');
  const dur = Date.now() - t0;
  onLog('SUCCESS', `WRITE OK (${(dur / 1000).toFixed(1)}s) — restart ECU.`);
  return { result: 'success', duration_ms: dur };
}

// Honda 8-bit checksum: two's complement of the byte sum. ((sum ^ 0xFF) + 1) & 0xFF
function hondaChecksum(bytes) {
  let s = 0;
  for (const b of bytes) s += b;
  return ((s ^ 0xff) + 1) & 0xff;
}

// Embed the Honda/Keihin 8-bit "sum-to-zero" checksum at addr (over the whole image,
// excluding the checksum byte itself). Matches the verified honda_8bit algorithm.
function embedHonda8bitChecksum(bytes, addr) {
  let rest = 0;
  for (let i = 0; i < bytes.length; i++) { if (i === addr) continue; rest = (rest + bytes[i]) & 0xff; }
  const corrected = (0 - rest) & 0xff;
  const before = bytes[addr];
  bytes[addr] = corrected;
  return { before, corrected, changed: before !== corrected };
}

// Read-Memory-by-Address frame (SID 0x72, 24-bit address).
// Derived from the working ECM ID read: 72 07 <addr:3> <num> <hondaCS>
function readMemCmd(addr, num) {
  const b = [0x72, 0x07, (addr >> 16) & 0xff, (addr >> 8) & 0xff, addr & 0xff, num & 0xff];
  b.push(hondaChecksum(b));
  return b;
}

// Parse a read response frame out of the raw reply (echo + response).
// Response frame: 02 <len=6+num> <addr:3> <data:num> <cs>.
function parseReadData(reply, num) {
  const b = Array.from(reply);
  const frameLen = (6 + num) & 0xff;
  for (let i = 0; i + 5 + num < b.length + 1; i++) {
    if (b[i] === 0x02 && b[i + 1] === frameLen && i + 5 + num <= b.length) {
      return b.slice(i + 5, i + 5 + num);
    }
  }
  // Fallback: strip 7-byte command echo, skip 5-byte response header.
  if (b.length >= 12 + num) return b.slice(12, 12 + num);
  return null;
}

// Read profile per ECU maker. The 0x72 read command is shared; base address,
// block size and vector differ. Shindengen (RH850) is verified from real data.
export function readProfile(maker) {
  if (maker === 'Keihin') {
    return { maker: 'Keihin', base: 0x000000, block: 0x80, note: 'Keihin (SH705x) read mode' };
  }
  // Default / Shindengen (RH850)
  return { maker: 'Shindengen', base: 0x000000, block: 0xF0, note: 'Shindengen (RH850) read mode' };
}

// Read the full ECU flash back into a byte array (for backup before writing).
export async function runRead(manager, opts, cb) {
  const { size, maker } = opts;
  const { onLog, onProgress, onPhase, onSpeed } = cb;
  // Keihin 128KB (38770-KWT-702, flash @0x0E0000) and 256KB (38770-K93 Scoopy, flash @0x000000)
  // both use the same password-gated 82/92 bootloader read — only the base page differs.
  if (!manager.simulated && maker === 'Keihin' && opts.r8c48) {
    return runReadKeihinR8C48(manager, opts, cb);
  }
  // Keihin 64KB M16C: same 82/82 bootloader reader as 48/56KB (page 0x00 @0x0000, 64KB). The old
  // generic SH705x 0x72 read returns all-0xFF on these M16C ECUs. A real SH7058 ROM is far larger than
  // 64KB, so ANY 64KB Keihin uses the 82/82 engine — do not exclude by the (often mislabeled) mcu.
  if (!manager.simulated && maker === 'Keihin' && size === 0x10000 && !opts.keihinCold) {
    onLog('INFO', 'Keihin 64KB M16C read: 82/82 bootloader (page 0x00 @0x0000, 64KB).');
    return runReadKeihinR8C48(manager, {
      ...opts,
      size: 0x10000,
      startOffset: (opts.startOffset != null ? opts.startOffset : 0x0000),
      basePage: (opts.basePage != null ? opts.basePage : 0x00),
    }, cb);
  }
  if (!manager.simulated && maker === 'Keihin' && (size === 0x20000 || size === 0x40000 || (size === 0x10000 && opts.keihinCold))) {
    return runReadKeihin82(manager, opts, cb);
  }
  if (maker === 'Shindengen') {
    return runReadShindengen(manager, opts, cb);
  }
  const prof = readProfile(maker);
  const base = prof.base;
  const block = prof.block;
  manager.abort = false;
  const t0 = Date.now();
  const out = new Uint8Array(size).fill(0xff);

  onPhase('reading');
  onLog('INFO', `${prof.note}: reading ${size} bytes from 0x${base.toString(16).toUpperCase().padStart(6, '0')} (block ${block}B)`);
  if (!manager.simulated) await manager.sendCommand(CMD.INIT_ECM, { timeout: 300 });

  let read = 0;
  let lastTick = Date.now();
  let lastRead = 0;
  let emptyBlocks = 0;
  let firstLogged = false;
  while (read < size) {
    if (manager.abort) {
      onLog('ERROR', 'Read aborted.');
      return { result: 'aborted', bytes: out.slice(0, read), duration_ms: Date.now() - t0 };
    }
    const num = Math.min(block, size - read);
    if (!manager.simulated) {
      const cmd = readMemCmd(base + read, num);
      const reply = await manager.sendCommand(cmd, { timeout: 1000, expect: 13 + num, pre: KLINE.BLOCK_GAP });
      if (!firstLogged) {
        onLog('UART', `TX ${cmd.map(hex).join(' ')}`);
        onLog('UART', `RX ${Array.from(reply).slice(0, 24).map(hex).join(' ')}${reply.length > 24 ? ' ...' : ''}`);
        firstLogged = true;
      }
      const data = parseReadData(reply, num);
      if (!data || data.length === 0) emptyBlocks++;
      for (let i = 0; i < num; i++) out[read + i] = (data && data[i] != null) ? data[i] : 0xff;
    } else {
      for (let i = 0; i < num; i++) out[read + i] = (read + i * 7 + 0x20) & 0xff;
      await sleep(3);
    }
    read += num;
    const pct = Math.round((read / size) * 100);
    onProgress('reading', pct);
    const now = Date.now();
    if (now - lastTick > 250) {
      const kbps = ((read - lastRead) / 1024) / ((now - lastTick) / 1000);
      onSpeed && onSpeed(kbps);
      if (Math.random() > 0.7) onLog('READ', `0x${(base + read).toString(16).toUpperCase().padStart(6, '0')}  (${pct}%)  ${kbps.toFixed(1)} KB/s`);
      lastTick = now; lastRead = read;
    }
  }

  onPhase('done');
  const dur = Date.now() - t0;
  if (!manager.simulated && emptyBlocks > 0) {
    const totalBlocks = Math.ceil(size / block);
    onLog('WARN', `${emptyBlocks}/${totalBlocks} blocks empty.`);
  }
  onLog('SUCCESS', `Read complete: ${size} bytes in ${(dur / 1000).toFixed(1)}s`);
  return { result: 'success', bytes: out, duration_ms: dur };
}


// CRC32 (IEEE) for read-back verification against a known dump.
function crc32(bytes) {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    crc ^= bytes[i];
    for (let j = 0; j < 8; j++) crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1));
  }
  return ((crc ^ 0xffffffff) >>> 0).toString(16).toUpperCase().padStart(8, '0');
}

// Shindengen (V850/RH850) READ / backup. Protocol decoded + verified against a known
// 256KB dump (CRC32 3460749A): per 4-byte word,
//   request : 72 08 80 <A3 A2 A1 A0 = 32-bit BE byte-address> <cs>
//   response: 02 08 80 <D0 D1 D2 D3 = 4 data bytes>          <cs>   (leading 0x80 = flag echo)
// Address starts at 0x0, steps +4. cs = two's-complement 8-bit sum. Runs in bridge or Web Serial.
// Clear the Shindengen "flash count" (number-of-writes counter). Captured 1:1 from AlexMap
// on K1AA-N13: INIT_ECM -> Security Access LEVEL 0xDF (note: read uses 0xCF) -> clear cmd.
//   SEC1  91 91 0D DF 9E 8D 9A 86 90 8A 8C 9B 88   -> 8E ..
//   SEC2  91 91 0D DF 92 9E 86 96 8B 8D 86 C0 6A   -> 8E ..
//   CLEAR 91 91 09 41 0E 00 00 00 A8               -> 8E 8E 05 41 BA (positive)
export async function runClearCountShindengen(manager, cb) {
  const { onLog } = cb;
  manager.abort = false;
  const INIT = [0x72, 0x05, 0x00, 0xF0, 0x99];
  const SEC = [
    [0x91, 0x91, 0x0D, 0xDF, 0x9E, 0x8D, 0x9A, 0x86, 0x90, 0x8A, 0x8C, 0x9B, 0x88],
    [0x91, 0x91, 0x0D, 0xDF, 0x92, 0x9E, 0x86, 0x96, 0x8B, 0x8D, 0x86, 0xC0, 0x6A],
  ];
  const CLEAR = [0x91, 0x91, 0x09, 0x41, 0x0E, 0x00, 0x00, 0x00, 0xA8];
  onLog('INFO', '=== CLEAR FLASH COUNT (Shindengen) START ===');
  if (manager.simulated) { onLog('SUCCESS', 'Simulated: flash count cleared.'); return { ok: true }; }

  // Prefer the NATIVE bridge command (>= 1.9.12): same proven read handshake + tight SEC(DF) pair —
  // the JS framed path below can't hit the tight SEC1->SEC2 window (that was the earlier failure).
  const toHexStr = (a) => a.map((b) => b.toString(16).padStart(2, '0')).join('');
  const verGE = (v, a, b, c) => { const p = String(v || '').split('.').map((n) => parseInt(n, 10) || 0); return p[0] > a || (p[0] === a && (p[1] > b || (p[1] === b && (p[2] || 0) >= c))); };
  if (manager.mode === 'bridge' && manager.bridge && manager.bridge.clearCount && verGE(manager.bridgeVersion, 1, 9, 12)) {
    try {
      onLog('WARN', 'Clear flash count…');
      let wl = false, il = false, idl = false, sl = false;
      const res = await manager.bridge.clearCount(toHexStr(SEC[0]), toHexStr(SEC[1]), toHexStr(CLEAR), (m) => {
        if (m.boot) {
          if (m.waiting) { onLog('INFO', `⚡ Waiting… ${m.left_s}s · wake=${m.wake ? '✓' : '✗'} init=${m.init ? '✓' : '✗'} id=${m.idok ? '✓' : '✗'} sec1=${m.sec1 ? '✓' : '✗'} sec2=${m.sec2 ? '✓' : '✗'}`); return; }
          if (m.wake && !wl) { onLog('INFO', 'Wake ACK (0E).'); wl = true; }
          if (m.init && !il) { onLog('INFO', 'ECU session alive (INIT_ECM → FA).'); il = true; }
          if (m.idok && !idl) { onLog('INFO', 'READ_ID + flash-count handshake OK — session set up.'); idl = true; }
          if (m.sec1 !== undefined && !sl) { onLog(m.sec ? 'INFO' : 'WARN', `Security Access (DF) — SEC1 ${m.sec1 ? '✓' : '✗'} · SEC2 ${m.sec2 ? '✓' : '✗'}`); sl = true; }
        }
      });
      onLog('UART', `Clear RX ${res.clearReply ? res.clearReply.replace(/(..)/g, '$1 ').trim() : '(none)'}`);
      if (res.ok) { onLog('SUCCESS', 'Flash count CLEARED ✓ (ECU replied 8E .. 41).'); return { ok: true }; }
      onLog('ERROR', `Clear not confirmed. ${res.why || ''}`);
      return { ok: false };
    } catch (e) {
      onLog('ERROR', `Clear flash count failed: ${e.message}`);
      if (e.why) onLog('WARN', e.why);
      return { ok: false };
    }
  }
  onLog('WARN', 'Legacy path — update bridge to v1.9.12+.');
  try {
    const init = await manager.sendFramed(INIT, { timeout: 500, pre: KLINE.P2 }).catch(() => ({ reply: [] }));
    if (init.reply && init.reply.length >= 4 && init.reply[3] === 0xFA) onLog('INFO', 'ECU session alive (INIT_ECM → FA).');
    else onLog('WARN', `INIT_ECM reply ${(init.reply || []).map(hex).join(' ') || '(none)'} (continuing)`);
    let sec = false;
    for (const f of SEC) {
      const r = await manager.sendFramed(f, { timeout: 400, pre: 0 }).catch(() => ({ reply: [] }));
      if (r.reply && r.reply.length >= 4 && r.reply[0] === 0x8E) { sec = true; onLog('UART', `SecAccess(DF) RX ${r.reply.map(hex).join(' ')}`); }
    }
    if (!sec) { onLog('ERROR', 'Security rejected — power-cycle & retry.'); return { ok: false }; }
    onLog('INFO', 'Clearing flash count: TX 91 91 09 41 0E 00 00 00 A8');
    const r = await manager.sendFramed(CLEAR, { timeout: 800, pre: 0 }).catch(() => ({ reply: [] }));
    const rp = r.reply || [];
    onLog('UART', `Clear RX ${rp.map(hex).join(' ') || '(none)'}`);
    if (rp.length >= 4 && rp[0] === 0x8E && rp[3] === 0x41) {
      onLog('SUCCESS', 'Flash count CLEARED ✓ (ECU replied 8E .. 41).');
      return { ok: true };
    }
    onLog('ERROR', 'Clear not confirmed (expected 8E 8E 05 41 ..).');
    return { ok: false };
  } catch (e) {
    onLog('ERROR', `Clear flash count failed: ${e.message}`);
    return { ok: false };
  }
}

async function runReadShindengen(manager, opts, cb) {
  const { size } = opts;
  const { onLog, onProgress, onPhase, onSpeed } = cb;
  manager.abort = false;
  const t0 = Date.now();
  const out = new Uint8Array(size).fill(0xff);
  const cs2c = (arr) => (0x100 - (arr.reduce((s, x) => s + x, 0) & 0xff)) & 0xff;
  const req = (addr) => {
    const a = [0x72, 0x08, 0x80, (addr >>> 24) & 0xff, (addr >>> 16) & 0xff, (addr >>> 8) & 0xff, addr & 0xff];
    a.push(cs2c(a));
    return a;
  };

  onPhase('reading');
  onLog('INFO', `Shindengen READ · ${size} B`);
  if (!manager.simulated) {
    // AlexMap "Read ROM Shindengen": CONNECT -> ignition OFF -> ON -> Security Access -> read.
    // Security Access only unlocks the read engine when done in the BOOT WINDOW right after power-on.
    // So detect the ignition OFF->ON on the bus and, the instant it comes back, fire Security Access
    // + a read probe. The 91.. frames are the Security Access request (ECU answers 8E..).
    const SEC_CF = [
      [0x91, 0x91, 0x0D, 0xCF, 0x9E, 0x8D, 0x9A, 0x86, 0x90, 0x8A, 0x8C, 0x9B, 0x98],
      [0x91, 0x91, 0x0D, 0xCF, 0x92, 0x9E, 0x86, 0x96, 0x8B, 0x8D, 0x86, 0xC0, 0x7A],
    ];
    // Write-session Security Access (level DF) — the SAME frames Clear Flash Count uses and that
    // are proven-accepted on K1AA hardware. A vendor-LOCKED ECU ignores the CF (read) level but
    // still accepts DF (write), so we use DF to open the read engine WITHOUT re-flashing the ori.
    const SEC_DF = [
      [0x91, 0x91, 0x0D, 0xDF, 0x9E, 0x8D, 0x9A, 0x86, 0x90, 0x8A, 0x8C, 0x9B, 0x88],
      [0x91, 0x91, 0x0D, 0xDF, 0x92, 0x9E, 0x86, 0x96, 0x8B, 0x8D, 0x86, 0xC0, 0x6A],
    ];
    const SEC = opts.lockedBypass ? SEC_DF : SEC_CF;
    const secLvl = opts.lockedBypass ? 'DF' : 'CF';
    if (opts.lockedBypass) {
      // ---- LOCK BYPASS read: unlock with the WRITE seed-key (7B challenge/response) — the same
      // security the flash path uses and that a vendor-LOCKED ECU still accepts — then read
      // 72 08 80 with NO erase/write. Needs the matching model .bin loaded for the key constants.
      onPhase('handshake');
      onLog('WARN', 'LOCK BYPASS: unlock, then read.');
      const fw = opts.firmwareBytes;
      if (!fw || fw.length < 0x40000) {
        onPhase('done');
        onLog('ERROR', 'Bypass read: load matching K1AA .bin first.');
        return { result: 'failed', bytes: null, duration_ms: Date.now() - t0 };
      }
      const skWrite = findWriteConstants(fw);
      if (!skWrite) {
        onPhase('done');
        onLog('ERROR', 'Seed-key not found in BIN — load correct ori.');
        return { result: 'failed', bytes: null, duration_ms: Date.now() - t0 };
      }
      const bsend = async (cmd, timeout = 800) => {
        const r = await manager.sendFramed(cmd, { timeout, pre: KLINE.P2 });
        onLog('UART', `TX ${cmd.map(hex).join(' ')}  |  RX ${r.reply && r.reply.length ? r.reply.map(hex).join(' ') : '(no reply)'}`);
        return r;
      };
      const secLocked = (r) => !!(r && r.reply && r.reply[1] === 5 && r.reply[3] === 0xFF);
      await manager.klineWake().catch(() => {});
      await manager.sendFramed(CMD.INIT_ECM, { timeout: 500, pre: KLINE.P2 }).catch(() => {});
      await bsend([0x7B, 0x08, 0x02, 0x02, 0x50, 0x19, 0x76, 0x9A]);
      await bsend([0x7B, 0x08, 0x02, 0x03, 0x25, 0x37, 0x86, 0x96]);
      for (let i = 0; i < 10; i++) {
        if (manager.abort) return { result: 'aborted', bytes: null, duration_ms: Date.now() - t0 };
        onLog('INFO', `Preparing Seed/Key (waiting ${10 - i}s)`);
        onProgress('handshake', Math.round((i / 10) * 100));
        await sleep(1000);
      }
      const sr = await bsend([0x7B, 0x05, 0x02, 0x04, 0x7A]);
      if (!(sr.ok && sr.reply[1] === 7 && sr.reply[3] === 0)) {
        onPhase('done');
        if (secLocked(sr)) onLog('ERROR', 'Security LOCKED (FF) — power OFF 15s, ON, try once.');
        else onLog('ERROR', 'Seed request failed — no valid seed returned.');
        return { result: 'failed', bytes: null, duration_ms: Date.now() - t0 };
      }
      const seed = [sr.reply[4], sr.reply[5]];
      const seed16 = ((seed[0] << 8) | seed[1]) & 0xffff;
      onLog('UART', `Seed: ${seed.map(hex).join(' ')}`);
      const key = [(computeKey(seed16, skWrite.C0, skWrite.C1, skWrite.C2) >> 8) & 0xff, computeKey(seed16, skWrite.C0, skWrite.C1, skWrite.C2) & 0xff];
      onLog('INFO', `Computed WRITE key (RH850): ${key.map(hex).join(' ')}`);
      const dKey = [0x7B, 0x07, 0x02, 0x05, key[0], key[1], 0x00];
      dKey[6] = cs8h(dKey, sr.reply[1] - 1, 0);
      const k = await bsend(dKey);
      const accepted = k.ok && k.reply[1] === 5 && k.reply[3] === 0;
      if (!accepted) {
        onPhase('done');
        if (secLocked(k)) onLog('ERROR', 'Key rejected (FF) — power OFF 15s; check BIN matches ECU.');
        else onLog('ERROR', `Seed-key not accepted (RX ${(k.reply || []).map(hex).join(' ')}).`);
        return { result: 'failed', bytes: null, duration_ms: Date.now() - t0 };
      }
      onLog('SUCCESS', 'Seed-Key OK. Setting up read…');
      // Now that the write session is authenticated, run the normal read session-setup + CF
      // Security Access. On a locked ECU the CF gate was blocked cold; the 7B write-unlock may
      // now let it through so the 72 08 80 read engine opens. All frames here are read-only queries.
      await bsend([0x72, 0x07, 0x72, 0x00, 0x00, 0x05, 0x10]); // READ_ID
      await bsend([0x7B, 0x06, 0x02, 0x01, 0x03, 0x79]);       // flash-count handshake
      const cf1 = await bsend(SEC_CF[0]);
      const cf2 = await bsend(SEC_CF[1]);
      const cfOk1 = !!(cf1.reply && cf1.reply[0] === 0x8E);
      const cfOk2 = !!(cf2.reply && cf2.reply[0] === 0x8E);
      onLog(cfOk1 || cfOk2 ? 'INFO' : 'WARN', `Post-unlock CF Security — SEC1 ${cfOk1 ? '✓' : '✗'} · SEC2 ${cfOk2 ? '✓' : '✗'}`);
      onLog('INFO', 'Probing read engine (72 08 80)…');
      let pr = [];
      for (let attempt = 0; attempt < 3; attempt++) {
        const probe = await manager.sendFramed(req(0), { timeout: 900, pre: 0 });
        pr = probe.reply || [];
        if (pr.length >= 8 && pr[0] === 0x02 && pr[2] === 0x80) break;
        await sleep(120);
      }
      if (!(pr.length >= 8 && pr[0] === 0x02 && pr[2] === 0x80)) {
        onPhase('done');
        onLog('ERROR', `Read engine not opened (RX ${pr.length ? pr.map(hex).join(' ') : '(none)'}) — vendor read key needed.`);
        return { result: 'failed', bytes: null, duration_ms: Date.now() - t0 };
      }
      onLog('SUCCESS', 'Read engine OPEN — dumping (JS per-word).');
    } else {
    // ---- COLD READ (bridge >= 1.9.7): the 72 08 80 read engine only opens from a cold
    // bootloader wake with NO prior runtime/READ_ID session. Runs the device-firmware
    // sequence natively (SetBaud 10417 -> BREAK wake -> INIT -> SEC CF -> read). ----
    if (opts.cold) {
      const verGE = (v, a, b, c) => { const p = String(v || '').split('.').map((n) => parseInt(n, 10) || 0); return p[0] > a || (p[0] === a && (p[1] > b || (p[1] === b && (p[2] || 0) >= c))); };
      if (!(manager.mode === 'bridge' && manager.bridge && manager.bridge.coldRead
            && verGE(manager.bridgeVersion, 1, 9, 7))) {
        onPhase('done');
        onLog('ERROR', 'Cold Read needs bridge v1.9.7+. Update TRMD Engine.');
        return { result: 'failed', bytes: null, duration_ms: Date.now() - t0 };
      }
      try {
        const toHexStr = (a) => a.map((b) => b.toString(16).padStart(2, '0')).join('');
        onLog('WARN', `Cold Read${opts.fast ? ' · FAST' : ''}${opts.lockedBypass ? ' · BYPASS' : ''} — keep ECU ON.`);
        if (opts.fast && !verGE(manager.bridgeVersion, 1, 9, 13)) onLog('WARN', 'Fast Read needs bridge v1.9.13+ — using normal speed.');
        let secLoggedN = false, initLoggedN = false, wakeLoggedN = false, idLoggedN = false, lastProbeN = '';
        const res = await manager.bridge.coldRead(size, {
          sec1: toHexStr(SEC[0]),
          sec2: toHexStr(SEC[1]),
          fast: !!opts.fast,
        }, (m) => {
          if (m.boot) {
            if (m.reopen !== undefined) { onLog(m.reopen ? 'INFO' : 'WARN', `Session dropped at 0x${(m.at || 0).toString(16).toUpperCase().padStart(6, '0')} — re-auth ${m.reopen ? 'OK, resuming read' : 'failed, retrying'}`); return; }
            if (m.waiting) { const fx = (v) => (v ? String(v).replace(/(..)/g, '$1 ').trim() : '(none)'); onLog('INFO', `⚡ Waiting… ${m.left_s}s · wake=${m.wake ? '✓' : '✗'} init=${m.init ? '✓' : '✗'} id=${m.idok ? '✓' : '✗'} sec1=${m.sec1 ? '✓' : '✗'} sec2=${m.sec2 ? '✓' : '✗'} · wkRX ${fx(m.wkrx)} · initRX ${fx(m.initrx)}`); return; }
            if (m.wake && !wakeLoggedN) { onLog('INFO', 'Wake ACK (0E).'); wakeLoggedN = true; }
            if (m.init && !initLoggedN) { onLog('INFO', 'ECU session alive (INIT_ECM → FA).'); initLoggedN = true; }
            if (m.idr !== undefined && !idLoggedN) {
              const hx = (v) => (v ? v.replace(/(..)/g, '$1 ').trim() : '(none)');
              onLog(m.idok ? 'INFO' : 'WARN', `READ_ID ${m.idok ? 'OK' : 'no valid reply'} — INIT RX ${hx(m.initr)} · READ_ID RX ${hx(m.idr)} · FCNT RX ${hx(m.fcr)}`);
              idLoggedN = true;
            }
            if ((m.sec1 !== undefined || m.sec) && !secLoggedN) {
              onLog(m.sec ? 'INFO' : 'WARN', `Security Access — SEC1 ${m.sec1 ? '✓' : '✗'} · SEC2 ${m.sec2 ? '✓' : '✗'}${m.sec ? ' — opening read engine…' : ''}`);
              const hx = (v) => (v ? v.replace(/(..)/g, '$1 ').trim() : '(none)');
              onLog('UART', `SEC1 RX ${hx(m.secr1)} · SEC2 RX ${hx(m.secr2)}`);
              secLoggedN = true;
            }
            if (m.probe !== undefined && m.probe !== lastProbeN) {
              lastProbeN = m.probe;
              const pretty = m.probe ? m.probe.replace(/(..)/g, '$1 ').trim() : '(none)';
              onLog('UART', `Read probe RX ${pretty}`);
            }
            return;
          }
          const pct = Math.round((m.progress / m.total) * 100);
          onProgress('reading', pct);
          const kbps = (m.progress / 1024) / ((m.elapsed_ms || 1) / 1000);
          onSpeed && onSpeed(kbps);
          onLog('READ', `0x${(m.progress).toString(16).toUpperCase().padStart(6, '0')} (${pct}%) ${kbps.toFixed(2)} KB/s`);
        });
        const hexstr = res.data || '';
        for (let i = 0; i < size; i++) {
          out[i] = (i * 2 + 1 < hexstr.length) ? parseInt(hexstr.substr(i * 2, 2), 16) : 0xff;
        }
        onPhase('done');
        const durN = Date.now() - t0;
        const emptyN = res.empty || 0;
        const crcN = crc32(out);
        if (emptyN > 0) onLog('WARN', `${emptyN}/${size / 4} words returned no reply during the dump.`);
        onLog('SUCCESS', `Cold Read complete: ${size} bytes in ${(durN / 1000).toFixed(1)}s · CRC32 ${crcN}`);
        return { result: emptyN > (size / 4) / 2 ? 'partial' : 'success', bytes: out, duration_ms: durN };
      } catch (e) {
        if (manager.abort) { onLog('ERROR', 'Read aborted.'); return { result: 'aborted', bytes: null, duration_ms: Date.now() - t0 }; }
        onPhase('done');
        onLog('ERROR', 'Cold Read: could not enter read mode.');
        if (e.why) onLog('WARN', e.why);
        if (e.lastProbe) onLog('UART', `Last probe RX ${e.lastProbe || '(none)'}`);
        return { result: 'failed', bytes: null, duration_ms: Date.now() - t0 };
      }
    }
    // ---- Native boot-read (bridge >= 1.9): run the wake+SEC+read loop directly on the
    // PC at full FTDI speed so the sub-second boot window isn't missed by browser latency. ----
    if (manager.mode === 'bridge' && manager.bridge && manager.bridge.bootRead
        && parseFloat(manager.bridgeVersion) >= 1.9) {
      try {
        const toHexStr = (a) => a.map((b) => b.toString(16).padStart(2, '0')).join('');
        onLog('WARN', `Read init${opts.lockedBypass ? ' · BYPASS' : ''} — keep ECU ON.`);
        let secLoggedN = false, initLoggedN = false, lastProbeN = '', downLoggedN = false, cyclesN = 0;
        const res = await manager.bridge.bootRead(size, {
          window: 90000,
          sec1: toHexStr(SEC[0]),
          sec2: toHexStr(SEC[1]),
        }, (m) => {
          if (m.boot) {
            if (m.init && !initLoggedN) { onLog('INFO', 'ECU session alive (INIT_ECM → FA).'); initLoggedN = true; }
            if (m.sec && !secLoggedN) { onLog('INFO', 'Security replied (8E). Reading…'); secLoggedN = true; }
            if (m.down && !downLoggedN) { onLog('WARN', 'ECU OFF ✓ — IGNITION ON now.'); downLoggedN = true; }
            if (typeof m.cycles === 'number' && m.cycles > cyclesN) { cyclesN = m.cycles; downLoggedN = false; secLoggedN = false; onLog('SUCCESS', `Power-cycle #${m.cycles} — retrying read…`); }
            if (m.probe !== undefined && m.probe !== lastProbeN) {
              lastProbeN = m.probe;
              const pretty = m.probe ? m.probe.replace(/(..)/g, '$1 ').trim() : '(none)';
              onLog('UART', `Read probe RX ${pretty}`);
            }
            if (typeof m.left_s === 'number') onLog('INFO', `Boot window: ${m.left_s}s left — cut ECU power OFF then ON.`);
            return;
          }
          const pct = Math.round((m.progress / m.total) * 100);
          onProgress('reading', pct);
          const kbps = (m.progress / 1024) / ((m.elapsed_ms || 1) / 1000);
          onSpeed && onSpeed(kbps);
          onLog('READ', `0x${(m.progress).toString(16).toUpperCase().padStart(6, '0')} (${pct}%) ${kbps.toFixed(2)} KB/s`);
        });
        const hexstr = res.data || '';
        for (let i = 0; i < size; i++) {
          out[i] = (i * 2 + 1 < hexstr.length) ? parseInt(hexstr.substr(i * 2, 2), 16) : 0xff;
        }
        onPhase('done');
        const durN = Date.now() - t0;
        const emptyN = res.empty || 0;
        const crcN = crc32(out);
        if (emptyN > 0) onLog('WARN', `${emptyN}/${size / 4} words returned no reply during the dump.`);
        onLog('SUCCESS', `Read OK: ${size} B in ${(durN / 1000).toFixed(1)}s · CRC32 ${crcN}`);
        return { result: emptyN > (size / 4) / 2 ? 'partial' : 'success', bytes: out, duration_ms: durN };
      } catch (e) {
        if (manager.abort) { onLog('ERROR', 'Read aborted.'); return { result: 'aborted', bytes: null, duration_ms: Date.now() - t0 }; }
        if (e && /read (mode not entered|init failed)/.test(e.message || '')) {
          onPhase('done');
          onLog('ERROR', 'Native boot-read: could not enter read mode within the boot window.');
          if (e.why) onLog('WARN', e.why);
          if (e.lastProbe) onLog('UART', `Last probe RX ${e.lastProbe || '(none)'}`);
          return { result: 'failed', bytes: null, duration_ms: Date.now() - t0 };
        }
        onLog('WARN', `Boot-read unavailable (${e.message}) — using fallback.`);
      }
    }
    let secRxLogged = false;
    const trySec = async () => {
      let got = false;
      for (const f of SEC) {
        const r = await manager.sendFramed(f, { timeout: 250, pre: 0 }).catch(() => ({ reply: [] }));
        if (r.reply && r.reply.length >= 4 && r.reply[0] === 0x8E) {
          got = true;
          if (!secRxLogged) { onLog('UART', `SecAccess RX ${r.reply.map(hex).join(' ')} (accepted)`); secRxLogged = true; }
        }
      }
      return got;
    };
    const tryRead0 = async () => {
      const r0 = await manager.sendFramed(req(0), { timeout: 200, pre: 0 });
      const rp = r0.reply || [];
      return (rp.length >= 8 && rp[0] === 0x02 && rp[2] === 0x80) ? rp : null;
    };
    onLog('WARN', 'BOOT WINDOW 90s: IGNITION OFF, then ON.');
    let probed = false, lastRx = [], iter = 0, alivePrev = null, silentCount = 0;
    const deadline = Date.now() + 90000;
    while (!probed && !manager.abort && Date.now() < deadline) {
      iter++;
      await manager.klineWake().catch(() => {});
      const secOk = await trySec();          // Security Access + our ON indicator
      const rp = await tryRead0();           // ALWAYS attempt the read this pass
      if (rp) { probed = true; lastRx = rp; break; }
      if (secOk) {
        silentCount = 0;
        if (alivePrev !== true) { onLog('INFO', 'ECU responding — firing Security Access + read…'); alivePrev = true; }
      } else {
        silentCount++;
        if (silentCount >= 4 && alivePrev !== false) { onLog('INFO', 'ECU silent (power OFF?) — switch it back ON.'); alivePrev = false; }
      }
      if (iter % 10 === 0) onLog('INFO', `…${Math.round((deadline - Date.now()) / 1000)}s left — IGNITION OFF → ON.`);
    }
    onLog('UART', `Probe TX ${req(0).map(hex).join(' ')} -> RX ${lastRx.length ? lastRx.map(hex).join(' ') : '(none)'}`);
    if (!probed) {
      onPhase('done');
      onLog('ERROR', 'Could not enter read mode within the boot window.');
      if (!secRxLogged) onLog('WARN', 'No Security reply — check wiring.');
      else onLog('WARN', 'Read engine not opened — retry with full OFF → ON.');
      return { result: 'failed', bytes: null, duration_ms: Date.now() - t0 };
    }
    onLog('SUCCESS', 'Security Access OK — read mode entered, dumping.');
    }
  }

  let read = 0, lastTick = Date.now(), lastRead = 0, empty = 0, firstLogged = true;
  while (read < size) {
    if (manager.abort) {
      onLog('ERROR', 'Read aborted.');
      return { result: 'aborted', bytes: out.slice(0, read), duration_ms: Date.now() - t0 };
    }
    let data = null;
    if (!manager.simulated) {
      const cmd = req(read);
      const r = await manager.sendFramed(cmd, { timeout: 600, pre: 0 });
      const rp = r.reply;
      if (!firstLogged) {
        onLog('UART', `TX ${cmd.map(hex).join(' ')} -> RX ${(rp || []).map(hex).join(' ')}`);
        firstLogged = true;
      }
      // Expect 02 08 80 <4 data> <cs>. Accept any framed 02 reply carrying >=4 data bytes.
      if (rp && rp.length >= 8 && rp[0] === 0x02 && rp[2] === 0x80) data = rp.slice(3, 7);
    } else {
      data = [read & 0xff, (read >> 8) & 0xff, (read >> 16) & 0xff, 0x00];
      await sleep(0);
    }
    if (data && data.length === 4) { for (let i = 0; i < 4 && read + i < size; i++) out[read + i] = data[i]; }
    else empty++;
    read += 4;
    const pct = Math.round((read / size) * 100);
    onProgress('reading', pct);
    const now = Date.now();
    if (now - lastTick > 300) {
      const kbps = ((read - lastRead) / 1024) / ((now - lastTick) / 1000);
      onSpeed && onSpeed(kbps);
      onLog('READ', `0x${read.toString(16).toUpperCase().padStart(6, '0')} (${pct}%) ${kbps.toFixed(1)} KB/s`);
      lastTick = now; lastRead = read;
    }
  }

  onPhase('done');
  const dur = Date.now() - t0;
  const words = size / 4;
  if (empty > 0) onLog('WARN', `${empty}/${words} words empty.`);
  const crc = crc32(out);
  onLog('SUCCESS', `Read OK: ${size} B in ${(dur / 1000).toFixed(1)}s · CRC32 ${crc}`);
  return { result: empty > words / 2 ? 'partial' : 'success', bytes: out, duration_ms: dur };
}

function _abort(onLog) {
  onLog('ERROR', 'Flash aborted by operator.');
  return { result: 'aborted', duration_ms: 0 };
}

// Keihin R8C read — PHASE 1 (arm / "enter read"). Runs AFTER power-cycle #1. 1:1 from the Honda
// tool capture (48kb.txt): wake FE 04 72 8C -> 72 05 71 00 18 (ID) -> 27 0B E0 HelloHo/wAreYou
// (plaintext liveness -> 07 08) -> re-arm (wake -> 72 05 71 00 18 -> 7E) -> 82 82 10 06 00 E6
// -> 92 92 10 07. This ARMS read mode; the ECU must then be power-cycled AGAIN (PHASE 2), after
// which it boots straight into read mode and answers 82 82 00 09 directly.
// IMPORTANT: do NOT send StartComm (72 05 00 F0 99) — it puts the ECU into a diagnostic session
// that STOPS answering the 27 handshake (root cause of earlier "Handshake RX (none)").
export async function armKeihinR8CRead(manager, opts, cb) {
  const { onLog } = cb;
  const pw = (opts.password || 'HelloHowAreYou');
  const frame = (bytes) => { const b = bytes.slice(); b.push(hondaChecksum(b)); return b; };
  manager.abort = false;

  onLog('INFO', 'Enter-read: waking ECU…');
  let woke = false;
  for (let attempt = 1; attempt <= 6 && !woke; attempt++) {
    if (attempt >= 3) await manager.klineWake().catch(() => {});
    const wk = await manager.sendFramed([0xFE, 0x04, 0x72, 0x8C], { timeout: 300, pre: attempt === 1 ? 0 : KLINE.P3 });
    if (wk.reply && wk.reply.length && wk.reply[0] === 0x0E) woke = true;
    else await sleep(150);
  }
  onLog('UART', `Wake FE 04 72 8C -> ${woke ? 'RX 0E 04 72 7C (awake)' : '(none)'}`);
  const idS = await manager.sendFramed([0x72, 0x05, 0x71, 0x00, 0x18], { timeout: 700, pre: KLINE.P3 });
  onLog('UART', `Session TX 72 05 71 00 18 -> RX ${idS.reply && idS.reply.length ? idS.reply.slice(0, 16).map(hex).join(' ') : '(none)'}`);

  const pwBytes = Array.from(pw).map((c) => c.charCodeAt(0) & 0xff);
  let hsOk = false;
  for (let off = 0; off < pwBytes.length; off += 7) {
    const seven = pwBytes.slice(off, off + 7); while (seven.length < 7) seven.push(0x00);
    const r = await manager.sendFramed(frame([0x27, 0x0B, 0xE0, ...seven]), { timeout: 700, pre: KLINE.P3 });
    if (r.reply && r.reply.length && r.reply[0] === 0x07) hsOk = true;
    onLog('UART', `Handshake TX 27 0B E0 ${seven.map(hex).join(' ')} -> RX ${r.reply && r.reply.length ? r.reply.map(hex).join(' ') : '(none)'}`);
  }

  // re-arm before read-mode open: wake -> 72 05 71 00 18 -> 7E keep-alive -> 82 82 10 06
  await manager.sendFramed([0xFE, 0x04, 0x72, 0x8C], { timeout: 400, pre: KLINE.P3 }).catch(() => {});
  await manager.sendFramed([0x72, 0x05, 0x71, 0x00, 0x18], { timeout: 500, pre: KLINE.P3 }).catch(() => {});
  await manager.sendFramed([0x7E, 0x06, 0x01, 0x01, 0x00, 0x7A], { timeout: 500, pre: KLINE.P3 }).catch(() => {});
  const ro = await manager.sendFramed([0x82, 0x82, 0x10, 0x06, 0x00, 0xE6], { timeout: 900, pre: KLINE.P3 });
  const opened = ro.reply && ro.reply.length >= 3 && ro.reply[0] === 0x92 && ro.reply[1] === 0x92 && ro.reply[2] === 0x10;
  onLog('UART', `Read-mode OPEN TX 82 82 10 06 00 E6 -> RX ${ro.reply && ro.reply.length ? ro.reply.map(hex).join(' ') : '(none)'}`);
  if (opened) onLog('SUCCESS', 'Read mode armed — IGNITION OFF → ON.');
  else onLog('WARN', 'Read-mode did not confirm (expected 92 92 10 07).');
  return { result: opened ? 'armed' : 'failed', armed: opened, handshake: hsOk };
}

// Keihin R8C read — PHASE 2 (bulk). Runs AFTER power-cycle #2: the ECU boots into read mode and
// answers 82 82 00 09 directly (no wake/handshake — see the empty gap in the capture before the
// first read). Addr 0x4000..0xFFFF (48KB) or 0x0000..0xDFFF (56KB), 12B/block -> 92 92 00 11.
async function runReadKeihinR8C48(manager, opts, cb) {
  const { onLog, onProgress, onPhase, onSpeed } = cb;
  const SIZE = opts.size || 0xC000;                                                 // 48KB=0xC000, 56KB=0xE000, 128KB=0x20000
  const START = (opts.startOffset != null) ? (opts.startOffset & 0xffff) : 0x4000;  // 48KB@0x4000, 56KB@0x0000, 128KB@0x0000
  const BASEPAGE = (opts.basePage != null) ? (opts.basePage & 0xff) : 0x00;         // 48/56KB page 0x00; 128KB pages 0x0E..0x0F
  const BLK = 0x0C;      // 12 bytes/block
  const KB = Math.round(SIZE / 1024);
  const LIN0 = (BASEPAGE << 16) + START;               // linear 24-bit flash start
  const LIN_END = LIN0 + SIZE - 1;
  const hex6 = (v) => (v >>> 0).toString(16).toUpperCase().padStart(6, '0');
  const t0 = Date.now();
  manager.abort = false;
  const out = new Uint8Array(SIZE).fill(0xff);
  const frame = (bytes) => { const b = bytes.slice(); b.push(hondaChecksum(b)); return b; };

  onLog('INFO', `Keihin R8C ${KB}KB read…`);

  // 4) bulk read — prefer the native bridge batch loop (no per-block WS round-trip).
  onPhase('reading');
  if (manager.mode === 'bridge' && manager.bridge && manager.bridge.readBlocks) {
    try {
      const frames = [];
      for (let off = 0; off < SIZE; ) {
        const num = (SIZE - off) >= BLK ? BLK : 1;   // full 12B blocks; tail read 1 byte at a time (matches Honda)
        const lin = LIN0 + off;
        const page = (lin >>> 16) & 0xff;
        const a = lin & 0xffff;
        const f = frame([0x82, 0x82, 0x00, 0x09, page, a & 0xff, (a >> 8) & 0xff, num]);
        for (const b of f) frames.push(b);
        off += num;
      }
      const framesHex = frames.map((b) => b.toString(16).padStart(2, '0')).join('');
      onLog('INFO', `Batch read ${Math.ceil(SIZE / BLK)} blocks…`);
      let lastPct = -1;
      const res = await manager.bridge.readBlocks(framesHex, { blocklen: 9, timeout: 800, retries: 3 }, (m) => {
        const pct = Math.round((m.progress / m.total) * 100);
        onProgress('reading', pct);
        const kbps = ((m.progress * BLK) / 1024) / ((m.elapsed_ms || 1) / 1000);
        onSpeed && onSpeed(kbps);
        if (pct >= lastPct + 5) { lastPct = pct; onLog('READ', `0x${hex6(LIN0 + m.progress * BLK)} (${pct}%) ${kbps.toFixed(1)} KB/s`); }
      });
      const hexstr = res.data || '';
      let empty = 0;
      for (let i = 0; i < SIZE; i++) { const v = (i * 2 + 1 < hexstr.length) ? parseInt(hexstr.substr(i * 2, 2), 16) : 0xff; out[i] = v; if (v === 0xff) empty++; }
      onPhase('done');
      const durB = Date.now() - t0;
      onLog('SUCCESS', `Read OK: ${SIZE} B in ${(durB / 1000).toFixed(1)}s · CRC32 ${crc32(out)}`);
      if (empty === SIZE) onLog('WARN', 'Whole dump is 0xFF — read-mode/handshake was not accepted by this ECU.');
      return { result: empty === SIZE ? 'partial' : 'success', bytes: out, duration_ms: durB };
    } catch (e) {
      if (manager.abort) { onLog('ERROR', 'Read aborted.'); return { result: 'aborted', bytes: null, duration_ms: Date.now() - t0 }; }
      onLog('WARN', `Batch read unavailable — using per-block.`);
    }
  }

  // Fallback: per-block JS read.
  let read = 0, lastTick = Date.now(), lastRead = 0, empty = 0, firstLogged = false;
  while (read < SIZE) {
    if (manager.abort) { onLog('ERROR', 'Read aborted.'); return { result: 'aborted', bytes: out.slice(0, read), duration_ms: Date.now() - t0 }; }
    const lin = LIN0 + read;
    const page = (lin >>> 16) & 0xff;
    const a = lin & 0xffff;
    const num = (SIZE - read) >= BLK ? BLK : 1;   // full 12B blocks; tail 1 byte/req (matches Honda)
    const cmd = frame([0x82, 0x82, 0x00, 0x09, page, a & 0xff, (a >> 8) & 0xff, num]);
    const r = await manager.sendFramed(cmd, { timeout: 1000, pre: KLINE.BLOCK_GAP });
    const rp = r.reply;
    if (!firstLogged) { onLog('UART', `TX ${cmd.map(hex).join(' ')} -> RX ${rp && rp.length ? rp.slice(0, 24).map(hex).join(' ') : '(none)'}`); firstLogged = true; }
    let got = 0;
    if (rp && rp.length >= 5 && rp[0] === 0x92 && rp[1] === 0x92) {
      const dlen = rp[3] - 5;
      const data = rp.slice(4, 4 + Math.max(0, dlen));
      got = Math.min(data.length, num);
      for (let i = 0; i < got; i++) out[read + i] = data[i];
      if (got === 0) empty++;
    } else { empty++; }
    read += got > 0 ? got : num;
    onProgress('reading', Math.round((read / SIZE) * 100));
    const now = Date.now();
    if (now - lastTick > 250) {
      const kbps = ((read - lastRead) / 1024) / ((now - lastTick) / 1000);
      onSpeed && onSpeed(kbps);
      onLog('READ', `0x${hex6(LIN0 + read)} (${Math.round((read / SIZE) * 100)}%) ${kbps.toFixed(1)} KB/s`);
      lastTick = now; lastRead = read;
    }
  }
  onPhase('done');
  const dur = Date.now() - t0;
  if (empty > 0) onLog('WARN', `${empty} blocks returned no data.`);
  onLog('SUCCESS', `Read OK: ${SIZE} B in ${(dur / 1000).toFixed(1)}s · CRC32 ${crc32(out)}`);
  return { result: empty > (SIZE / BLK) / 2 ? 'partial' : 'success', bytes: out, duration_ms: dur };
}

// Keihin 128KB password-gated read (decoded from the reference tool KWT-702 log).
// Security: 27 0B E0 <7 pw bytes> cs (password sent in two 7-byte halves).
// Read: 82 82 00 09 <a2 a1 a0> <num> cs -> 92 92 00 <len> <data..> cs ; data_len = len-5.
// Flash is mapped at 0x0E0000; we store bytes at (addr - 0x0E0000) into a size buffer.
async function runReadKeihin82(manager, opts, cb) {
  const { size } = opts;
  const { onLog, onProgress, onPhase, onSpeed } = cb;
  // Read block size: 12 bytes (0x0C). Verified against the valid K93 256KB read capture — bulk
  // reads are 82 82 00 09 <lo> <hi> <bank> 0C -> reply 92 92 00 11 <12 data> cs. (The 08-byte
  // frames in the log are only the addr-0 arming reads during security, not the bulk read.)
  const BLK = 0x0C;
  // Base page = high byte of the flash's physical address. 128KB Keihin (KWT-702) maps at
  // 0x0E0000 (pages 0E/0F); 256KB Keihin (K93 Scoopy) maps at 0x000000 (pages 00..03).
  const basePage = (opts.basePage != null) ? (opts.basePage & 0xff) : (size >= 0x40000 ? 0x00 : 0x0E);
  const BASE = basePage << 16;
  // Start offset within the flash. Default 0. The Keihin 48KB read maps its 48KB window at
  // 0x4000..0xFFFF (start 0x4000, size 0xC000) — same 12-byte engine, just a non-zero start.
  const START = opts.startOffset ? (opts.startOffset & 0xffffff) : 0;
  const pw = (opts.password || 'HelloHowAreYou');
  manager.abort = false;
  const t0 = Date.now();
  const out = new Uint8Array(size).fill(0xff);
  const frame = (bytes) => { const b = bytes.slice(); b.push(hondaChecksum(b)); return b; };

  onPhase('handshake');
  onLog('INFO', `Keihin ${Math.round(size / 1024)}KB read…`);

  // Native Keihin cold read (bridge >= 1.9.22): the whole open sequence runs in the bridge with
  // tight timing (JS round-trips were too slow, so the ECU ignored the 27 0B E0 password). When
  // Security lands the ECU reboots into its bootloader (bike lights/speedo go OFF) — no manual
  // power cycle needed. Falls back to the JS flow on older bridges.
  if (opts.keihinCold && opts.keihinNative && !START && manager.mode === 'bridge' && manager.bridge && manager.bridge.keihinColdRead && eeVerGE(manager.bridgeVersion, 1, 9, 31)) {
    const pwHex = Array.from(pw).map((c) => (c.charCodeAt(0) & 0xff).toString(16).padStart(2, '0')).join('');
    onLog('WARN', 'Keihin cold read (90s) — IGNITION OFF, turn ON when told.');
    let openShown = false; let lastRound = 0; let lastReadLog = 0; let onPrompted = false;
    // K93 256KB uses the PROVEN v1.9.82 flow (user-confirmed working on FTDI + CH340):
    //   WAKE FE 04 72 8C -> SID 72 05 71 00 18 -> SEC1/SEC2 (07 08) -> KA 7E.. -> read-mode-open
    //   82 82 10 06 (92 92 10 07) -> block reads len 0x0C. The sm3 StartComm-dwell variant dropped
    //   the read-mode-open and only got `sec ✗` / empty RX on CH340, so it is no longer used.
    const useSm3 = false;
    const nr = await manager.bridge.keihinColdRead({ size, basePage, pw: pwHex, variant: useSm3 ? 'sm3' : '', ...(opts.secDelay != null ? { secDelay: opts.secDelay } : {}), probePages: size === 0x10000 ? (eeVerGE(manager.bridgeVersion, 2, 1, 3) ? 4 : eeVerGE(manager.bridgeVersion, 2, 0, 96) ? 2 : 1) : size !== 0x40000 ? 0 : (opts.k2f && eeVerGE(manager.bridgeVersion, 2, 0, 94)) ? 3 : eeVerGE(manager.bridgeVersion, 2, 0, 93) ? 2 : eeVerGE(manager.bridgeVersion, 2, 0, 67) ? 1 : 0, blk: useSm3 ? 12 : ([16, 24, 32].includes(opts.turboBlk) ? opts.turboBlk : 12), turbo: eeVerGE(manager.bridgeVersion, 2, 1, 21) || (size === 0x40000 && (opts.k2f ? eeVerGE(manager.bridgeVersion, 2, 1, 19) : eeVerGE(manager.bridgeVersion, 2, 1, 13))) ? 1 : 0 }, (m) => {
      if (m.boot) {
        if (m.opened) { if (!openShown) { openShown = true; onLog('SUCCESS', `Security OK (round ${m.round}). Reading…`); if (size === 0x10000) onLog('UART', `read-open RX ${m.probe || '(none)'}`); } return; }
        if (!onPrompted && m.round >= 1) { onPrompted = true; onLog('WARN', useSm3 ? '🔑🔑 ENGINE READY — TURN IGNITION ON NOW! 🔑🔑' : '🔑 IGNITION ON NOW!'); }
        if (m.round && m.round !== lastRound) { lastRound = m.round; if (m.wake || m.round % 15 === 0) onLog('INFO', `Round ${m.round} · wake ${m.wake ? '✓' : '✗'} · sess ${m.sess ? '✓' : '✗'} · sec ${m.sec ? '✓' : '✗'} · RX ${m.secr || 'none'} · probe ${m.probe || 'none'}${m.jw != null && m.jw >= 0 ? ` · jwake ${m.jw >= 0x1000 ? `fastinit rc=0x${(m.jw & 0xfff).toString(16)}` : m.jw === 0 ? 'gnd ok' : `rc=0x${m.jw.toString(16)}`}` : ''}`); }
        return;
      }
      if (m.pageprobe != null) { onLog('UART', `page probe 0x${Number(m.pageprobe).toString(16).toUpperCase().padStart(2, '0')}: TX ${m.rawtx} -> RX ${m.probeRx || '(none)'}${m.hit ? ' ✓' : ''}`); return; }
      if (m.fmtprobe != null) { if (m.sweep && !m.hit && !m.any && !m.probeRx && m.idx % 32 !== 1) { if (m.idx % 32 === 0) onLog('INFO', `Searching address range… (${m.idx} tries)`); return; } onLog('UART', `addr probe ${m.label || `fmt${m.fmtprobe}`} @0x${Number(m.paddr).toString(16).toUpperCase()} blk${m.pblk}: TX ${m.rawtx} -> RX ${m.probeRx || '(none)'}${m.hit ? ' ✓ NEW DATA' : m.any ? ' (same as page 0)' : ''}`); return; }
      if (m.reread != null) { onLog(m.hit ? 'SUCCESS' : 'WARN', `re-read block 0x${Number(m.reread).toString(16).toUpperCase().padStart(6, '0')}: ${m.hit ? 'OK' : 'still no reply'}`); return; }
      if (m.turbo) { onLog(m.baud === 62500 ? 'SUCCESS' : 'INFO', m.baud === 62500 ? 'Fast Read ENABLED @ 62500 baud (ECU ACKed, block 0x0 verified).' : `Fast baud not accepted (ACK ${m.ack || 'none'}) — staying at 10400.`); return; }
      if (m.turboblk != null) { onLog(m.hit ? 'SUCCESS' : 'INFO', m.hit ? `Block ${m.turboblk} B verified — reading with ${m.turboblk} B blocks.` : `Block ${m.turboblk} B not accepted.`); return; }
      if (m.blkprobe != null) { onLog('UART', `block probe fmt5 ${m.blkprobe}B: TX ${m.rawtx} -> RX ${m.probeRx || '(none)'}${m.hit ? ' ✓' : ''}`); return; }
      if (m.fmtselected != null) { onLog('SUCCESS', m.hiKey != null ? `Second 32 KB answered (key 0x${Number(m.hiKey).toString(16).toUpperCase()}, base 0x${Number(m.hiBase).toString(16).toUpperCase()}) — reading…` : `Read address format fmt${m.fmtselected} (block ${m.pblk}B) answered — reading…`); return; }
      if (m.regscan != null) { onProgress('read', Math.round((m.done / m.regs) * 100)); onLog('UART', `region scan 0x${Number(m.regscan).toString(16).toUpperCase().padStart(4, '0')}: ${m.hit ? 'data ✓' : 'no reply (skip)'}`); if (m.done === 1) onLog('INFO', 'Scanning flash regions…'); return; }
      if (m.rawrx != null) return;
      if (m.progress != null) {
        const pct = Math.round((m.progress / size) * 100);
        onProgress('read', pct);
        const kbps = m.elapsed_ms ? (m.progress / 1024) / (m.elapsed_ms / 1000) : 0;
        if (kbps) onSpeed(kbps);
        const now = Date.now();
        if (now - lastReadLog > 1000 || m.progress >= size) {
          lastReadLog = now;
          onLog('READ', `0x${(BASE + m.progress).toString(16).toUpperCase().padStart(6, '0')} (${pct}%) ${kbps.toFixed(1)} KB/s`);
        }
      }
    }).catch((e) => { onLog('ERROR', `Keihin cold read failed: ${e.why || e.message}`); return null; });
    if (!nr || !nr.ok) return { result: 'failed', bytes: null, duration_ms: Date.now() - t0 };
    const out2 = new Uint8Array(nr.data.length / 2);
    for (let i = 0; i < out2.length; i++) out2[i] = parseInt(nr.data.substr(i * 2, 2), 16);
    if (nr.empty >= Math.floor(size / 8)) { onLog('ERROR', 'Read FAILED: no data.'); return { result: 'failed', bytes: null, duration_ms: Date.now() - t0 }; }
    if (nr.empty) onLog('WARN', `${nr.empty} block(s) returned no reply.`);
    if (nr.skipped) onLog('WARN', `${(nr.skipped / 1024).toFixed(0)} KB not answered by ECU — filled with FF.`);
    if (nr.hiMissing) { onLog('WARN', 'K84: hanya 32KB pertama terbaca — BUKAN backup lengkap. Jangan dipakai untuk flash.'); onLog('SUCCESS', `Read (partial): ${out2.length} B · CRC32 ${crc32(out2)} (${((Date.now() - t0) / 1000).toFixed(1)}s)`); return { result: 'partial', bytes: out2, duration_ms: Date.now() - t0 }; }
    onLog('SUCCESS', `Read OK: ${out2.length} B · CRC32 ${crc32(out2)} (${((Date.now() - t0) / 1000).toFixed(1)}s)`);
    return { result: nr.empty > 4 ? 'partial' : 'success', bytes: out2, duration_ms: Date.now() - t0 };
  }
  await manager.klineWake().catch(() => {});
  // Match the Honda ECU Flasher capture: a single StartComm right before Security (NOT a 3s
  // flood — the flood desynced the line and garbled the 27 reply, e.g. 25 09 E0…).
  await manager.sendFramed([0x72, 0x05, 0x00, 0xF0, 0x99], { timeout: 300, pre: KLINE.P3 }).catch(() => {});

  // Security Access: password split into 7-byte halves -> 27 0B E0 <7 bytes> cs. The working
  // serial capture sends each half exactly ONCE (Hello, wAreYou) then reads immediately.
  const pwBytes = Array.from(pw).map((c) => c.charCodeAt(0) & 0xff);
  const sendSecurity = async () => {
    const halves = [];
    for (let off = 0; off < pwBytes.length; off += 7) { const s = pwBytes.slice(off, off + 7); while (s.length < 7) s.push(0x00); halves.push(s); }
    const seq = halves;
    for (const seven of seq) {
      const r = await manager.sendFramed(frame([0x27, 0x0B, 0xE0, ...seven]), { timeout: 800, pre: KLINE.P3 });
      onLog('UART', `SecAccess TX 27 0B E0 ${seven.map(hex).join(' ')} -> RX ${r.reply.length ? r.reply.map(hex).join(' ') : '(none)'}`);
    }
  };
  await sendSecurity();

  // Cold flow (opts.keihinCold): after entering the security password the read engine only
  // unlocks after a power cycle. Prompt OFF -> ON, detect the ECU coming back, re-send the
  // password, then enter read mode — mirrors the Honda tool's Connect -> password -> OFF/ON ->
  // Security Access -> read procedure.
  if (opts.keihinCold) {
    const windowMs = 60000;
    const alive = async () => {
      // Mirror Connect's proven wake: BREAK pulse + FE 04 72 8C x3, then StartComm 72 05 00 F0 99.
      // A cold-booted ECU only answers after this full wake+init, so treat a real (non-echo) reply
      // to either the wake or StartComm as "powered". (A single bare FE 04 72 8C wasn't enough.)
      await manager.klineWake().catch(() => {});
      for (let a = 1; a <= 3; a++) {
        const wk = await manager.sendCommand(CMD.WAKEUP, { timeout: 250, pre: a === 1 ? 0 : KLINE.P3 }).catch(() => new Uint8Array(0));
        if (wk && wk.length && !(wk.length === CMD.WAKEUP.length && sameBytes(wk, CMD.WAKEUP, CMD.WAKEUP.length))) return true;
        await sleep(100);
      }
      const sc = await manager.sendFramed([0x72, 0x05, 0x00, 0xF0, 0x99], { timeout: 350, pre: KLINE.P3 }).catch(() => ({ reply: [] }));
      return !!(sc.reply && sc.reply.length && sc.reply[0] !== 0x72);
    };
    // Phase 1 — wait for ignition OFF (ECU stops answering the probe).
    onLog('WARN', 'Security password sent. Now turn the ECU ignition OFF…');
    let sawOff = false; const tOff = Date.now();
    while (Date.now() - tOff < windowMs && !manager.abort) {
      if (!(await alive())) { sawOff = true; onLog('INFO', 'ECU OFF detected — now turn it back ON…'); break; }
      onLog('INFO', `Turn IGNITION OFF — ${Math.ceil((windowMs - (Date.now() - tOff)) / 1000)}s…`);
      await sleep(500);
    }
    if (manager.abort) { onLog('ERROR', 'Read aborted.'); return { result: 'aborted', bytes: null, duration_ms: Date.now() - t0 }; }
    if (!sawOff) onLog('WARN', 'ECU OFF not detected — turn OFF, then ON.');
    // Phase 2 — wait for ignition ON (ECU answers again).
    let powered = false; const tOn = Date.now();
    while (Date.now() - tOn < windowMs && !manager.abort) {
      if (await alive()) { powered = true; break; }
      onLog('INFO', `Turn IGNITION ON — ${Math.ceil((windowMs - (Date.now() - tOn)) / 1000)}s…`);
      await sleep(400);
    }
    if (manager.abort) { onLog('ERROR', 'Read aborted.'); return { result: 'aborted', bytes: null, duration_ms: Date.now() - t0 }; }
    if (powered) {
      // Honda ECU Flasher timing (from workbench): after ignition ON, wait ~0.5s then IMMEDIATELY
      // send Security. A long StartComm flood here misses the short post-boot security window (K93).
      onLog('SUCCESS', 'ECU powered back ON — waiting 0.5s then entering Security Access…');
      await sleep(500);
      await manager.klineWake().catch(() => {});
      await manager.sendFramed([0x72, 0x05, 0x00, 0xF0, 0x99], { timeout: 250, pre: KLINE.P3 }).catch(() => {});
    } else {
      onLog('WARN', 'No power-on detected in the window — trying to enter read mode anyway…');
    }
    await sendSecurity();
  }

  // KA_KEIHIN keepalive / read-mode arm (7E 06 01 01 00 7A) — the K93 256KB monitor confirms
  // this IS sent for 256KB too. The 82 82 10 06 enter-read-mode frame is 128KB-only.
  await manager.sendFramed([0x7E, 0x06, 0x01, 0x01, 0x00, 0x7A], { timeout: 500, pre: KLINE.P3 });
  if (size < 0x40000) {
    const rmR = await manager.sendFramed([0x82, 0x82, 0x10, 0x06, 0x00, 0xE6], { timeout: 800, pre: KLINE.P3 });
    onLog('UART', `Enter read-mode TX 82 82 10 06 00 E6 -> RX ${rmR.reply && rmR.reply.length ? rmR.reply.map(hex).join(' ') : '(none)'}`);
  }

  // ---- Native batch read (bridge >= 1.8): stream all requests in one message ----
  // The bridge loops WriteRead at full port speed with NO per-block WebSocket round-trip,
  // which is the ONLY way to read 256KB in reasonable time (per-request JS = ~0.1 KB/s).
  // 256KB K93 is now included: the old "stall" was caused by the wrong 8-byte block size
  // (every block failed -> 4 retries x 1s timeout -> looked like a hang). With the proven
  // 12-byte (0x0C) frames each block gets a 92 92 00 11 reply immediately, so the batch loop
  // runs fast. Progress streams every 250ms; abort is honoured between blocks.
  if (manager.mode === 'bridge' && manager.bridge && manager.bridge.readBlocks) {
    try {
      const frames = [];
      for (let off = 0; off < size; off += BLK) {
        const num = Math.min(BLK, size - off);
        const a = START + off;
        const page = (basePage + ((a >> 16) & 0xff)) & 0xff;
        const f = frame([0x82, 0x82, 0x00, 0x09, page, a & 0xff, (a >> 8) & 0xff, num]);
        for (const b of f) frames.push(b);
      }
      const framesHex = frames.map((b) => b.toString(16).padStart(2, '0')).join('');
      onPhase('reading');
      onLog('INFO', `Batch read ${Math.ceil(size / BLK)} blocks…`);
      const res = await manager.bridge.readBlocks(framesHex, { blocklen: 9, timeout: 800, retries: 3 }, (m) => {
        const pct = Math.round((m.progress / m.total) * 100);
        onProgress('reading', pct);
        const kbps = ((m.progress * BLK) / 1024) / ((m.elapsed_ms || 1) / 1000);
        onSpeed && onSpeed(kbps);
      });
      const hexstr = res.data || '';
      let empty2 = 0;
      for (let i = 0; i < size; i++) {
        const v = (i * 2 + 1 < hexstr.length) ? parseInt(hexstr.substr(i * 2, 2), 16) : 0xff;
        out[i] = v;
      }
      for (let i = 0; i < size; i++) if (out[i] === 0xff) empty2++;
      onPhase('done');
      const durB = Date.now() - t0;
      onLog('SUCCESS', `Read OK: ${size} B in ${(durB / 1000).toFixed(1)}s`);
      if (empty2 === size) onLog('WARN', 'Whole dump is 0xFF — the Security password may have been rejected.');
      return { result: empty2 === size ? 'partial' : 'success', bytes: out, duration_ms: durB };
    } catch (e) {
      if (manager.abort) { onLog('ERROR', 'Read aborted.'); return { result: 'aborted', bytes: out.slice(0, 0), duration_ms: Date.now() - t0 }; }
      onLog('WARN', `Batch read unavailable — using per-block.`);
    }
  }

  onPhase('reading');
  let read = 0, lastTick = Date.now(), lastRead = 0, empty = 0, firstLogged = false;
  while (read < size) {
    if (manager.abort) { onLog('ERROR', 'Read aborted.'); return { result: 'aborted', bytes: out.slice(0, read), duration_ms: Date.now() - t0 }; }
    const num = Math.min(BLK, size - read);
    // Address encoding (from KWT-702 log): 82 82 00 09 <page> <offLo> <offHi> <num>,
    // page = 0x0E + (offset>>16) (0x0E for first 64KB, 0x0F above); offLo/offHi little-endian.
    const a = START + read;
    const page = (basePage + ((a >> 16) & 0xff)) & 0xff;
    const cmd = frame([0x82, 0x82, 0x00, 0x09, page, a & 0xff, (a >> 8) & 0xff, num]);
    const r = await manager.sendFramed(cmd, { timeout: 1000, pre: KLINE.BLOCK_GAP });
    const rp = r.reply;
    if (!firstLogged) { onLog('UART', `TX ${cmd.map(hex).join(' ')} -> RX ${rp.slice(0, 24).map(hex).join(' ')}`); firstLogged = true; }
    let got = 0;
    if (rp && rp.length >= 5 && rp[0] === 0x92 && rp[1] === 0x92) {
      const dlen = rp[3] - 5;
      const data = rp.slice(4, 4 + Math.max(0, dlen));
      got = data.length;
      for (let i = 0; i < got; i++) out[read + i] = data[i];
      if (got === 0) empty++;
    } else { empty++; }
    read += got > 0 ? got : num;
    onProgress('reading', Math.round((read / size) * 100));
    const now = Date.now();
    if (now - lastTick > 250) {
      const kbps = ((read - lastRead) / 1024) / ((now - lastTick) / 1000);
      onSpeed && onSpeed(kbps);
      onLog('READ', `0x${(BASE + read).toString(16).toUpperCase().padStart(6, '0')} (${Math.round((read / size) * 100)}%) ${kbps.toFixed(1)} KB/s`);
      lastTick = now; lastRead = read;
    }
  }
  onPhase('done');
  const dur = Date.now() - t0;
  if (empty > 0) onLog('WARN', `${empty} blocks empty.`);
  onLog('SUCCESS', `Read complete: ${size} bytes in ${(dur / 1000).toFixed(1)}s`);
  return { result: empty > size / BLK / 2 ? 'partial' : 'success', bytes: out, duration_ms: dur };
}

// ------------------------------------------------------------------ Keihin real write
// Honda/Keihin K-Line block programming: init -> erase -> 128B block writes with a
// per-block ECU ACK + retry (no blind writing) -> sample read-back verify -> finalize.
// Fast Write drops the inter-block delay to ~1ms (MoToTan-style) for maximum speed.
async function runFlashKeihin(manager, firmware, opts, cb) {
  const { fastWrite = false, verify = true, startOffset = '0', checksumOffset = '0' } = opts;
  const { onLog, onProgress, onPhase, onSpeed } = cb;
  manager.abort = false;
  const t0 = Date.now();
  const total = firmware.size;

  onLog('INFO', `Keihin WRITE · start 0x${startOffset} · cks 0x${checksumOffset}`);
  onLog('INFO', fastWrite
    ? 'FAST WRITE — 0ms inter-command delay (Honda Flash Tools fast write).'
    : 'SAFE WRITE — 128B blocks, paced for reliability.');

  // Embed the Honda 8-bit "sum-to-zero" checksum where it applies (<=64KB H8, or SH705x @FFFF8).
  let writeBytes = firmware.bytes;
  const csAddr = parseInt(checksumOffset, 16);
  const is8bitCs = total <= 0x10000 || (checksumOffset || '').toUpperCase() === 'FFFF8';
  if (Number.isFinite(csAddr) && csAddr >= 0 && csAddr < total && is8bitCs) {
    writeBytes = new Uint8Array(firmware.bytes);
    const r = embedHonda8bitChecksum(writeBytes, csAddr);
    const hx2 = (v) => v.toString(16).toUpperCase().padStart(2, '0');
    onLog('INFO', `Checksum @0x${csAddr.toString(16).toUpperCase()}: ${r.changed ? `${hx2(r.before)} → ${hx2(r.corrected)}` : 'OK'}`);
  }

  // ---- Init / open programming session ----
  onPhase('handshake');
  onLog('UART', 'Keihin init: opening programming session...');
  const initR = await manager.sendFramed(manager.keepAlive || CMD.INIT_ECM, { timeout: 600, pre: KLINE.P2 });
  onLog(initR.ok ? 'SUCCESS' : 'WARN', initR.ok
    ? `Session ACK: ${initR.reply.map(hex).join(' ')}`
    : 'No init ACK — continuing (some Keihin ECUs answer only after erase).');

  // ---- Erase ----
  onPhase('erasing');
  onLog('ERASE', 'Erasing flash...');
  const erR = await manager.sendFramed(CMD.ERASE, { timeout: 4000, pre: KLINE.P2 });
  onLog(erR.ok ? 'SUCCESS' : 'WARN', erR.ok
    ? `Erase ACK: ${erR.reply.map(hex).join(' ')}`
    : 'No erase ACK — proceed with caution and verify the read-back.');
  onProgress('erasing', 100);

  // ---- Write 128-byte blocks with per-block ACK + retry ----
  onPhase('writing');
  const BLK = 128;
  const big = total > 0x10000; // 24-bit address for >64KB (SH705x 1MB)
  onLog('WRITE', `Writing ${total} bytes in ${Math.ceil(total / BLK)} blocks of ${BLK}B...`);
  let written = 0, lastTick = Date.now(), lastW = 0, firstLogged = false;
  while (written < total) {
    if (manager.abort) return _abort(onLog);
    const size = Math.min(BLK, total - written);
    const block = Array.from(writeBytes.slice(written, written + size));
    const frame = big
      ? [0x77, size & 0xff, (written >> 16) & 0xff, (written >> 8) & 0xff, written & 0xff, ...block]
      : [0x77, size & 0xff, (written >> 8) & 0xff, written & 0xff, ...block];
    frame.push(hondaChecksum(frame));
    let ok = false, lastRx = null;
    for (let retry = 0; retry < 6; retry++) {
      if (manager.abort) return _abort(onLog);
      const r = await manager.sendFramed(frame, { timeout: 1500, pre: fastWrite ? 0 : KLINE.P3 });
      lastRx = r;
      if (!firstLogged) {
        onLog('UART', `TX ${frame.slice(0, 5).map(hex).join(' ')} … (${frame.length}B)`);
        onLog('UART', `RX ${r.reply.length ? r.reply.map(hex).join(' ') : '(none)'}`);
        firstLogged = true;
      }
      if (r.ok) { ok = true; break; }         // ECU acknowledged this block
      if (!fastWrite) await sleep(150);        // busy / no reply: back off and resend the same block
    }
    if (!ok) {
      onLog('ERROR', `Write failed at 0x${written.toString(16).toUpperCase()} (no ACK after 6 tries). Last RX: ${lastRx && lastRx.raw.length ? lastRx.raw.map(hex).join(' ') : '(none)'}`);
      onLog('INFO', 'Tip: turn OFF Fast Write for a slower, more reliable write.');
      return { result: 'failed', duration_ms: Date.now() - t0 };
    }
    written += size;
    const pct = Math.round((written / total) * 100);
    onProgress('writing', pct);
    const now = Date.now();
    if (now - lastTick > 300) {
      const kbps = ((written - lastW) / 1024) / ((now - lastTick) / 1000);
      onSpeed && onSpeed(kbps);
      onLog('WRITE', `0x${written.toString(16).toUpperCase().padStart(6, '0')} (${pct}%) ${kbps.toFixed(1)} KB/s`);
      lastTick = now; lastW = written;
    }
  }
  onLog('WRITE', 'All blocks written.');

  // ---- Verify: read a few sampled blocks back and compare ----
  if (verify && !manager.abort) {
    onPhase('verifying');
    onLog('INFO', 'Verifying (sample read-back)...');
    const samples = [0, Math.floor(total / 2) & ~0x7f, Math.max(0, total - BLK)];
    let bad = 0, checked = 0;
    for (let si = 0; si < samples.length; si++) {
      if (manager.abort) return _abort(onLog);
      const a = samples[si];
      const reply = await manager.sendCommand(readMemCmd(a, BLK), { timeout: 1200, expect: 13 + BLK, pre: KLINE.BLOCK_GAP });
      const data = parseReadData(reply, BLK);
      if (data && data.length) {
        checked++;
        for (let i = 0; i < BLK && a + i < total; i++) if (data[i] !== writeBytes[a + i]) { bad++; break; }
      }
      onProgress('verifying', Math.round(((si + 1) / samples.length) * 100));
    }
    if (checked === 0) onLog('WARN', 'Verify: no read-back data.');
    else if (bad) onLog('ERROR', `Verify FAILED on ${bad}/${checked} sampled blocks.`);
    else onLog('SUCCESS', `Verify OK on ${checked} sampled blocks. Checksum @0x${checksumOffset}.`);
  }

  // ---- Finalize ----
  await manager.sendFramed(manager.keepAlive || CMD.INIT_ECM, { timeout: 500, pre: KLINE.P2 });
  onPhase('done');
  const dur = Date.now() - t0;
  onLog('SUCCESS', `WRITE OK (${(dur / 1000).toFixed(1)}s) — restart ECU.`);
  return { result: 'success', duration_ms: dur };
}

// ------------------------------------------------------------------ Keihin R8C 48KB real write
// Byte-exact replay of the CONFIRMED-GOOD write capture ("log writing.txt") matched to
// VERZA 38770-K18-902.bin (CRC 3794B035). The ECU accepts EXACTLY 355 blocks — three all-0xFF
// regions are SKIPPED. Hardware (2026-06) proved 384 contiguous blocks FAIL: the ECU rejected
// every block with status FF (wrong per-block accumulator) and finalize returned recovery 0x12.
// Frame: 7E 8B 01 06 <A1 A0> <128 data> <nextA1 nextA0> <Xhi Xlo> <cs>  ->  ACK 0E 05 06 02 E5
//   addr16 sequence = R8C_SEGMENTS [start,count]; bin offset = (addr16-0x400)*16;
//   nextA1A0 = next block's addr16 (0x0000 on the last block);
//   X = (0x10000 - sum(data128) - C[k]) & 0xFFFF, C[k] = ECU accumulator taken VERBATIM from the
//   capture (355 values). Verified: reconstructs all 355 capture frames byte-exact from the BIN.
const R8C_SEGMENTS = [[0x400, 228], [0xb60, 73], [0xe00, 53], [0xff8, 1]];
const R8C_C = [16,32,48,64,80,96,112,128,144,160,176,192,208,224,240,256,272,288,304,320,336,352,368,384,400,416,432,448,464,480,496,257,18,34,50,66,82,98,114,130,146,162,178,194,210,226,242,258,274,290,306,322,338,354,370,386,402,418,434,450,466,482,498,259,20,36,52,68,84,100,116,132,148,164,180,196,212,228,244,260,276,292,308,324,340,356,372,388,404,420,436,452,468,484,500,261,22,38,54,70,86,102,118,134,150,166,182,198,214,230,246,262,278,294,310,326,342,358,374,390,406,422,438,454,470,486,502,263,24,40,56,72,88,104,120,136,152,168,184,200,216,232,248,264,280,296,312,328,344,360,376,392,408,424,440,456,472,488,504,265,26,42,58,74,90,106,122,138,154,170,186,202,218,234,250,266,282,298,314,330,346,362,378,394,410,426,442,458,474,490,506,267,28,44,60,76,92,108,124,140,156,172,188,204,220,236,252,268,284,300,316,332,348,364,380,396,412,428,444,460,476,492,508,269,30,46,62,142,222,238,254,270,286,302,318,334,350,366,382,398,414,430,446,462,478,494,510,271,32,48,64,80,96,112,128,144,160,176,192,208,224,240,256,272,288,304,320,336,352,368,384,400,416,432,448,464,480,496,512,273,34,50,66,82,98,114,130,146,162,178,194,210,226,242,258,274,290,306,322,338,187,36,52,68,84,100,116,132,148,164,180,196,212,228,244,260,276,292,308,324,340,356,372,388,404,420,436,452,468,484,500,516,277,38,54,70,86,102,118,134,150,166,182,198,214,230,246,262,278,294,310,326,342,438,263];
async function runFlashKeihinR8C(manager, firmware, opts, cb) {
  const { verify = true, checksumOffset = '7600', fastWrite = false } = opts;
  const { onLog, onProgress, onPhase, onSpeed } = cb;
  manager.abort = false;
  const t0 = Date.now();
  const total = firmware.size;
  if (total !== 49152 && total !== 65536) { onLog('ERROR', `File must be 48/64KB (got ${total} B).`); return { result: 'failed', duration_ms: 0 }; }

  // 48KB R8C (base 0x4000 → addr16 0x400) and 64KB M16C/R8C (base 0x8000 → addr16 0x800) share the
  // SAME 7E 8B 128-byte block programming flow (PGM/-FI handshake, ~15s settle, bulk erase, arm,
  // 08/09/0A/0C/0D finalize). Decoded byte-exact from the user's captures:
  //   48KB: base 0x400, 384 blocks, C = Cpos(k),   checksum @0x9FEF, terminator C=0x107.
  //   64KB: base 0x800, 512 blocks, C = Cpos(k)+8, checksum @0x0000 (start 0x8000), terminator C=0xDF
  //         (K15-921 last non-FF block = 505). Verified against "64kb write.txt": every contiguous
  //         block + the 0xDF terminator reconstruct the capture X byte-for-byte (0 mismatches).
  const is64 = total === 65536;
  const baseAddr = is64 ? 0x0800 : 0x400;
  const cAdd = is64 ? 8 : 0;
  // Embed the Honda 8-bit sum-to-zero checksum at the ECU-verified slot (48KB @0x9FEF; 64KB @0x0).
  // No-op on an already-valid image; corrects the right byte on an edited tune so the ECU commits.
  const writeBytes = new Uint8Array(firmware.bytes);
  const csAddr = is64 ? 0x0000 : 0x9FEF;
  if (csAddr >= 0 && csAddr < total) {
    const r = embedHonda8bitChecksum(writeBytes, csAddr);
    const hx2 = (v) => v.toString(16).toUpperCase().padStart(2, '0');
    onLog('INFO', `Checksum @0x${csAddr.toString(16).toUpperCase()}: ${r.changed ? `${hx2(r.before)} → ${hx2(r.corrected)}` : 'OK'}`);
  }

  const fr = (a) => { const b = a.slice(); b.push(hondaChecksum(b)); return b; };
  // CH340: the first 2 bytes of a fast ECU reply can merge into 1 garbage byte (e.g. "A8 04 00 E9" = 0E 05 04 00 E9).
  // Rebuild header 0E <len> only when the echoed cmd byte AND the Honda sum-to-zero checksum both match.
  const fixHdr = (rep, cmd) => {
    if (!rep || rep.length < 3 || rep[0] === 0x0e || rep[0] === 0x0d) return null;
    const rec = [0x0e, rep.length + 1, ...rep.slice(1)];
    if (rec[2] !== cmd) return null;
    return (rec.reduce((s, v) => s + v, 0) & 0xff) === 0 ? rec : null;
  };
  const send = async (a, to = 800) => {
    const r = await manager.sendFramed(fr(a), { timeout: to, pre: KLINE.P2 });
    const rec = fixHdr(r.reply, a[3]);
    if (!rec) return r;
    onLog('UART', `RX ${r.reply.map(hex).join(' ')} → header rebuilt ${rec.map(hex).join(' ')} (checksum OK)`);
    return { ...r, reply: rec };
  };
  // Handshake helper: send + log TX/RX and decode the ECU state byte so we can see exactly
  // how far the programming state machine got (capture goes 0x00 -> 0x10 -> 0x20 -> 0x30 -> armed).
  const sendL = async (label, a, to = 800) => {
    const r = await send(a, to);
    const rx = r.reply.length ? r.reply.map(hex).join(' ') : (r.raw && r.raw.length ? r.raw.map(hex).join(' ') : '(none)');
    onLog('UART', `${label}: TX ${a.map(hex).join(' ')} → RX ${rx}`);
    return r;
  };
  const poll = async (label, expect) => {
    const r = await send([0x7e, 0x06, 0x01, 0x01, 0x00]);
    const st = (r.reply.length >= 4 && r.reply[0] === 0x0e && r.reply[2] === 0x01) ? r.reply[3] : null;
    const rx = r.reply.length ? r.reply.map(hex).join(' ') : '(none)';
    if (st === null) onLog('WARN', `state poll (${label}): no valid reply — RX ${rx}`);
    else if (expect != null && st !== expect) onLog('WARN', `state ${label}: 0x${hex(st)} (exp 0x${hex(expect)})`);
    else onLog('UART', `state poll (${label}): 0x${st == null ? '??' : hex(st)}`);
    return st;
  };

  // ---- INIT-WRITE (open programming session) ----
  onPhase('handshake');
  onLog('UART', `Keihin ${is64 ? '64KB' : '48KB'} 7E-8B init-write: opening programming session...`);
  await sendL('wake', [0xfe, 0x04, 0x72]);                   // re-wake bus (capture does this before init-write)
  await sendL('init PGM', [0x7d, 0x08, 0x01, 0x02, 0x50, 0x47, 0x4d]);   // "PGM"
  await sendL('init -FI', [0x7d, 0x08, 0x01, 0x03, 0x2d, 0x46, 0x49]);   // "-FI"
  await poll('after init', 0x00);

  // ---- PRE-ERASE (exact ordered sequence from capture) ----
  // The reference tool idles ~14s in the open programming session (state 0x00) BEFORE the erase.
  // Erasing immediately makes THIS ECU refuse the erase with status F0 (observed 2026-06), so we
  // reproduce that settle wait. Keep the ignition KEY ON with the ENGINE OFF during flashing.
  onPhase('erasing');
  // 64KB short settle (10 polls) was HW-tested 2026-10-10 on K84 via SM3 Pro → erase refused 0xF0. Keep 15s for all.
  onLog('INFO', 'Session open — waiting 15s before erase. Keep IGNITION ON.');
  for (let s = 0; s < 15; s++) { if (manager.abort) return _abort(onLog); await sleep(1000); }
  onLog('ERASE', 'Erasing flash (this can take ~15s — do not cut power)...');
  // Reference tool (KeihinPreWriteFlash in FormMain.cs) FIRES the erase and does NOT check its reply.
  // The erase command normally answers with a busy/echo status that differs by transport
  // (0x02 on Tactrix/J2534, 0x00 on CH340 — both harmless). Erase success is validated LATER at
  // cmd-04 (7E 06 01 04 FF) and cmd-05 (7E 05 01 05), each status == 0x00 (0xFA = block error).
  const erR = await sendL('bulk erase', [0x7e, 0x05, 0x01, 0x02], 16000);
  onLog('UART', `Erase command reply: ${erR.reply.length >= 4 ? '0x' + hex(erR.reply[3]) : '(no framed reply)'} — informational; validated at cmd-04/cmd-05.`);
  await sleep(1500);
  await sendL('erase-2', [0x7e, 0x07, 0x01, 0x03, 0x00, 0x00], 8000);
  await poll('after erase', 0x10);
  await sendL('range', [0x7e, 0x0b, 0x01, 0x0b, 0x00, 0x00, 0x00, 0xff, 0xff, 0xff], 8000);
  await poll('after range', 0x20);
  await sendL('cmd-0e', [0x7e, 0x07, 0x01, 0x0e, 0x01, 0x90]);
  // Erase validation point #1 (reference array7: status must be 0x00).
  const er04 = await sendL('cmd-04', [0x7e, 0x06, 0x01, 0x04, 0xff], 4000);
  if (!(er04.reply.length >= 4 && er04.reply[3] === 0x00)) {
    const st = er04.reply.length >= 4 ? hex(er04.reply[3]) : '??';
    onLog('ERROR', `Erase not confirmed (0x${st}) — aborted, nothing written.`);
    return { result: 'failed', duration_ms: Date.now() - t0 };
  }
  await sleep(2000);
  // Erase validation point #2 (reference array8: status must be 0x00; 0xFA = block error).
  let er05 = await sendL('cmd-05', [0x7e, 0x05, 0x01, 0x05], 3000);
  // 0x01 = erase still busy (seen in TunerForge 64KB log) — re-ask a few times before judging.
  for (let i = 0; i < 8 && er05.reply.length >= 4 && er05.reply[3] === 0x01; i++) {
    if (manager.abort) return _abort(onLog);
    await sleep(1000);
    er05 = await sendL('cmd-05', [0x7e, 0x05, 0x01, 0x05], 3000);
  }
  if (er05.reply.length >= 4 && er05.reply[3] === 0xFA) {
    onLog('ERROR', 'Erase FAILED (0xFA) — aborted.');
    return { result: 'failed', duration_ms: Date.now() - t0 };
  }
  if (!(er05.reply.length >= 4 && er05.reply[3] === 0x00)) {
    const st = er05.reply.length >= 4 ? hex(er05.reply[3]) : '??';
    onLog('ERROR', `Erase not confirmed (0x${st}) — aborted.`);
    return { result: 'failed', duration_ms: Date.now() - t0 };
  }
  onLog('SUCCESS', 'Flash erase CONFIRMED (cmd-04 & cmd-05 = 0x00).');
  await poll('after erase confirm');
  // Reference tool does NOT require the 0x30 armed-state; keep it only as a non-fatal check so a
  // transport-dependent poll value never causes a false abort now that erase is proven above.
  const st30 = await poll('before arm', 0x30);
  if (st30 !== 0x30) {
    onLog('WARN', `Armed state 0x${st30 == null ? '??' : hex(st30)} — continuing.`);
  }
  // 48KB K25-901 does not accept 62500 (7E 06 01 A0 02 unanswered at 10400 and 62500, HW-tested 2026-10-09).
  await sendL('arm', [0x7e, 0x06, 0x01, 0xa0, 0x02]);        // arm write (capture sends twice)
  await sendL('arm', [0x7e, 0x06, 0x01, 0xa0, 0x02]);
  await sleep(1200);
  // 64KB Fast Write probe (user-approved 2026-10-10): the arm (= 62500 select) is unanswered at 10400, so
  // switch to 62500 and send a READ-ONLY state poll. Valid reply → write @62500; else back to 10400 + re-check.
  let fast64 = false;
  // K84-901 (ECM 010232…) HW-tested 2026-10-10: no reply at 62500 → skip the probe (saves ~2s).
  const noFast64 = /^010232/.test(String(opts.ecmId || '').toUpperCase().replace(/[^0-9A-F]/g, '')) || /K84/i.test(String(opts.part || ''));
  if (is64 && fastWrite && !noFast64 && manager.mode === 'bridge' && manager.setBaud) {
    onLog('WRITE', 'Fast Write (Keihin 64KB): probing 62500 baud (read-only state poll)...');
    if (await manager.setBaud(62500)) {
      await sleep(150);
      const s62 = await poll('62500 probe');
      if (s62 != null) {
        fast64 = true;
        onLog('SUCCESS', `Fast Write ENABLED @ 62500 baud (ECU state 0x${hex(s62)}).`);
      } else {
        await manager.setBaud(10400);
        await sleep(150);
        onLog('INFO', 'No reply at 62500 — back to 10400 (proven path).');
      }
    }
    if (!fast64) {
      const s104 = await poll('10400 re-check');
      if (s104 == null) {
        onLog('ERROR', 'ECU not answering at 10400 or 62500 — aborted before write. Cycle ignition and retry WRITE.');
        return { result: 'failed', duration_ms: Date.now() - t0 };
      }
    }
  }
  const restore104 = async () => { if (fast64) { onLog('WRITE', 'Fast Write: restoring K-Line to 10400 baud...'); await manager.setBaud(10400); fast64 = false; } };
  onProgress('erasing', 100);
  if (manager.abort) return _abort(onLog);

  // ---- WRITE all 384 blocks CONTIGUOUSLY (0x400..0xFF8) ----
  // The capture skipped three all-0xFF regions (original VERZA had 0xFF there). But a MODIFIED tune
  // stores data in those regions — notably the checksum byte at file 0x9FEF (block addr16 0xDF8) —
  // and skipping them leaves 0xFF in the ECU, so commit verification fails (finalize stalls at 0x0D).
  // Every CONTIGUOUS block's accumulator C = Cpos((addr16-0x400)/8) — proven on all 351 contiguous
  // capture blocks (0 mismatches). The capture's 4 special C values only existed because of the skips
  // (next16 jump); writing contiguously removes them. Last block (0xFF8) keeps next16=0, C=0x107.
  onPhase('writing');
  const Cpos = (pos) => { const cyc = Math.floor(pos / 32), pic = pos % 32; return (pic < 31 ? (0x10 * (pic + 1) + 2 * cyc) : (0x101 + 2 * cyc)) + cAdd; };
  const nblocks = total / 128;   // 384 (48KB) or 512 (64KB)
  // Write contiguously up to (and including) the last NON-0xFF block. Trailing all-FF blocks are the
  // erased state, so — exactly like the reference tool — we terminate at the last real block. Interior
  // FF regions ARE written (an edited tune may store data there); only the trailing tail is skipped.
  let termIdx = nblocks - 1;
  if (is64) {
    termIdx = 0;
    for (let k = 0; k < nblocks; k++) {
      const off = k * 128; let allFF = true;
      for (let j = 0; j < 128; j++) if (writeBytes[off + j] !== 0xff) { allFF = false; break; }
      if (!allFF) termIdx = k;
    }
  }
  const writeCount = termIdx + 1;
  // 64KB sparse write (reference 64KB tool log 2026-10-10, K84): interior all-FF blocks are SKIPPED and the
  // previous block's next16 jumps to the next written block. Accumulator C = addrHi+addrLo+nextHi+nextLo
  // (verified on all 487 reference frames, incl. jumps 0x0BA0→0x0C00 / 0x0D98→0x0E00 and terminator).
  const isFFBlk = (k) => { const off = k * 128; for (let j = 0; j < 128; j++) if (writeBytes[off + j] !== 0xff) return false; return true; };
  const wIdx = [];
  for (let k = 0; k <= termIdx; k++) if (!is64 || k === termIdx || !isFFBlk(k)) wIdx.push(k);
  const nextOf = new Map(); wIdx.forEach((k, i) => nextOf.set(k, wIdx[i + 1]));
  const bsum16 = (a) => ((a >> 8) & 0xff) + (a & 0xff);
  // Terminator accumulator: 48KB uses the proven 0x107. 64KB is capture-verified — 0xDF is byte-exact
  // for the K15-921 capture (last real block 505); a couple of derived fallbacks cover a tune whose
  // last real block differs (a wrong terminator is simply rejected without committing — safe to retry).
  // Terminator C = addr16 byte sum (next16 = 0): K15 capture block 505 @0x17C8 → 0x17+0xC8 = 0xDF; 48KB 0xFF8 → 0x107.
  const termAddr = baseAddr + termIdx * 8;
  const termC = ((termAddr >> 8) & 0xff) + (termAddr & 0xff);
  const termCandidates = is64 ? [...new Set([termC, 0xdf, (Cpos(termIdx) - 0x10) & 0xffff, Cpos(termIdx), 0x107])] : [0x107];
  onLog('WRITE', `Building ${wIdx.length} write frames (base addr16 0x${baseAddr.toString(16)}, terminator block ${termIdx})…`);
  // Build every 139-byte frame up front (identical construction to the per-block path).
  const buildR8CFrame = (k, cOverride) => {
    const addr16 = baseAddr + k * 8;
    const isLastBlk = k === termIdx;
    const next16 = isLastBlk ? 0x0000 : (is64 ? baseAddr + nextOf.get(k) * 8 : addr16 + 8);
    const C = cOverride != null ? cOverride : (isLastBlk ? termCandidates[0] : (is64 ? bsum16(addr16) + bsum16(next16) : Cpos(k)));
    const off = k * 128;
    const data = Array.from(writeBytes.slice(off, off + 128));
    let s = 0; for (const x of data) s += x;
    const X = (0x10000 - s - C) & 0xffff;
    return fr([0x7e, 0x8b, 0x01, 0x06, (addr16 >> 8) & 0xff, addr16 & 0xff,
      ...data, (next16 >> 8) & 0xff, next16 & 0xff, (X >> 8) & 0xff, X & 0xff]);
  };

  // Fast batch (bridge-side loop) is a Windows C# bridge feature (keihinR8CBlocks, >=1.9.49).
  // The Android bridge (v2.0.x) advertises a numerically-higher version but does NOT reply to
  // this RPC, so it fails with an empty result (block ?, RX none). Gate the batch on the user's
  // "Fast Write" toggle: OFF → per-block path (same writeRead the erase used, ACK per block).
  // Fast batch is 48KB-only for now: the 64KB terminator uses candidate-retry which the bridge-side
  // loop can't do, so 64KB always uses the per-block path (also better for first hardware validation).
  const canR8CBatch = !is64 && fastWrite && manager.mode === 'bridge' && manager.bridge && manager.bridge.keihinR8CBlocks && eeVerGE(manager.bridgeVersion, 1, 9, 49);
  if (!fastWrite) onLog('INFO', 'Fast Write OFF — per-block mode.');
  let batchWrote = false;
  if (canR8CBatch) {
    // ---- FAST: stream all frames to the bridge; it loops WriteReadKeihin server-side ----
    const framesHex = [];
    for (let k = 0; k < nblocks; k++) framesHex.push(Array.from(buildR8CFrame(k)).map((b) => b.toString(16).padStart(2, '0')).join(''));
    onLog('WRITE', `Fast batch: streaming ${nblocks} × 139B blocks to bridge (bridge-side loop, no per-block round-trip)…`);
    let lastTick = Date.now(), lastW = 0;
    let r = null;
    try {
      r = await manager.bridge.keihinR8CBlocks(framesHex.join(''), { blocklen: 139, timeout: 1500, retries: 6 }, (m) => {
        if (m.progress != null) {
          const pct = Math.round((m.progress / m.total) * 100);
          onProgress('writing', pct);
          const now = Date.now();
          if (now - lastTick > 400 || m.progress >= m.total) {
            const done = m.progress * 128;
            const kbps = ((done - lastW) / 1024) / ((now - lastTick) / 1000);
            onSpeed && onSpeed(kbps);
            onLog('WRITE', `block ${m.progress}/${m.total} (${pct}%) ${kbps.toFixed(1)} KB/s`);
            lastTick = now; lastW = done;
          }
        }
      });
    } catch (e) {
      onLog('ERROR', `Batch write error: ${e.message} — cycle ignition, retry.`);
      return { result: 'failed', duration_ms: Date.now() - t0 };
    }
    if (r && r.rejected) {
      const fi = r.failIndex != null ? r.failIndex : '?';
      const addrHex = r.failIndex != null ? (baseAddr + r.failIndex * 8).toString(16) : '?';
      onLog('ERROR', `Block ${fi} REJECTED. RX: ${r.rx ? r.rx : '(none)'}`);
      return { result: 'failed', duration_ms: Date.now() - t0 };
    }
    if (!r || !r.ok) {
      // Empty / no-diagnostic result = this bridge doesn't answer the batch RPC (Android v2.0.x).
      // The session is still armed (state 0x30) and nothing was committed → auto-fall back to the
      // proven per-block path in the SAME session instead of failing the recovery.
      onLog('WARN', '↳ Batch not supported — switching to per-block…');
    } else {
      batchWrote = true;
      onLog('WRITE', `All ${nblocks} blocks written (fast batch, ${((r.elapsed_ms || 0) / 1000).toFixed(1)}s).`);
    }
  }
  if (!batchWrote) {
  onLog('INFO', 'Per-block mode…');
  onLog('WRITE', is64 && wIdx.length < writeCount ? `Writing ${wIdx.length} blocks of 128B (${writeCount - wIdx.length} empty 0xFF blocks skipped)...` : `Writing ${writeCount} blocks of 128B (contiguous)...`);
  let lastTick = Date.now(), lastW = 0, firstLogged = false;
  for (let wi = 0; wi < wIdx.length; wi++) {
    const k = wIdx[wi];
    if (manager.abort) return _abort(onLog);
    const addr16 = baseAddr + k * 8;
    const isLastBlk = k === termIdx;
    // A programmed block ACKs with status 0x02 -> 0E 05 06 02 E5. The FINAL block (next16 = 0x0000)
    // instead ACKs with status 0x00 -> 0E 05 06 00 E7. For 64KB the terminator accumulator isn't
    // formula-derivable, so try candidate C values in turn — a wrong one is rejected WITHOUT
    // committing, and the correct one advances the ECU toward commit.
    const cList = isLastBlk ? termCandidates : [null];
    const R8C_BLOCK_GAP = 12;
    let ok = false, rejected = false, lastRx = null;
    for (let ci = 0; ci < cList.length && !ok && !rejected; ci++) {
      const frame = buildR8CFrame(k, cList[ci]);
      let hardReject = false;
      for (let retry = 0; retry < 6; retry++) {
        if (manager.abort) return _abort(onLog);
        const r = await manager.sendFramed(frame, { timeout: 1500, pre: retry === 0 ? (fastWrite ? KLINE.TURBO : R8C_BLOCK_GAP) : KLINE.P3 });
        lastRx = r;
        if (!firstLogged) {
          onLog('UART', `TX ${frame.slice(0, 6).map(hex).join(' ')} … (${frame.length}B)`);
          onLog('UART', `RX ${r.reply.length ? r.reply.map(hex).join(' ') : '(none)'}`);
          firstLogged = true;
        }
        if (r.reply.length >= 4 && r.reply[0] === 0x0e && r.reply[2] === 0x06) {
          if (r.reply[3] === 0x02 || (isLastBlk && r.reply[3] === 0x00)) { ok = true; break; }
          // 0E 07 06 03 <addr16> = sequence error carrying the address the ECU expects NEXT. If that is
          // our next block, this block was already accepted but its ACK got lost on the wire (Android
          // CH340/FTDI drops the first ACK bytes) — treat as accepted instead of aborting the write.
          if (r.reply[3] === 0x03 && r.reply.length >= 6 && (((r.reply[4] << 8) | r.reply[5]) === ((addr16 + 8) & 0xffff))) {
            onLog('WARN', `Block ${k}: ACK lost, ECU already at next address 0x${(addr16 + 8).toString(16)} — continuing.`);
            ok = true; break;
          }
          hardReject = true; break;   // explicit rejection status for this frame
        }
        await sleep(120);
      }
      if (ok) {
        if (isLastBlk && cList.length > 1) onLog('INFO', `Terminator block ${k} committed with C=0x${cList[ci].toString(16)}.`);
        break;
      }
      if (hardReject && !isLastBlk) { rejected = true; break; }        // real error mid-image
      if (hardReject && isLastBlk && ci < cList.length - 1) {
        onLog('WARN', `Terminator C=0x${cList[ci].toString(16)} rejected (RX ${lastRx.reply.map(hex).join(' ')}) — trying next candidate…`);
      }
    }
    if (!ok) {
      const rx = lastRx && lastRx.reply.length ? lastRx.reply.map(hex).join(' ') : (lastRx && lastRx.raw && lastRx.raw.length ? lastRx.raw.map(hex).join(' ') : '(none)');
      await restore104();
      if (rejected) {
        onLog('ERROR', `Block ${k}/${writeCount} REJECTED — stopped. RX: ${rx}`);
      } else {
        onLog('ERROR', `Block ${k}/${writeCount}: no ACK — stopped. RX: ${rx}`);
      }
      return { result: 'failed', duration_ms: Date.now() - t0 };
    }
    const pct = Math.round(((wi + 1) / wIdx.length) * 100);
    onProgress('writing', pct);
    const now = Date.now();
    if (now - lastTick > 300) {
      const doneBytes = (wi + 1) * 128;
      const kbps = ((doneBytes - lastW) / 1024) / ((now - lastTick) / 1000);
      onSpeed && onSpeed(kbps);
      onLog('WRITE', `block ${wi + 1}/${wIdx.length} (${pct}%) ${kbps.toFixed(1)} KB/s`);
      lastTick = now; lastW = doneBytes;
    }
  }
  onLog('WRITE', `All ${wIdx.length} blocks written.`);
  }

  // ---- POST-WRITE (finalize) ----
  // The commit state machine needs ~1s to advance between each step (capture: 08→0x40,
  // 09→0x50, 0a→0x50, 0c→0x0F). Firing back-to-back makes finalize return 0x12 (recovery),
  // so we settle ~1s and poll after each step, then poll until 0x0F before finalizing.
  onPhase('verifying');
  onLog('INFO', 'Finalizing...');
  await sendL('fin-08', [0x7e, 0x05, 0x01, 0x08], 3000);
  await sleep(1000); await poll('fin-08', 0x40);
  await sendL('fin-09', [0x7e, 0x05, 0x01, 0x09], 2000);
  await sleep(1000); await poll('fin-09', 0x50);
  await sendL('fin-0a', [0x7e, 0x05, 0x01, 0x0a], 2000);
  await sleep(1000); await poll('fin-0a', 0x50);
  await sendL('fin-0c', [0x7e, 0x05, 0x01, 0x0c], 2000);
  // poll until state reaches 0x0F (ready to commit) — up to ~6s
  let st0f = null;
  for (let i = 0; i < 12; i++) {
    if (manager.abort) return _abort(onLog);
    await sleep(500);
    st0f = await poll('fin-0c', 0x0f);
    if (st0f === 0x0f) break;
  }
  if (st0f !== 0x0f) {
    await restore104();
    onLog('ERROR', `Commit state not 0x0F (0x${st0f == null ? '??' : hex(st0f)}) — retry WRITE.`);
    onProgress('verifying', 100); onPhase('done');
    return { result: 'failed', duration_ms: Date.now() - t0 };
  }
  const finR = await sendL('finalize', [0x7e, 0x05, 0x01, 0x0d], 2000);   // -> 0E 05 0D 0F committed
  await restore104();
  const finReply = finR.reply.length ? finR.reply.map(hex).join(' ') : '(no reply)';
  const committed = finR.reply.length >= 4 && finR.reply[0] === 0x0e && finR.reply[2] === 0x0d && finR.reply[3] === 0x0f;
  if (committed) {
    onLog('SUCCESS', `Finalize: ${finReply} — state 0x0F (committed).`);
  } else {
    const st = finR.reply.length >= 4 ? hex(finR.reply[3]) : '??';
    onLog('ERROR', `Finalize FAILED (state ${st}) — ECU still in recovery.`);
    onProgress('verifying', 100);
    onPhase('done');
    return { result: 'failed', duration_ms: Date.now() - t0 };
  }
  onProgress('verifying', 100);

  onPhase('done');
  const dur = Date.now() - t0;
  onLog('SUCCESS', `WRITE OK (${(dur / 1000).toFixed(1)}s) — cycle ignition.`);
  return { result: 'success', duration_ms: dur };
}

// ------------------------------------------------------------------ Keihin 56KB real write
// Decoded byte-exact from the user's K-Line capture (write log.txt + 56KB FLASH.bin,
// 38770-KWT-912 / ECM 01008B1602). Verified offline: the leading 7E 8B 128B block AND all
// 896 7E 4B 64B blocks reconstruct byte-for-byte from bin + this formula + Honda frame cs.
//   lead:  7E 8B 01 06 <00 00> <128 data> <00 08> <Xhi Xlo> cs   (X = 0x10000 - sum - 8)
//   block: 7E 4B 01 06 <A1 A0> <64 data> <nA1 nA0> <Xhi Xlo> cs  -> ACK 0E 05 06 02 E5
// addr16 = pos*4 (16-byte units); next = addr+4 (0 on the final block). C(pos) is positional:
//   cyc=pos/64, pic=pos%64 -> pic<63 ? 4+2*cyc+8*pic : 0xFD+2*cyc ; final block (pos 895) = 0x109.
//   X = (0x10000 - sum(data) - C(pos)) & 0xFFFF. NOTE: agent-derived, NOT yet hardware-validated.
// Denso Titan security descriptors are stored as: <6 const bytes C0 C1 C2 (LE)> <5-byte ECM ID> …
// The APP block carries the full ID (WRITE key constants); the BOOTLOADER block carries the ID with a
// wiped tail (…0000) and the RECOVERY key constants. Scan the loaded BIN for the recovery descriptor.
// Cari kandidat konstanta kunci Denso di dalam BIN. ID bisa tersimpan beberapa bentuk, jadi semua pola
// dicoba dan semua kandidat dikembalikan (kunci salah hanya ditolak security, tidak merusak flash).
function findDensoKeyCandidates(bytes, idHex) {
  const out = [];
  if (!bytes || bytes.length < 16 || !idHex || idHex.length < 6) return out;
  const push = (p, pat) => {
    if (p < 0 || p + 6 > bytes.length) return;
    const cLE = { C0: bytes[p] | (bytes[p + 1] << 8), C1: bytes[p + 2] | (bytes[p + 3] << 8), C2: bytes[p + 4] | (bytes[p + 5] << 8), at: p, pat: pat + '/LE' };
    const cBE = { C0: (bytes[p] << 8) | bytes[p + 1], C1: (bytes[p + 2] << 8) | bytes[p + 3], C2: (bytes[p + 4] << 8) | bytes[p + 5], at: p, pat: pat + '/BE' };
    // XTune membuktikan konstanta Denso = 6 byte sebelum ID, LITTLE-ENDIAN. LE jadi prioritas.
    for (const x of [cLE, cBE]) {
      if (!x.C0 && !x.C1) continue;
      if (!out.some((y) => y.C0 === x.C0 && y.C1 === x.C1 && y.C2 === x.C2)) out.push(x);
    }
  };
  for (const n of [10, 8, 6]) {
    if (idHex.length < n) continue;
    const id = idHex.slice(0, n);
    const bs = [];
    for (let i = 0; i + 1 < id.length; i += 2) bs.push(parseInt(id.slice(i, i + 2), 16));
    const variants = [
      { b: bs, pat: `ID${n}` },
      { b: bs.slice().reverse(), pat: `ID${n}-rev` },
      { b: Array.from(id, (ch) => ch.charCodeAt(0)), pat: `ID${n}-ascii` },
    ];
    for (const v of variants) {
      for (let o = 6; o + v.b.length <= bytes.length; o++) {
        let ok = true;
        for (let j = 0; j < v.b.length; j++) if (bytes[o + j] !== v.b[j]) { ok = false; break; }
        if (ok) push(o - 6, v.pat);
      }
    }
  }
  return out.slice(0, 8);
}

function findDensoRecoveryConsts(bytes, idPrefixHex) {
  if (!bytes || bytes.length < 8 || !idPrefixHex || idPrefixHex.length < 6) return null;
  const pfx = [];
  for (let i = 0; i + 1 < idPrefixHex.length; i += 2) pfx.push(parseInt(idPrefixHex.slice(i, i + 2), 16));
  for (let o = 6; o + pfx.length + 2 <= bytes.length; o++) {
    let ok = true;
    for (let j = 0; j < pfx.length; j++) if (bytes[o + j] !== pfx[j]) { ok = false; break; }
    if (!ok) continue;
    if (bytes[o + pfx.length] !== 0x00 || bytes[o + pfx.length + 1] !== 0x00) continue;
    const p = o - 6;
    return { C0: bytes[p] | (bytes[p + 1] << 8), C1: bytes[p + 2] | (bytes[p + 3] << 8), C2: bytes[p + 4] | (bytes[p + 5] << 8), at: p };
  }
  return null;
}

// ====================== HONDA DENSO — Titan 160 K2G-B12 (384KB, 7C/0C K-Line) ======================
// Decoded byte-for-byte from the user's the reference capture write capture (kline_capture_20260930_210339.txt):
//   WAKE 72/02:  FE 04 72 8C -> 0E 04 72 7C ; 72 05 00 F0 99 -> 02 04 00 FA ; ID 72 07 72 00 00 05 10
//                -> 02 0B 72 00 00 <ID5>  (Titan 160 K2G-B12 = 0104190802)
//   SECURITY (fixed): 7B 08 03 02 58857922 | 7B 08 03 03 8575136A | 7B 05 03 04 (->seed) | 7B 07 03 05 48D45A
//                     each -> 0B 05 0x 00
//   ERASE:  poll 7C 06 03 01 00 -> 0C 05 01 <state> ; range 7C 0B 03 0B 13375AECC8A5 ;
//           7C 06 03 04 FF -> 0C 05 04 00 ; 7C 05 03 05 -> 0C 05 05 01 (busy) ... until 0C 05 05 00
//   WRITE:  7C 8B 03 06 <aHi aLo> <128 data> <nHi nLo> <XHi XLo>  -> ACK 0C 05 06 02 (last block 00)
//   FINAL:  7C 05 03 08 -> 0C 05 08 00 (state 0x40); 09; 0A (state 0x50); 0C -> 0C 05 0C 00
// Dry-run: wake → ID → security → key → report status, then STOP before erase (non-destructive).
// Used to validate the derived seed→key on a real ECU without risking the flash.
export async function testUnlockDenso(manager, opts, cb) {
  return runFlashDenso(manager, { size: 0, bytes: new Uint8Array() }, { ...opts, unlockOnly: true }, cb);
}

// READ-ONLY probe after unlock: tries unused bootloader command bytes (never 04/05/06/08/09/0A/0B/0C)
// and reports any data-bearing reply, matched against the loaded reference BIN (raw or de-obfuscated).
export async function probeReadDenso(manager, opts, cb) {
  return runFlashDenso(manager, { size: 0, bytes: new Uint8Array() }, { ...opts, readProbe: true }, cb);
}

async function densoReadProbe({ manager, sendL, poll, onLog, ref, t0 }) {
  const h = (b) => b.toString(16).toUpperCase().padStart(2, '0');
  // Shindengen "lock bypass" style: after 7B unlock → READ_ID → flash-count → SEC CF → 72 08 80 read.
  await sendL('read-id', [0x72, 0x07, 0x72, 0x00, 0x00, 0x05], 1200);
  for (const ad of [0x00000, 0x10000]) await denso80Probe(manager, 'unlock+80-read', ad, ref, onLog);
  await sendL('flash-count', [0x7b, 0x06, 0x03, 0x01, 0x03], 1200);
  for (let s = 0; s < 2; s++) { const r = await manager.sendFramed(DENSO_SEC_CF[s], { timeout: 900, pre: s ? 10 : KLINE.P3 }); onLog('UART', `SEC${s + 1} (CF) → RX ${(r.reply || []).map(hex).join(' ') || '(none)'}`); }
  for (const ad of [0x00000, 0x10000]) await denso80Probe(manager, 'unlock+sec+80-read', ad, ref, onLog);
  // Probe #3 (user accepted risk): send the proven RANGE command first (state 0x10 → 0x20). Erase (04) is NEVER sent.
  await poll('probe start', 0x10);
  await sendL('range', [0x7c, 0x0b, 0x03, 0x0b, 0x13, 0x37, 0x5a, 0xec, 0xc8, 0xa5], 4000);
  const st0 = await poll('after range', 0x20);
  const deob = (x) => (((x ^ 0x4a) - 0x5a) & 0xff);
  const findIn = (data) => {
    if (!ref || ref.length < 0x10000 || data.length < 6) return null;
    const scan = (d) => { for (let o = 0; o + d.length <= ref.length; o++) { let k = 0; while (k < d.length && ref[o + k] === d[k]) k++; if (k === d.length) return o; } return -1; };
    const raw = scan(data); if (raw >= 0) return `RAW @0x${raw.toString(16).toUpperCase()}`;
    const ob = scan(data.map(deob)); if (ob >= 0) return `OBFUSCATED @0x${ob.toString(16).toUpperCase()}`;
    return 'no match in reference BIN';
  };
  // Probe #1/#2 (state 0x10): only no-payload 0D→12 / 0F→14 answer; any payload → silent.
  // Probe #3: same commands in state 0x20 (after RANGE).
  const cmds = [0x0d, 0x0f, 0x02, 0x03, 0x07, 0x0e, 0x10, 0x11, 0x12, 0x13];
  const forms = [[], [0x00, 0x00], [0x00, 0x01, 0x00, 0x00], [0x00, 0x01, 0x00, 0x00, 0x00, 0x80]];
  const hits = [];
  for (const form of forms) {
    for (const c of cmds) {
      const a = [0x7c, 5 + form.length, 0x03, c, ...form];
      const r = await sendL(`probe ${h(c)}${form.length ? ' +' + form.map(h).join('') : ''}`, a, 1500);
      const rep = r.reply || [];
      const known = { 0x0d: 0x12, 0x0f: 0x14 };
      if (rep.length >= 4 && rep.length <= 8 && rep[0] === 0x0c && rep[2] === c && rep[3] !== known[c]) onLog('SUCCESS', `PROBE: cmd ${h(c)} status 0x${h(rep[3])} (payload ${form.length} B)`);
      if (rep.length > 8) {
        const data = rep.slice(4, rep.length - 1);
        const m = findIn(data);
        onLog('SUCCESS', `PROBE HIT cmd ${h(c)}: ${rep.length} B reply${m ? ` — ${m}` : ''}`);
        hits.push({ cmd: c, form, len: rep.length, match: m });
      }
      const st = await poll(`after ${h(c)}`, null);
      if (st0 != null && st != null && st !== st0) { onLog('WARN', `Bootloader state changed 0x${h(st0)} → 0x${h(st)} — probe stopped for safety.`); return { result: 'success', duration_ms: Date.now() - t0, hits }; }
      if (hits.length) break;
    }
    if (hits.length) break;
  }
  onLog(hits.length ? 'SUCCESS' : 'WARN', hits.length ? `PROBE done: ${hits.length} candidate read command(s). Kirim log ini.` : 'PROBE done: tidak ada perintah baca yang menjawab data. Nothing erased/written.');
  onLog('INFO', 'Matikan IGNITION 15 detik sebelum memakai ECU lagi.');
  return { result: 'success', duration_ms: Date.now() - t0, hits };
}

// ---- Denso Titan READ via APPLICATION mode ("Sahara style"): no 7B bootloader, no erase/write ----
const densoFindInRef = (ref, data) => {
  if (!ref || data.length < 6 || data.every((b) => b === data[0])) return -1;
  for (let o = 0; o + data.length <= ref.length; o++) { let k = 0; while (k < data.length && ref[o + k] === data[k]) k++; if (k === data.length) return o; }
  return -1;
};
const densoAppSend = (manager, a, to = 900) => { const b = a.slice(); b.push(hondaChecksum(b)); return manager.sendFramed(b, { timeout: to, pre: KLINE.P3 }); };
const DENSO_SEC_CF = [
  [0x91, 0x91, 0x0D, 0xCF, 0x9E, 0x8D, 0x9A, 0x86, 0x90, 0x8A, 0x8C, 0x9B, 0x98],
  [0x91, 0x91, 0x0D, 0xCF, 0x92, 0x9E, 0x86, 0x96, 0x8B, 0x8D, 0x86, 0xC0, 0x7A],
];
const denso80Req = (ad) => { const a = [0x72, 0x08, 0x80, (ad >>> 24) & 0xff, (ad >>> 16) & 0xff, (ad >>> 8) & 0xff, ad & 0xff]; a.push(hondaChecksum(a)); return a; };
const densoParse80 = (rp) => { for (let i = 0; i + 7 < rp.length; i++) if (rp[i] === 0x02 && rp[i + 1] === 0x08 && rp[i + 2] === 0x80) return rp.slice(i + 3, i + 7); return null; };
// Shindengen-style read: 72 08 80 <addr32BE> → 02 08 80 <4 data>. Reads 4 words (16 B) for a reliable match.
async function denso80Probe(manager, label, ad, ref, onLog) {
  const data = [];
  for (let k = 0; k < 4; k++) {
    const a = denso80Req(ad + k * 4);
    const r = await manager.sendFramed(a, { timeout: 900, pre: KLINE.P3 });
    const rp = r.reply || [];
    if (k === 0) onLog('UART', `${label} 0x${ad.toString(16).toUpperCase()}: TX ${a.map(hex).join(' ')} → RX ${rp.length ? rp.slice(0, 24).map(hex).join(' ') : '(none)'}`);
    const d = densoParse80(rp);
    if (!d) return null;
    data.push(...d);
  }
  const o = densoFindInRef(ref, data);
  onLog(o >= 0 ? 'SUCCESS' : 'INFO', `  ${label} data ${data.map(hex).join(' ')} → ${ref ? (o >= 0 ? `COCOK BIN referensi @0x${o.toString(16).toUpperCase()}` : 'tidak cocok dengan BIN referensi') : 'tanpa BIN referensi'}`);
  return o >= 0 ? { label, phys: ad, refOffset: o, delta: o - ad } : { label, phys: ad, refOffset: -1 };
}
const densoParse82 = (rp) => {
  for (let i = 0; i + 4 < rp.length; i++) if (rp[i] === 0x92 && rp[i + 1] === 0x92) return rp.slice(i + 4, i + 4 + Math.max(0, rp[i + 3] - 5));
  return null;
};
async function densoAppWake(manager, onLog) {
  await manager.sendFramed([0xfe, 0x04, 0x72, 0x8c], { timeout: 1200, pre: KLINE.P2 }).catch(() => {});
  await sleep(60);
  const i = await densoAppSend(manager, [0x72, 0x05, 0x00, 0xf0], 1000);
  const id = await densoAppSend(manager, [0x72, 0x07, 0x72, 0x00, 0x00, 0x05], 1200);
  onLog('UART', `init → RX ${(i.reply || []).map(hex).join(' ') || '(none)'} | read-id → RX ${(id.reply || []).map(hex).join(' ') || '(none)'}`);
}

export async function probeReadDensoApp(manager, opts, cb) {
  const { onLog } = cb;
  const ref = opts.refBytes || null;
  manager.abort = false;
  const t0 = Date.now();
  const hits = [];
  onLog('INFO', 'Mode aplikasi: TANPA bootloader 7B, TANPA erase/write.');
  await densoAppWake(manager, onLog);
  const report = (label, a, rp, data, phys) => {
    onLog('UART', `${label}: TX ${a.map(hex).join(' ')} → RX ${rp.length ? rp.slice(0, 40).map(hex).join(' ') : '(none)'}`);
    if (!data || !data.length) return;
    const o = densoFindInRef(ref, data);
    onLog(o >= 0 ? 'SUCCESS' : 'INFO', `  data ${data.length} B → ${ref ? (o >= 0 ? `COCOK BIN referensi @0x${o.toString(16).toUpperCase()}` : 'tidak cocok dengan BIN referensi') : 'tanpa BIN referensi'}`);
    if (o >= 0) hits.push({ label, phys, refOffset: o, delta: o - phys });
  };
  // A) Honda read-memory 72 07 72 <hi> <lo> <n>
  for (const ad of [0x0000, 0x0010, 0x0100, 0x1000, 0x4000, 0x8000, 0xc000, 0xff00]) {
    if (manager.abort) break;
    const a = [0x72, 0x07, 0x72, (ad >> 8) & 0xff, ad & 0xff, 0x10];
    const r = await densoAppSend(manager, a);
    const rp = r.reply || [];
    const data = rp.length > 6 && rp[0] === 0x02 && rp[2] === 0x72 ? rp.slice(5, rp[1] - 1) : null;
    report(`72-read 0x${ad.toString(16).toUpperCase().padStart(4, '0')}`, a, rp, data, ad);
  }
  // C) Shindengen-style 72 08 80 <addr32>: plain, then after flash-count query + Security CF
  const ADDRS80 = [0x00000, 0x10000, 0x40000];
  for (const ad of ADDRS80) { if (manager.abort) break; const h = await denso80Probe(manager, '80-read', ad, ref, onLog); if (h && h.refOffset >= 0) hits.push(h); }
  if (!manager.abort) {
    await densoAppSend(manager, [0x72, 0x07, 0x72, 0x00, 0x00, 0x05]);
    for (const fc of [[0x7b, 0x06, 0x03, 0x01, 0x03], [0x7b, 0x06, 0x02, 0x01, 0x03]]) {
      const r = await densoAppSend(manager, fc);
      onLog('UART', `flash-count ${fc.map(hex).join(' ')} → RX ${(r.reply || []).map(hex).join(' ') || '(none)'}`);
    }
    for (let s = 0; s < 2; s++) {
      const r = await manager.sendFramed(DENSO_SEC_CF[s], { timeout: 900, pre: s ? 10 : KLINE.P3 });
      onLog('UART', `SEC${s + 1} (CF) → RX ${(r.reply || []).map(hex).join(' ') || '(none)'}`);
    }
    for (const ad of ADDRS80) { if (manager.abort) break; const h = await denso80Probe(manager, '80-read+sec', ad, ref, onLog); if (h && h.refOffset >= 0) hits.push(h); }
  }
  // B) Keihin-style 82 82 00 09 <page> <lo> <hi> <n> — without, then with read-mode arm
  for (const arm of [false, true]) {
    if (manager.abort) break;
    if (arm) {
      const k = await manager.sendFramed([0x7e, 0x06, 0x01, 0x01, 0x00, 0x7a], { timeout: 600, pre: KLINE.P3 });
      const m = await manager.sendFramed([0x82, 0x82, 0x10, 0x06, 0x00, 0xe6], { timeout: 900, pre: KLINE.P3 });
      onLog('UART', `arm 7E 06 01 01 00 7A → RX ${(k.reply || []).map(hex).join(' ') || '(none)'} | read-mode 82 82 10 06 00 E6 → RX ${(m.reply || []).map(hex).join(' ') || '(none)'}`);
    }
    for (const page of [0x00, 0x01, 0x02, 0x03, 0x04, 0x05]) {
      for (const ad of [0x0000, 0x8000]) {
        if (manager.abort) break;
        const a = [0x82, 0x82, 0x00, 0x09, page, ad & 0xff, (ad >> 8) & 0xff, 0x0c];
        const r = await densoAppSend(manager, a);
        const rp = r.reply || [];
        report(`82-read${arm ? '+arm' : ''} p${hex(page)}:${ad.toString(16).toUpperCase().padStart(4, '0')}`, a, rp, densoParse82(rp), (page << 16) | ad);
      }
    }
  }
  const best = hits.find((h) => h.label.startsWith('82-read') || h.label.startsWith('80-read'));
  if (best) onLog('SUCCESS', `PROBE: perintah ${best.label} cocok (delta 0x${best.delta.toString(16).toUpperCase()}). Tombol Read 384KB aktif.`);
  else if (hits.length) onLog('SUCCESS', `PROBE: ${hits.length} blok 72-read cocok (alamat 16-bit saja, belum cukup untuk 384KB). Kirim log ini.`);
  else onLog('WARN', 'PROBE selesai: tidak ada data yang cocok dengan BIN referensi. Nothing erased/written. Kirim log ini.');
  const method = !best ? null : best.label.startsWith('80') ? { type: '80', delta: best.delta, sec: best.label.includes('+sec') } : { type: '82', delta: best.delta, arm: best.label.includes('+arm') };
  return { result: 'success', duration_ms: Date.now() - t0, hits, method };
}

async function readDenso80(manager, method, cb, t0) {
  const { onLog, onProgress, onPhase, onSpeed } = cb;
  const SIZE = 393216;
  if (method.sec) {
    await densoAppSend(manager, [0x72, 0x07, 0x72, 0x00, 0x00, 0x05]);
    await densoAppSend(manager, [0x7b, 0x06, 0x03, 0x01, 0x03]).catch(() => {});
    await densoAppSend(manager, [0x7b, 0x06, 0x02, 0x01, 0x03]).catch(() => {});
    await manager.sendFramed(DENSO_SEC_CF[0], { timeout: 900, pre: KLINE.P3 });
    await manager.sendFramed(DENSO_SEC_CF[1], { timeout: 900, pre: 10 });
  }
  const out = new Uint8Array(SIZE).fill(0xff);
  onPhase && onPhase('reading');
  let off = 0, fails = 0, lastTick = Date.now(), lastOff = 0;
  while (off < SIZE) {
    if (manager.abort) { onLog('ERROR', 'Read aborted.'); return { result: 'aborted', bytes: null, duration_ms: Date.now() - t0 }; }
    let d = null;
    for (let t = 0; t < 3 && !d; t++) { const r = await manager.sendFramed(denso80Req(off - method.delta), { timeout: 900, pre: KLINE.BLOCK_GAP }); d = densoParse80(r.reply || []); }
    if (!d) { fails++; onLog('ERROR', `Word 0x${off.toString(16).toUpperCase()} gagal dibaca (3x).`); if (fails >= 5) return { result: 'failed', bytes: null, duration_ms: Date.now() - t0 }; off += 4; continue; }
    out.set(d, off);
    off += 4;
    onProgress && onProgress('reading', Math.round((off / SIZE) * 100));
    const now = Date.now();
    if (now - lastTick > 1000) { const kbps = ((off - lastOff) / 1024) / ((now - lastTick) / 1000); onSpeed && onSpeed(kbps); onLog('READ', `0x${off.toString(16).toUpperCase().padStart(5, '0')} (${Math.round((off / SIZE) * 100)}%) ${kbps.toFixed(2)} KB/s`); lastTick = now; lastOff = off; }
  }
  onPhase && onPhase('done');
  const dur = Date.now() - t0;
  onLog(fails ? 'WARN' : 'SUCCESS', `Read selesai: ${SIZE} B dalam ${(dur / 1000).toFixed(1)}s${fails ? ` — ${fails} word gagal (JANGAN dipakai untuk flash)` : ''}`);
  return { result: fails ? 'partial' : 'success', bytes: out, duration_ms: dur };
}

export async function readDensoApp(manager, opts, cb) {
  const { onLog, onProgress, onPhase, onSpeed } = cb;
  const { method } = opts;
  const SIZE = 393216, BLK = 0x0c;
  manager.abort = false;
  const t0 = Date.now();
  if (!method || (method.type !== '82' && method.type !== '80')) { onLog('ERROR', 'Jalankan Probe Read Mode Aplikasi dulu (perlu hasil cocok).'); return { result: 'failed', bytes: null, duration_ms: 0 }; }
  onPhase && onPhase('handshake');
  await densoAppWake(manager, onLog);
  if (method.type === '80') return readDenso80(manager, method, cb, t0);
  if (method.arm) {
    await manager.sendFramed([0x7e, 0x06, 0x01, 0x01, 0x00, 0x7a], { timeout: 600, pre: KLINE.P3 }).catch(() => {});
    await manager.sendFramed([0x82, 0x82, 0x10, 0x06, 0x00, 0xe6], { timeout: 900, pre: KLINE.P3 }).catch(() => {});
  }
  const out = new Uint8Array(SIZE).fill(0xff);
  onPhase && onPhase('reading');
  let off = 0, fails = 0, lastTick = Date.now(), lastOff = 0;
  while (off < SIZE) {
    if (manager.abort) { onLog('ERROR', 'Read aborted.'); return { result: 'aborted', bytes: null, duration_ms: Date.now() - t0 }; }
    const n = Math.min(BLK, SIZE - off);
    const phys = off - method.delta;
    const a = [0x82, 0x82, 0x00, 0x09, (phys >> 16) & 0xff, phys & 0xff, (phys >> 8) & 0xff, n];
    let data = null;
    for (let t = 0; t < 3 && !data; t++) { const r = await densoAppSend(manager, a, 1000); const d = densoParse82(r.reply || []); if (d && d.length === n) data = d; }
    if (!data) { fails++; onLog('ERROR', `Blok 0x${off.toString(16).toUpperCase()} gagal dibaca (3x).`); if (fails >= 5) return { result: 'failed', bytes: null, duration_ms: Date.now() - t0 }; off += n; continue; }
    out.set(data, off);
    off += n;
    onProgress && onProgress('reading', Math.round((off / SIZE) * 100));
    const now = Date.now();
    if (now - lastTick > 1000) { const kbps = ((off - lastOff) / 1024) / ((now - lastTick) / 1000); onSpeed && onSpeed(kbps); onLog('READ', `0x${off.toString(16).toUpperCase().padStart(5, '0')} (${Math.round((off / SIZE) * 100)}%) ${kbps.toFixed(2)} KB/s`); lastTick = now; lastOff = off; }
  }
  onPhase && onPhase('done');
  const dur = Date.now() - t0;
  onLog(fails ? 'WARN' : 'SUCCESS', `Read selesai: ${SIZE} B dalam ${(dur / 1000).toFixed(1)}s${fails ? ` — ${fails} blok gagal (JANGAN dipakai untuk flash)` : ''}`);
  return { result: fails ? 'partial' : 'success', bytes: out, duration_ms: dur };
}


async function runFlashDenso(manager, firmware, opts, cb) {
  const { fastWrite = false, unlockOnly = false, readProbe = false } = opts;
  const { onLog, onProgress, onPhase, onSpeed } = cb;
  manager.abort = false;
  const t0 = Date.now();
  const total = firmware.size;
  const is128 = total === 131072;
  // Denso 96KB (CRF250F 38770-K99-B02, ECM 0103160801) — decoded byte-for-byte from the XTune 1.0.1 capture
  // 2026-10-19 (639/639 ACK, state 10→20→30→40→50→0F). Same LegacyDenso state machine as 128KB; differences:
  // window file 0x0–0x13F80 on wire addr16 0x0800+k*8, RAW data (no +5A^4A), range 00 00 00 FF FF FF,
  // Σ16 BE [0,0x13C00) = AA55 @0x13BFE, and XTune's preparation block @0x3C80 (written in the capture).
  const is96 = total === 98304;
  if (!unlockOnly && !readProbe && total !== 393216 && !is128 && !is96) { onLog('ERROR', `File must be 96/128KB or 384KB (got ${total} B).`); return { result: 'failed', duration_ms: 0 }; }
  const writeBytes = new Uint8Array(firmware.bytes);
  if (!unlockOnly && is96) {
    const PREP_AT = 0x3c80;
    const STOCK_3C80 = '4c31800c4de4230024bdd366814da97aab8027caec97da0aa16d89fa0b32f69489c1bfa21804c1c51360b90361f2b6feb5e0b1f6b2052393ce5fb4e3805549964e14d9c7a8780b3a1ee81924526ea5c1c24521c5aa5d804dcf6957e9d68de7eb11f30cc05a2aa8cd58f87ab1bcf07d9a87e9b5ff502d4e3964b0e92bcb5cea5f';
    const PREP_3C80 = '4a8000fbb63cb4406a000afc769efd76a0f33d34137c3e0cfc3d34fe76a227021810f33e0c7c3d360a1f3c9940161f321d01111e321d020c1d321d011d3c99401c3c99801e3c9980041820008f793ca81e3c1302f414101c0250081d0258011d024040dc44c301005c561e024108061d02400820041c024008875a485a495a4a';
    const cur = Array.from(writeBytes.slice(PREP_AT, PREP_AT + 128)).map(hex).join('').toLowerCase();
    if (cur === STOCK_3C80) {
      for (let j = 0; j < 128; j++) writeBytes[PREP_AT + j] = parseInt(PREP_3C80.substr(j * 2, 2), 16);
      onLog('INFO', 'K99 preparation: blok 0x3C80 diganti blok referensi (sesuai capture terbukti).');
    } else if (cur !== PREP_3C80) onLog('WARN', 'K99: blok 0x3C80 bukan stock maupun blok preparasi — ditulis apa adanya.');
    const sum16 = () => { let s = 0; for (let o = 0; o < 0x13c00; o += 2) s = (s + ((writeBytes[o] << 8) | writeBytes[o + 1])) & 0xffff; return s; };
    const h4 = (v) => v.toString(16).toUpperCase().padStart(4, '0');
    const s0 = sum16();
    if (s0 !== 0xaa55) {
      const oldW = (writeBytes[0x13bfe] << 8) | writeBytes[0x13bff];
      const w = (oldW + 0xaa55 - s0) & 0xffff;
      writeBytes[0x13bfe] = (w >> 8) & 0xff; writeBytes[0x13bff] = w & 0xff;
      onLog('INFO', `Checksum @0x13BFE: ${h4(oldW)} → ${h4(w)}${sum16() === 0xaa55 ? ' ✓' : ' — MISMATCH'}`);
    } else onLog('INFO', `Checksum Σ16 [0x0–0x13C00) = AA55 ✓`);
  }
  // Denso 128KB (XTune LegacyDenso): Σ BE16 over [0x0, 0x1BC00) == 0xAA55, correction word @0x1BBFE.
  if (!unlockOnly && is128) {
    const sum16 = () => { let s = 0; for (let o = 0; o < 0x1bc00; o += 2) s = (s + ((writeBytes[o] << 8) | writeBytes[o + 1])) & 0xffff; return s; };
    const h4 = (v) => v.toString(16).toUpperCase().padStart(4, '0');
    const s0 = sum16();
    if (s0 !== 0xaa55) {
      const oldW = (writeBytes[0x1bbfe] << 8) | writeBytes[0x1bbff];
      const w = (oldW + 0xaa55 - s0) & 0xffff;
      writeBytes[0x1bbfe] = (w >> 8) & 0xff; writeBytes[0x1bbff] = w & 0xff;
      onLog('INFO', `Checksum @0x1BBFE: ${h4(oldW)} → ${h4(w)}${sum16() === 0xaa55 ? ' ✓' : ' — MISMATCH'}`);
    } else onLog('INFO', `Checksum Σ16 [0x0–0x1BC00) = AA55 ✓`);
  }

  // ---- Embed Denso integrity checksum (decoded from the reference capture Prepare/Sum/ReadWord) ----
  // Rule: Σ ReadWord32BE(image, off) for off in [0x10000, 0x5FE00) step 4  ==  0xAA5555AA.
  // (Target confirmed by byte-diffing the reference capture's _CKS.bin vs original: only 0x5EFFC changes.)
  // The correction word lives at 0x5EFFC (389116); set it so the window sum hits the target.
  // Makes ANY bin pass the finalize integrity step (status 0x0D) — raw dumps and edited tunes alike.
  if (!unlockOnly && total === 393216) {
    const rdw = (o) => (((writeBytes[o] << 24) | (writeBytes[o + 1] << 16) | (writeBytes[o + 2] << 8) | writeBytes[o + 3]) >>> 0);
    const sumWin = () => { let s = 0; for (let o = 0x10000; o < 0x5fe00; o += 4) s += rdw(o); return s % 4294967296; };
    const CSADDR = 389116; // 0x5EFFC
    const oldW = rdw(CSADDR);
    const s = sumWin();
    const newW = (((oldW + 0xaa5555aa - s) % 4294967296) + 4294967296) % 4294967296;
    writeBytes[CSADDR] = (newW >>> 24) & 0xff; writeBytes[CSADDR + 1] = (newW >>> 16) & 0xff;
    writeBytes[CSADDR + 2] = (newW >>> 8) & 0xff; writeBytes[CSADDR + 3] = newW & 0xff;
    const v = sumWin();
    const h8 = (x) => (x >>> 0).toString(16).toUpperCase().padStart(8, '0');
    onLog('INFO', `Checksum @0x5EFFC: ${h8(oldW)} → ${h8(newW)}${v === 0xaa5555aa ? ' ✓' : ' — MISMATCH'}`);
  }
  const fr = (a) => { const b = a.slice(); b.push(hondaChecksum(b)); return b; };
  const send = async (a, to = 800) => manager.sendFramed(fr(a), { timeout: to, pre: KLINE.P2 });
  // Locate a checksum-valid reply frame (head, len, sub, …) even when stale bus bytes precede it.
  const pickFrame = (rep, head, sub) => {
    for (let i = 0; i + 3 < rep.length; i++) {
      if (rep[i] !== head || rep[i + 2] !== sub) continue;
      const L = rep[i + 1];
      if (L >= 4 && i + L <= rep.length) { const f = rep.slice(i, i + L); if ((f.reduce((x, y) => x + y, 0) & 0xff) === 0) return f; }
    }
    return null;
  };
  const sendL = async (label, a, to = 800) => {
    const r = await send(a, to);
    const rx = r.reply.length ? r.reply.map(hex).join(' ') : (r.raw && r.raw.length ? r.raw.map(hex).join(' ') : '(none)');
    onLog('UART', `${label}: TX ${a.map(hex).join(' ')} → RX ${rx}`);
    return r;
  };
  const poll = async (label, expect) => {
    const r = await send([0x7c, 0x06, 0x03, 0x01, 0x00]);
    const pf = (r.reply.length >= 4 && r.reply[0] === 0x0c && r.reply[2] === 0x01) ? r.reply : pickFrame(r.reply, 0x0c, 0x01);
    const st = pf ? pf[3] : null;
    const rx = r.reply.length ? r.reply.map(hex).join(' ') : '(none)';
    if (st === null) onLog('WARN', `state poll (${label}): no valid reply — RX ${rx}`);
    else if (expect != null && st !== expect) onLog('WARN', `state ${label}: 0x${hex(st)} (exp 0x${hex(expect)})`);
    else onLog('UART', `state poll (${label}): 0x${hex(st)}`);
    return st;
  };

  // ---- WAKE + ID (standard Honda 72/02) ----
  onPhase('handshake');
  onLog('INFO', 'Denso Titan: waking ECU (K-Line)…');
  // Recovery-capable wake: hammer the broadcast FE 04 FF FF break-wake (same as cold connect) so an
  // ERASED/recovery ECU re-enters the bootloader window, THEN the addressed FE 04 72 8C.
  if (manager.mode === 'bridge' && manager.bridge && manager.bridge.klineInit) {
    for (let w = 0; w < 3; w++) {
      try { const wk = await manager.bridge.klineInit({ low: 70, high: 140, bytes: [0xfe, 0x04, 0xff, 0xff], timeout: 400 }); if (wk && wk.length) break; } catch (_) {}
      await sleep(100);
    }
  }
  await manager.sendFramed([0xfe, 0x04, 0x72, 0x8c], { timeout: 1200, pre: KLINE.P2 });
  await sleep(60);
  await sendL('init', [0x72, 0x05, 0x00, 0xf0], 1000);         // -> 02 04 00 FA
  const idR = await sendL('read-id', [0x72, 0x07, 0x72, 0x00, 0x00, 0x05], 1200);
  const idRaw = extractEcmId(idR.raw && idR.raw.length ? idR.raw : idR.reply);
  const idConn = (opts.ecmId || '').toUpperCase().replace(/[^0-9A-F]/g, '');
  const idB = /^01[0-9A-F]{8}$/i.test(idRaw || '') ? idRaw : (idConn || null);
  if (idB) onLog('INFO', `ECM ID: ${idB}${idB !== idRaw ? ' (from connect; read-id reply incomplete)' : ''}`);

  // ---- SECURITY ----
  // Capture timing: after sec-1 the ECU needs ~12s to PREPARE THE SEED before it answers sec-2
  // (7B 05 03 04 -> 0B 07 04 00 <seed>). Asking too early returns 0B 05 04 F0 ("seed not ready").
  // Single-shot wait (like Shindengen) — NEVER auto-retry / resend the preamble (can trigger FF lockout).
  onLog('SECURITY', 'Sending Denso unlock preamble…');
  const sg = async (label, a) => {
    const r = await sendL(label, a, 2000);
    if (r.reply.length >= 4 && r.reply[0] === 0x0b) return r.reply;
    const f = pickFrame(r.reply, 0x0b, a[3]);
    if (f) onLog('UART', `${label}: stale bytes skipped → ${f.map(hex).join(' ')}`);
    return f;
  };
  const r0 = await sg('sec-0', [0x7b, 0x08, 0x03, 0x02, 0x58, 0x85, 0x79, 0x22]);
  if (!(r0 && r0[3] === 0x00)) { onLog('ERROR', 'Unlock step 0 rejected — aborted.'); return { result: 'failed', duration_ms: Date.now() - t0 }; }
  const r1 = await sg('sec-1', [0x7b, 0x08, 0x03, 0x03, 0x85, 0x75, 0x13, 0x6a]);
  if (!(r1 && r1[3] === 0x00)) { onLog('ERROR', 'Unlock step 1 rejected — aborted.'); return { result: 'failed', duration_ms: Date.now() - t0 }; }
  // Single-shot seed preparation wait (~12.5s). Keep the bus quiet — do NOT poll.
  for (let s = 13000; s > 0; s -= 1000) {
    if (manager.abort) return _abort(onLog);
    onLog('SECURITY', `Preparing seed… ${Math.ceil(s / 1000)}s (do not disconnect / cut power)`);
    await sleep(Math.min(1000, s));
  }
  const rSeed = await sg('sec-2 (seed)', [0x7b, 0x05, 0x03, 0x04]);
  if (!(rSeed && rSeed[3] === 0x00 && rSeed.length >= 6)) {
    const st = rSeed && rSeed.length >= 4 ? hex(rSeed[3]) : '??';
    onLog('ERROR', `Seed status 0x${st} — power-cycle 15s, retry once. Aborted.`);
    return { result: 'failed', duration_ms: Date.now() - t0 };
  }
  const seedHi = rSeed[4], seedLo = rSeed[5];
  const seed16 = ((seedHi << 8) | seedLo) & 0xffff;
  const seed = `${hex(seedHi)}${hex(seedLo)}`.toUpperCase();
  onLog('SECURITY', `Seed = ${seed}`);
  // Denso seed→key (derived from the reference capture Honda Denso, verified): key16 = ((seed+kc0)*kc1 + (kc2? seed%kc2 : seed)) & 0xFFFF.
  // Per-ECU keyConstants keyed by ECM ID. Verified vs 4 Titan pairs (6EC1→48D4, 8CCF→C666, 6107→A0AE, A2F6→440F).
  // Key frame = 7B 07 03 05 <keyHi> <keyLo> + Honda checksum (send() appends it; e.g. 6EC1 → 48 D4 5A).
  const ecmKey = (idB || '').toUpperCase().replace(/[^0-9A-F]/g, '');
  const inRecovery = !!opts.recovery || /0000$/.test(ecmKey);
  const parseHex16 = (v) => { const c = String(v == null ? '' : v).replace(/[^0-9a-fA-F]/g, ''); return c.length ? (parseInt(c.slice(-4), 16) & 0xffff) : null; };
  const h16 = (v) => (v & 0xffff).toString(16).toUpperCase().padStart(4, '0');
  let key16 = null;
  let kcAlts = [];
  // RECOVERY override (user backdoor) — SAME computeKey formula, but recovery constants/key. The
  // bootloader enforces a DIFFERENT key in recovery than normal write (DENSO_KC write constants are
  // rejected → 0xF1). The user supplies recovery C0 C1 C2 (preferred, seed-independent) or a fixed key.
  if (inRecovery && Array.isArray(opts.recoveryConsts) && opts.recoveryConsts.length === 3 && opts.recoveryConsts.every((x) => x != null && !Number.isNaN(x))) {
    const [c0, c1, c2] = opts.recoveryConsts.map((x) => x & 0xffff);
    key16 = computeKey(seed16, c0, c1, c2) & 0xffff;
    onLog('SECURITY', `Recovery key (manual C0=${h16(c0)} C1=${h16(c1)} C2=${h16(c2)}).`);
  } else if (inRecovery && parseHex16(opts.recoveryKey) != null) {
    key16 = parseHex16(opts.recoveryKey);
    onLog('SECURITY', `Recovery key (manual fixed) = ${h16(key16)}.`);
  }
  // Auto: in recovery, derive the key from the loaded BIN's bootloader security descriptor
  // (ID tail 0000 + 6 constant bytes before it) — same computeKey formula, recovery constants.
  // Denso 128KB key constants (XTune LegacyDenso.Profile, Add/Multiply/Divisor). Same formula as 384KB.
  // BIN fallback: the 6 bytes after preamble 58 85 79 00 85 75 13 00, read BIG-endian (matches all 3 profiles).
  if (key16 == null && (is128 || is96)) {
    const KC128 = { '0101360801': [7038, 28537, 45220], '0100E20801': [52441, 8799, 62371], '0100CD0801': [63176, 61870, 12982], '0103160801': [0xad62, 0xec08, 0xe877] };
    const idHex = (ecmKey && !/0000$/.test(ecmKey) ? ecmKey : (opts.ecmId || '')).toUpperCase().replace(/[^0-9A-F]/g, '');
    // Denso embeds TWO security descriptors (same preamble 58 85 79 00 85 75 13 00): the APP/write key
    // AND a SEPARATE bootloader/RECOVERY key. In recovery the bootloader rejects the app key (0xF1),
    // so pick the descriptor whose constants differ from the app key (same as the Titan recovery idea).
    const pre = [0x58, 0x85, 0x79, 0x00, 0x85, 0x75, 0x13, 0x00];
    const descs = [];
    for (let o = 0; o + 14 <= writeBytes.length; o++) {
      let ok = true; for (let k = 0; k < 8; k++) if (writeBytes[o + k] !== pre[k]) { ok = false; break; }
      if (!ok) continue;
      const p = o + 8;
      descs.push({
        be: [(writeBytes[p] << 8) | writeBytes[p + 1], (writeBytes[p + 2] << 8) | writeBytes[p + 3], (writeBytes[p + 4] << 8) | writeBytes[p + 5]],
        le: [writeBytes[p] | (writeBytes[p + 1] << 8), writeBytes[p + 2] | (writeBytes[p + 3] << 8), writeBytes[p + 4] | (writeBytes[p + 5] << 8)],
        at: p,
      });
    }
    const appC = KC128[idHex] || (descs[0] ? descs[0].be : null);
    let c = null;
    if (inRecovery && descs.length >= 2 && appC) {
      const rec = descs.find((d) => d.be[0] !== appC[0] || d.be[1] !== appC[1] || d.be[2] !== appC[2]);
      if (rec) {
        c = rec.be;
        kcAlts = [rec.le, ...descs.filter((d) => d !== rec).flatMap((d) => [d.be, d.le])];
        onLog('SECURITY', `Recovery: pakai descriptor bootloader @0x${rec.at.toString(16).toUpperCase()} (app key ditolak di recovery).`);
      }
    }
    if (!c) c = appC;
    if (!c) { onLog('ERROR', `Denso ${is96 ? '96' : '128'}KB: key constants for ${idHex || 'ID'} not found — aborted. Nothing changed.`); return { result: 'failed', duration_ms: Date.now() - t0 }; }
    key16 = computeKey(seed16, c[0], c[1], c[2]) & 0xffff;
    onLog('SECURITY', `Denso ${is96 ? '96' : '128'}KB key constants: ${c.map(h16).join(' ')}`);
  }
  if (key16 == null && inRecovery) {
    const idPfx = (ecmKey || (opts.ecmId || '').toUpperCase().replace(/[^0-9A-F]/g, '')).slice(0, 6);
    const rc = idPfx.length === 6 ? findDensoRecoveryConsts(writeBytes, idPfx) : null;
    if (rc) {
      key16 = computeKey(seed16, rc.C0, rc.C1, rc.C2) & 0xffff;
      onLog('SECURITY', `Recovery key from BIN → ${h16(key16)}.`);
    }
  }
  if (key16 == null) {
    const DENSO_KC = {
      '0104190802': [16648, 32686, 22411], // Titan / CG 160 K2G-B12
      '0104180801': [11058, 33903, 65273], // Start 160 K2G-B01
      '0104D10803': [29618, 64889, 25448], // CB300F K2K-B22
      '0104E60801': [18883, 1787, 9301],   // Sahara 300 K3H-B03
      '0105480802': [35661, 9413, 19343],  // CG160 922 K2G-922
      '01053B0802': [26263, 43992, 52641], // Pop 110i BC3
      // 01054D0803 (BROS 160 K1S-903): konstanta = 6 byte sebelum ID (12 CB 31 F4 34 E3) dibaca
      // LITTLE-ENDIAN (metode XTune, terbukti 6/6 profil). seed E009->5C34, 4AC8->4182, 9705->496C.
      '01054D0803': [51986, 62513, 58164], // = 0xCB12, 0xF431, 0xE334 (LE)
    };
    let kc = DENSO_KC[ecmKey];
    if (!kc && ecmKey.length >= 6) {
      // Recovery/erased ECU reports e.g. 0104190000 — share the 6-digit maker prefix with the full ID.
      const pfx = ecmKey.slice(0, 6);
      const hit = Object.keys(DENSO_KC).find((k) => k.slice(0, 6) === pfx);
      if (hit) { kc = DENSO_KC[hit]; onLog('INFO', `ECM ID ${ecmKey} matched profile ${hit} by prefix (recovery mode).`); }
    }
    if (!kc) {
      const fb = (opts.ecmId || '').toUpperCase().replace(/[^0-9A-F]/g, '');
      if (DENSO_KC[fb]) { kc = DENSO_KC[fb]; onLog('INFO', `Using connected-device ECM ID ${fb} for key constants.`); }
    }
    if (!kc) {
      // Konstanta kunci tersimpan di dalam BIN tepat sebelum ECM ID. Coba semua pola (ID penuh/8/6 digit,
      // urutan normal/terbalik/ASCII, little & big endian). Kunci salah hanya ditolak security.
      const idFull = (ecmKey || (opts.ecmId || '')).toUpperCase().replace(/[^0-9A-F]/g, '');
      const cands = findDensoKeyCandidates(writeBytes, idFull);
      if (cands.length) {
        kc = [cands[0].C0, cands[0].C1, cands[0].C2];
        kcAlts = cands.slice(1).map((x) => [x.C0, x.C1, x.C2]);
        onLog('SECURITY', `Kandidat key constants dari BIN: ${cands.length} buah — pakai @0x${cands[0].at.toString(16).toUpperCase()} (${cands[0].pat}): ${kc.map(h16).join(' ')}`);
        cands.slice(1, 4).forEach((x, i) => onLog('INFO', `  cadangan #${i + 1} @0x${x.at.toString(16).toUpperCase()} (${x.pat}): ${[x.C0, x.C1, x.C2].map(h16).join(' ')}`));
      }
    }
    if (!kc) {
      onLog('ERROR', `No key constants for ${ecmKey || 'unknown ID'} — aborted.`);
      return { result: 'failed', duration_ms: Date.now() - t0 };
    }
    key16 = (((seed16 + kc[0]) * kc[1] + (kc[2] ? (seed16 % kc[2]) : seed16)) & 0xffff) >>> 0;
    if (inRecovery) onLog('WARN', 'Recovery: using WRITE key — if rejected, enter Recovery Key C0 C1 C2.');
  }
  const keyHi = (key16 >> 8) & 0xff, keyLo = key16 & 0xff;
  onLog('SECURITY', `Key = ${hex(keyHi)}${hex(keyLo)} (seed ${seed})`);
  const trySendKey = async (k16, label) => {
    const kHi = (k16 >> 8) & 0xff, kLo = k16 & 0xff;
    const raw = await sendL(`sec-3 (key${label ? ' ' + label : ''})`, [0x7b, 0x07, 0x03, 0x05, kHi, kLo], 2000);
    let rk = (raw.reply.length >= 4 && raw.reply[0] === 0x0b) ? raw.reply : pickFrame(raw.reply, 0x0b, 0x05);
    if (!rk) {
      onLog('WARN', 'Key ACK incomplete — verifying unlock via state poll…');
      const stK = await poll('verify key', 0x10);
      if (stK === 0x10) rk = [0x0b, 0x05, 0x05, 0x00];
    }
    return rk;
  };
  let rKey = await trySendKey(key16, '');
  // CATATAN: Denso Titan mengunci (0xFF) setelah SATU key salah per power-cycle. JANGAN auto-retry
  // kandidat lain di sesi yang sama — itu hanya mengunci ECU. Kirim 1 key saja lalu stop.
  if (!(rKey && rKey[3] === 0x00)) {
    const st = rKey && rKey.length >= 4 ? hex(rKey[3]) : '??';
    if (Array.isArray(kcAlts) && kcAlts.length) {
      const list = kcAlts.map((a) => h16(computeKey(seed16, a[0], a[1], a[2]) & 0xffff));
      onLog('INFO', `Kandidat key lain (TIDAK ditembak otomatis — ECU lock setelah 1 salah): ${list.join(' / ')}`);
    }
    onLog('ERROR', `Key rejected (0x${st}) — IGNITION OFF 20s lalu ON, coba sekali lagi. Tidak ada yang ditulis.`);
    return { result: 'failed', duration_ms: Date.now() - t0 };
  }
  onLog('SUCCESS', 'Denso unlocked.');
  if (readProbe) return densoReadProbe({ manager, sendL, poll, onLog, ref: opts.refBytes, t0 });
  if (unlockOnly) {
    onLog('SUCCESS', `✅ TEST UNLOCK OK — seed ${seed} → key ${hex(keyHi)}${hex(keyLo)}. Nothing written.`);
    return { result: 'success', duration_ms: Date.now() - t0, unlockOnly: true, seed, key: `${hex(keyHi)}${hex(keyLo)}` };
  }

  // ---- ERASE ----
  await poll('after sec', 0x10);
  await sendL('range', is96 ? [0x7c, 0x0b, 0x03, 0x0b, 0x00, 0x00, 0x00, 0xff, 0xff, 0xff] : [0x7c, 0x0b, 0x03, 0x0b, 0x13, 0x37, 0x5a, 0xec, 0xc8, 0xa5], 4000);
  await poll('after range', 0x20);
  onLog('ERASE', 'Erasing flash (this takes a few seconds — do not cut power)…');
  await sendL('erase-arm', [0x7c, 0x06, 0x03, 0x04, 0xff], 4000);
  let erased = false;
  for (let i = 0; i < 60; i++) {
    if (manager.abort) return _abort(onLog);
    const r = await send([0x7c, 0x05, 0x03, 0x05], 4000);   // -> 0C 05 05 01 (busy) … 0C 05 05 00 (done)
    const stB = (r.reply.length >= 4 && r.reply[0] === 0x0c && r.reply[2] === 0x05) ? r.reply[3] : null;
    if (stB === 0x00) { erased = true; break; }
    if (stB === null) onLog('WARN', `erase poll: no valid reply — RX ${r.reply.map(hex).join(' ') || '(none)'}`);
    await sleep(250);
  }
  if (!erased) { onLog('ERROR', 'Erase not confirmed — aborted.'); return { result: 'failed', duration_ms: Date.now() - t0 }; }
  onLog('SUCCESS', 'Flash erase confirmed.');

  // ---- WRITE: Denso block program 7C 8B 03 06 (decoded from the reference capture DataFrame/Command) ----
  // Window = [0x10000, 0x5FE00): the first 64KB (bootloader) and the top 0x200 are NOT programmed.
  //   a = (addr - 0x10000) / 16                               (on-wire block address, +8 per 128B block)
  //   data[j] = ((image[addr+j] + 0x5A) & 0xFF) ^ 0x4A        (Denso payload obfuscation)
  //   n = (addr+128 == 0x5FE00) ? 0 : a+8                     (next block addr; 0 marks terminator/last)
  //   c = (-(Σ payload[0..131])) & 0xFFFF                     (inner block checksum)
  // Frame = 7C 8B 03 06 <aHi aLo> <128 data> <nHi nLo> <cHi cLo> + K-line checksum (fr()).
  onPhase('writing');
  const WR_START = 0x0 + ((is128 || is96) ? 0 : 0x10000), WR_END = is96 ? 0x13f80 : (is128 ? 0x1bf80 : 0x5fe00);
  const nblocks = (WR_END - WR_START) / 128;    // 2556 (384KB) / 895 (128KB) / 639 (96KB K99)
  const WIRE_BASE = is96 ? 0x0800 : 0;           // K99: on-wire addr16 starts at 0x0800 (capture bloco_001 = 08 00)
  const buildFrame = (addr) => {
    const a = WIRE_BASE + (addr - WR_START) / 16;
    const isLast = (addr + 128) === WR_END;
    const n = isLast ? 0x0000 : (a + 8);
    const payload = new Array(134);
    payload[0] = (a >> 8) & 0xff; payload[1] = a & 0xff;
    for (let j = 0; j < 128; j++) payload[2 + j] = is96 ? writeBytes[addr + j] : ((((writeBytes[addr + j] + 0x5a) & 0xff) ^ 0x4a) & 0xff);
    payload[130] = (n >> 8) & 0xff; payload[131] = n & 0xff;
    let s = 0; for (let i = 0; i < 132; i++) s += payload[i];
    const c = (-s) & 0xffff;
    payload[132] = (c >> 8) & 0xff; payload[133] = c & 0xff;
    return fr([0x7c, 0x8b, 0x03, 0x06, ...payload]);
  };
  // FAST WRITE: stream every 139B block to the bridge which loops write+ACK locally (no per-block
  // WebSocket round-trip). Needs bridge v2.0.41+ densoBlocks. Falls back to per-block otherwise.
  // Native batch is ONLY proven on Scanmatik (SM2/SM3). On FTDI/Tactrix the proven path is per-block
  // (FTDI 126s, Tactrix 142s). Native batch on FTDI stalls @block 0 (erases then fails → ECU bricked
  // into recovery) — observed on BOTH K99 96KB and Titan 384KB (K2K). So allow native batch on
  // Scanmatik devices only; every other transport uses the per-block path below.
  const _dll = (manager._bridgeOpen && manager._bridgeOpen.dll) || '';
  const _isScanmatik = _dll === 'sm2' || _dll === 'sm3' || _dll === 'sm3wifi';
  const batchOk = fastWrite && manager.mode === 'bridge' && manager.bridge && manager.bridge.densoBlocks && eeVerGE(manager.bridgeVersion, 2, 0, (is128 || is96) ? 64 : 41) && _isScanmatik;
  if (batchOk) {
    onLog('WRITE', `FAST WRITE (native batch): ${nblocks} × 139B → bridge (bridge-side loop)…`);
    const allFrames = new Uint8Array(nblocks * 139);
    for (let k = 0; k < nblocks; k++) allFrames.set(buildFrame(WR_START + k * 128), k * 139);
    // 128KB: bridge sends Query on a missing ACK (never resends data) and we do NOT resume (resume resends data).
    const MAX_RESUME = (is128 || is96) ? 0 : 6;
    let startBlk = 0, done = false;
    for (let resume = 0; resume <= MAX_RESUME && !done; resume++) {
      if (manager.abort) return _abort(onLog);
      const subHex = Array.from(allFrames.subarray(startBlk * 139)).map(hex).join('');
      let r;
      try {
        r = await manager.bridge.densoBlocks(subHex, { blocklen: 139, timeout: 1500, retries: 6, query: (is128 || is96) ? 1 : 0 }, (p) => {
          if (p && p.total) {
            const abs = startBlk + p.progress;
            onProgress('writing', Math.round((abs / nblocks) * 100));
            const el = (Date.now() - t0) / 1000;
            if (el > 0) onSpeed && onSpeed(((abs * 128) / 1024) / el);
          }
        });
      } catch (e) { onLog('ERROR', `Batch error: ${e.message} — cycle ignition, retry.`); return { result: 'failed', duration_ms: Date.now() - t0 }; }
      if (r && r.ok) { onProgress('writing', 100); onLog('WRITE', `All ${nblocks} blocks written (native batch${resume > 0 ? `, ${resume}× resume` : ''}, ${((Date.now() - t0) / 1000).toFixed(1)}s).`); done = true; break; }
      const failAbs = startBlk + (r && r.failIndex != null ? r.failIndex : 0);
      // Hard reject = wrong data/checksum for a block → NOT a stall; do not resume.
      if (r && r.rejected) { onLog('ERROR', `Block ${failAbs} REJECTED — stopped. ECU in recovery, WRITE again.`); return { result: 'failed', duration_ms: Date.now() - t0 }; }
      if (resume >= MAX_RESUME) { onLog('ERROR', `Stall @block ${failAbs} — stopped. Reconnect & WRITE again.`); return { result: 'failed', duration_ms: Date.now() - t0 }; }
      // Transient no-ACK stall (ECU momentarily stopped answering): re-sync the K-Line session and
      // RESUME from the failed block (each block carries its own address, so resume is safe).
      onLog('WARN', `Stall @block ${failAbs} — resync ${resume + 1}/${MAX_RESUME}…`);
      if (manager.bridge.klineInit) { try { await manager.bridge.klineInit({ low: 70, high: 140, bytes: [0xfe, 0x04, 0xff, 0xff], timeout: 400 }); } catch (_) {} }
      await manager.sendFramed(fr([0x72, 0x05, 0x00, 0xf0]), { timeout: 800, pre: KLINE.P2 });
      await sleep(250);
      startBlk = failAbs;
    }
  } else {
  onLog('WRITE', `Writing ${nblocks} blocks of 128B (window 0x${WR_START.toString(16)}–0x${WR_END.toString(16)}${is96 ? ', raw, wire base 0x0800' : ', obfuscated'})…`);
  let lastTick = Date.now(), lastW = 0, firstLogged = false;
  for (let k = 0; k < nblocks; k++) {
    if (manager.abort) return _abort(onLog);
    const addr = WR_START + k * 128;
    const isLast = (addr + 128) === WR_END;
    const frame = buildFrame(addr);
    // 128KB (XTune): never resend valid data on a missing ACK — send the Query variant (inner cs low byte ^1).
    const query = (is128 || is96) ? (() => { const q = frame.slice(0, -1); q[137] ^= 0x01; return fr(q); })() : frame;
    let ok = false, lastRx = null, hardReject = false;
    for (let retry = 0; retry < 6 && !ok && !hardReject; retry++) {
      const r = await manager.sendFramed(retry > 0 ? query : frame, { timeout: 1500, pre: retry === 0 ? ((fastWrite && !is96) ? KLINE.TURBO : 12) : KLINE.P3 });
      lastRx = r;
      if (!firstLogged) { onLog('UART', `TX ${frame.slice(0, 6).map(hex).join(' ')} … (${frame.length}B)`); onLog('UART', `RX ${r.reply.length ? r.reply.map(hex).join(' ') : '(none)'}`); firstLogged = true; }
      if (r.reply.length >= 4 && r.reply[0] === 0x0c && r.reply[2] === 0x06) {
        if (r.reply[3] === 0x02 || (isLast && r.reply[3] === 0x00)) { ok = true; break; }
        hardReject = true; break;
      }
      await sleep(120);
    }
    if (!ok) {
      const rx = lastRx && lastRx.reply.length ? lastRx.reply.map(hex).join(' ') : '(none)';
      onLog('ERROR', `Block ${k}/${nblocks} ${hardReject ? 'REJECTED' : 'no ACK'} — stopped. RX ${rx}`);
      return { result: 'failed', duration_ms: Date.now() - t0 };
    }
    const pct = Math.round(((k + 1) / nblocks) * 100);
    onProgress('writing', pct);
    const now = Date.now();
    if (now - lastTick > 300) { const db = (k + 1) * 128; const kbps = ((db - lastW) / 1024) / ((now - lastTick) / 1000); onSpeed && onSpeed(kbps); onLog('WRITE', `block ${k + 1}/${nblocks} (${pct}%) ${kbps.toFixed(1)} KB/s`); lastTick = now; lastW = db; }
  }
  onLog('WRITE', `All ${nblocks} blocks written.`);
  }

  // ---- FINALIZE (08 → state 0x40, 09, 0A → 0x50, 0C commit) ----
  onPhase('verifying');
  onLog('INFO', 'Finalizing…');
  await sendL('fin-08', [0x7c, 0x05, 0x03, 0x08], 3000); await sleep(1000); await poll('fin-08', 0x40);
  await sendL('fin-09', [0x7c, 0x05, 0x03, 0x09], 2000); await sleep(800);
  await sendL('fin-0a', [0x7c, 0x05, 0x03, 0x0a], 2000);
  // 0A kicks off the ECU checksum+marker validation; it is NOT instant. the reference capture polls CHECKSUM_AND_MARKER
  // until state reaches 0x50. Status 0x0D = still computing / not ready. Poll state (don't re-send 0A).
  let fa = null;
  for (let i = 0; i < 30; i++) { await sleep(500); fa = await poll('fin-0a', 0x50); if (fa === 0x50) break; }
  if (fa !== 0x50) onLog('WARN', `fin state 0x${fa != null ? fa.toString(16) : '??'} — committing anyway.`);
  const finC = await sendL('fin-0c', [0x7c, 0x05, 0x03, 0x0c], 2000);
  const committed = finC.reply.length >= 4 && finC.reply[0] === 0x0c && finC.reply[2] === 0x0c && finC.reply[3] === 0x00;
  if (!committed) { onLog('ERROR', `Finalize (0C) not confirmed — RX ${finC.reply.map(hex).join(' ') || '(none)'}. Cycle ignition and re-read to check the result.`); return { result: 'failed', duration_ms: Date.now() - t0 }; }
  onProgress('writing', 100);
  onLog('SUCCESS', `WRITE OK (${((Date.now() - t0) / 1000).toFixed(1)}s) — cycle ignition.`);
  return { result: 'success', duration_ms: Date.now() - t0 };
}

async function runFlashKeihin56K(manager, firmware, opts, cb) {
  const { checksumOffset, fastWrite = false } = opts;
  const { onLog, onProgress, onPhase, onSpeed } = cb;
  manager.abort = false;
  const t0 = Date.now();
  const total = firmware.size;
  if (total !== 57344 && total !== 65536) { onLog('ERROR', `File must be 56/64KB (got ${total} B).`); return { result: 'failed', duration_ms: 0 }; }

  // Embed the Honda 8-bit sum-to-zero checksum (byte @ size-0x11: 0xDFEF for 56K, 0xFFEF for 64K).
  const writeBytes = new Uint8Array(firmware.bytes);
  // Honor the DB checksum offset even when it is 0x0 (e.g. K15-921 uses the Honda 8-bit byte @0x0,
  // NOT the M16C default 0xFFEF). No-op on an already-valid STD image; corrects the right slot on edits.
  const _pcs = (checksumOffset != null && checksumOffset !== '') ? parseInt(checksumOffset, 16) : NaN;
  const csAddr = Number.isFinite(_pcs) ? _pcs : (total - 0x11);
  if (csAddr >= 0 && csAddr < total) {
    const r = embedHonda8bitChecksum(writeBytes, csAddr);
    const hx2 = (v) => v.toString(16).toUpperCase().padStart(2, '0');
    onLog('INFO', `Checksum @0x${csAddr.toString(16).toUpperCase()}: ${r.changed ? `${hx2(r.before)} → ${hx2(r.corrected)}` : 'OK'}`);
  }

  const fr = (a) => { const b = a.slice(); b.push(hondaChecksum(b)); return b; };
  const send = async (a, to = 800) => manager.sendFramed(fr(a), { timeout: to, pre: KLINE.P2 });
  const BLK = 64, nblocks = total / BLK;   // 896 (56K) / 1024 (64K)
  const Cpos = (pos) => {
    const cyc = Math.floor(pos / 64), pic = pos % 64;
    if (pos === nblocks - 1 && nblocks === 896) return 0x109;   // 56K final block (positional, from capture)
    return pic < 63 ? (4 + 2 * cyc + 8 * pic) : (0xfd + 2 * cyc);
  };
  const ackOk = (r) => r.ok || (r.reply.length >= 3 && r.reply[0] === 0x0e && r.reply[2] === 0x06);

  // ---- INIT-WRITE (open programming session) ----
  onPhase('handshake');
  onLog('UART', `Keihin ${total / 1024}KB init-write: opening programming session...`);
  await send([0x7d, 0x08, 0x01, 0x02, 0x50, 0x47, 0x4d]);   // "PGM"
  await send([0x7d, 0x08, 0x01, 0x03, 0x2d, 0x46, 0x49]);   // "-FI"
  await send([0x7e, 0x06, 0x01, 0x01, 0x00]);               // state poll

  // ---- ERASE ---- In the capture the ECU auto-erases after -FI and the tool WAITS ~14s
  // (the "pre-erase" frames run only AFTER that) before the write is accepted. Hold with
  // keep-alive polls so the flash is fully erased before we start writing.
  onPhase('erasing');
  onLog('ERASE', 'Erasing flash (this takes ~15s — do NOT cut power)...');
  const eraseStart = Date.now();
  while (Date.now() - eraseStart < 15000) {
    if (manager.abort) return _abort(onLog);
    await send([0x7e, 0x06, 0x01, 0x01, 0x00], 800);
    await sleep(700);
    onProgress('erasing', Math.min(95, Math.round(((Date.now() - eraseStart) / 15000) * 100)));
  }

  // ---- POST-ERASE confirmation (exact captured frames + inter-command settle gaps) ----
  await send([0x7e, 0x05, 0x01, 0x02], 4000);               // erase status
  await send([0x7e, 0x07, 0x01, 0x03, 0x00, 0x00], 4000);
  await send([0x7e, 0x06, 0x01, 0x01, 0x00]);
  await send([0x7e, 0x0b, 0x01, 0x0b, 0x00, 0x00, 0x00, 0xff, 0xff, 0xff], 4000);
  await sleep(500);
  await send([0x7e, 0x06, 0x01, 0x01, 0x00]);
  await send([0x7e, 0x07, 0x01, 0x0e, 0x01, 0x90]);
  await send([0x7e, 0x06, 0x01, 0x04, 0xff], 4000);
  await sleep(2100);
  await send([0x7e, 0x05, 0x01, 0x05]);
  await send([0x7e, 0x06, 0x01, 0x01, 0x00]);
  await send([0x7e, 0x06, 0x01, 0xa0, 0x02]);               // arm write
  await sleep(1500);
  onProgress('erasing', 100);
  if (manager.abort) return _abort(onLog);

  // ---- Leading 7E 8B 128-byte block (vector table). The reference tool gets NO ACK here
  // (it retries once and moves on); the real per-block ACKs start at the 7E 4B blocks. So
  // send this best-effort and continue regardless. ----
  onPhase('writing');
  {
    const data = Array.from(writeBytes.slice(0, 128));
    let s = 0; for (const x of data) s += x;
    const X = (0x10000 - s - 8) & 0xffff;
    const frame = fr([0x7e, 0x8b, 0x01, 0x06, 0x00, 0x00, ...data, 0x00, 0x08, (X >> 8) & 0xff, X & 0xff]);
    let acked = false;
    for (let attempt = 0; attempt < 2 && !acked; attempt++) {
      if (manager.abort) return _abort(onLog);
      const r = await manager.sendFramed(frame, { timeout: 1500, pre: KLINE.P3 });
      if (ackOk(r)) acked = true; else await sleep(300);
    }
    onLog(acked ? 'SUCCESS' : 'INFO', acked
      ? 'Leading 128B block ACKed.'
      : 'Leading 128B block sent (no ACK — expected, matches reference tool). Continuing.');
  }

  // ---- WRITE the data blocks ----
  let lastTick = Date.now(), lastW = 0, firstLogged = false;
  if (total === 65536) {
    // 64KB Keihin R8C: the ECU ACKs 7E 8B 128-byte blocks (the leading block ACKed) but NOT the 56K
    // 7E 4B 64-byte blocks. Continue the chain the leading block started: 128-byte blocks, addr16 in
    // 16-byte units (+8 per block), C=8 (128B = 8 words) exactly like the ACKed leading block. The
    // leading block already wrote block 0 (addr16 0) with next16=8, so the loop resumes at block 1.
    // EXPERIMENTAL (agent-inferred from one ACK) — run with Fast Write OFF for per-block diagnostics.
    const B = 128, nb = total / B; // 512
    onLog('WRITE', `Writing ${total} bytes in ${nb} blocks of ${B}B (R8C 7E 8B, resume @block 1)…`);
    for (let k = 1; k < nb; k++) {
      if (manager.abort) return _abort(onLog);
      const off = k * B;
      const data = Array.from(writeBytes.slice(off, off + B));
      const a16 = k * 8;
      const n16 = k === nb - 1 ? 0 : a16 + 8;
      let s = 0; for (const x of data) s += x;
      const X = (0x10000 - s - 8) & 0xffff;
      const frame = fr([0x7e, 0x8b, 0x01, 0x06, (a16 >> 8) & 0xff, a16 & 0xff, ...data, (n16 >> 8) & 0xff, n16 & 0xff, (X >> 8) & 0xff, X & 0xff]);
      let ok = false, lastRx = null;
      const gap = fastWrite ? KLINE.TURBO : 12;
      for (let retry = 0; retry < 6; retry++) {
        if (manager.abort) return _abort(onLog);
        const r = await manager.sendFramed(frame, { timeout: 1500, pre: retry === 0 ? gap : KLINE.P3 });
        lastRx = r;
        if (!firstLogged) { onLog('UART', `TX ${frame.slice(0, 6).map(hex).join(' ')} … (${frame.length}B)`); onLog('UART', `RX ${r.reply.length ? r.reply.map(hex).join(' ') : '(none)'}`); firstLogged = true; }
        if (ackOk(r)) { ok = true; break; }
        await sleep(fastWrite ? 20 : 120);
      }
      if (!ok) { onLog('ERROR', `Write failed at block ${k} (addr16 0x${a16.toString(16)}) after 6 tries. Last RX: ${lastRx && lastRx.raw.length ? lastRx.raw.map(hex).join(' ') : '(none)'}`); return { result: 'failed', duration_ms: Date.now() - t0 }; }
      const pct = Math.round(((off + B) / total) * 100);
      onProgress('writing', pct);
      const now = Date.now();
      if (now - lastTick > 300) { const kbps = ((off + B - lastW) / 1024) / ((now - lastTick) / 1000); onSpeed && onSpeed(kbps); onLog('WRITE', `block ${k + 1}/${nb} (${pct}%) ${kbps.toFixed(1)} KB/s`); lastTick = now; lastW = off + B; }
    }
    onLog('WRITE', `All ${nb} blocks written.`);
  } else {
  onLog('WRITE', `Writing ${total} bytes in ${nblocks} blocks of ${BLK}B (contiguous)${fastWrite ? ' · FAST WRITE (TURBO gap)' : ' · 12ms gap'}...`);
  for (let pos = 0; pos < nblocks; pos++) {
    if (manager.abort) return _abort(onLog);
    const off = pos * BLK;
    const data = Array.from(writeBytes.slice(off, off + BLK));
    const addr16 = pos * 4;
    const next16 = pos === nblocks - 1 ? 0 : addr16 + 4;
    let s = 0; for (const x of data) s += x;
    const X = (0x10000 - s - Cpos(pos)) & 0xffff;
    const frame = fr([0x7e, 0x4b, 0x01, 0x06, (addr16 >> 8) & 0xff, addr16 & 0xff,
      ...data, (next16 >> 8) & 0xff, next16 & 0xff, (X >> 8) & 0xff, X & 0xff]);
    let ok = false, lastRx = null;
    const KEIHIN_BLK_GAP = fastWrite ? KLINE.TURBO : 12;
    for (let retry = 0; retry < 6; retry++) {
      if (manager.abort) return _abort(onLog);
      const r = await manager.sendFramed(frame, { timeout: 1500, pre: retry === 0 ? KEIHIN_BLK_GAP : KLINE.P3 });
      lastRx = r;
      if (!firstLogged) {
        onLog('UART', `TX ${frame.slice(0, 6).map(hex).join(' ')} … (${frame.length}B)`);
        onLog('UART', `RX ${r.reply.length ? r.reply.map(hex).join(' ') : '(none)'}`);
        firstLogged = true;
      }
      if (ackOk(r)) { ok = true; break; }
      await sleep(fastWrite ? 20 : 120);
    }
    if (!ok) {
      onLog('ERROR', `Write failed at block ${pos} (addr16 0x${addr16.toString(16)}) after 6 tries. Last RX: ${lastRx && lastRx.raw.length ? lastRx.raw.map(hex).join(' ') : '(none)'}`);
      return { result: 'failed', duration_ms: Date.now() - t0 };
    }
    if (pos % 2 === 1) await manager.sendFramed(fr([0x7e, 0x05, 0x01, 0x07]), { timeout: 1500, pre: fastWrite ? KLINE.TURBO : KLINE.P2 });
    const done = off + BLK, pct = Math.round((done / total) * 100);
    onProgress('writing', pct);
    const now = Date.now();
    if (now - lastTick > 300) {
      const kbps = ((done - lastW) / 1024) / ((now - lastTick) / 1000);
      onSpeed && onSpeed(kbps);
      onLog('WRITE', `block ${pos + 1}/${nblocks} (${pct}%) ${kbps.toFixed(1)} KB/s`);
      lastTick = now; lastW = done;
    }
  }
  onLog('WRITE', `All ${nblocks} blocks written.`);
  }

  // ---- POST-WRITE (finalize). Drive the ECU state machine 30 -> 40 -> 50 -> 0F by REPEATING
  // the 08/09/0A/0C sequence (each ~1s apart, polled) until it reports 0x0F, then send the final
  // 7E 05 01 0D (expect 0E 05 0D 0F). Finalizing before 0x0F leaves the ECU in boot/recovery. ----
  onPhase('verifying');
  onLog('INFO', 'Finalizing (advancing ECU state machine to 0x0F)...');
  const pollState = async () => {
    const r = await send([0x7e, 0x06, 0x01, 0x01, 0x00], 800);
    return (r.reply && r.reply.length >= 4 && r.reply[0] === 0x0e && r.reply[2] === 0x01) ? r.reply[3] : -1;
  };
  // The final page-commit (7E 05 01 07) was already sent after the last block (pos 895) inside
  // the write loop, exactly as in the reference capture. Just settle before advancing.
  await sleep(1200);
  let st = -1;
  for (let round = 0; round < 5 && st !== 0x0f; round++) {
    if (manager.abort) return _abort(onLog);
    await send([0x7e, 0x05, 0x01, 0x08], 3000); await sleep(1000); st = await pollState();
    if (st === 0x0f) break;
    await send([0x7e, 0x05, 0x01, 0x09], 2000); await sleep(1000); st = await pollState();
    if (st === 0x0f) break;
    await send([0x7e, 0x05, 0x01, 0x0a], 2000); await sleep(1000); st = await pollState();
    if (st === 0x0f) break;
    await send([0x7e, 0x05, 0x01, 0x0c], 2000); await sleep(1000); st = await pollState();
    onLog('INFO', `Post-write round ${round + 1}: state 0x${(st < 0 ? 0 : st).toString(16)}`);
  }
  if (st !== 0x0f) onLog('WARN', `Not ready (0x${(st < 0 ? 0 : st).toString(16)}) — finalizing.`);
  const finR = await send([0x7e, 0x05, 0x01, 0x0d], 3000);   // expect -> 0E 05 0D 0F
  const finOk = finR.reply && finR.reply.length >= 4 && finR.reply[0] === 0x0e && finR.reply[2] === 0x0d && finR.reply[3] === 0x0f;
  onLog(finOk ? 'SUCCESS' : 'WARN', `Finalize: ${finR.reply.length ? finR.reply.map(hex).join(' ') : '(no reply)'}${finOk ? '' : ' (expected 0E 05 0D 0F — if not 0F, cycle the ignition OFF/ON and reconnect)'}`);
  onProgress('verifying', 100);

  onPhase('done');
  const dur = Date.now() - t0;
  onLog('SUCCESS', `WRITE OK (${(dur / 1000).toFixed(1)}s) — cycle ignition.`);
  return { result: 'success', duration_ms: dur };
}

// ------------------------------------------------------------------ Keihin SPC560 (KEIHINOTHER) real write
// Reference: decompiled Honda Flash Tools WriteFlash(KEIHINOTHER) + KeihinPreWriteFlash.
// 128-byte blocks; write frame = 7E 8B 01 06 <addrHi addrLo> <128 data> <nextHi nextLo>
// <cs1 cs2 cs3>. Address is in 16-byte units (+8 per block). All-0xFF blocks are skipped
// by pointing the ECU's "next address" past them. Reply[1]==5 => block OK, ==7 => status.
// Fast Write (opts.fastWrite, bridge only): after erase, sends 7E 06 01 A0 02 D9 (baud
// select, captured from the the reference tool debug log) and — if the ECU ACKs — switches the port
// to 62500 baud for the data transfer, then restores 10400 before finalize. ~256KB drops
// from ~5 min to ~1 min. Without the native bridge it stays at 10400 (Web Serial can't re-baud).
async function runFlashKeihinSPC560(manager, firmware, opts, cb) {
  const { startOffset = '0', fastWrite = false } = opts;
  // Log label only: K2F (checksum 9FEF / RH850) shares this 7E-8B protocol but is not an SPC560.
  const KL = (/^9FEF$/i.test(opts.checksumOffset || '') || /RH850/i.test(opts.mcu || '')) ? 'Keihin RH850' : 'SPC560';
  const { onLog, onProgress, onPhase, onSpeed } = cb;
  manager.abort = false;
  const t0 = Date.now();
  const total = firmware.size;
  const BLK = 128, STEP = 8;
  const numBlocks = Math.floor(total / BLK);

  onLog('INFO', `Keihin WRITE · ${total} B · ${numBlocks} blocks`);
  onLog('WARN', 'Backup first. Do NOT power off during write.');

  // Auto-checksum (matches KeihinAutoChecksum): embed the 8-bit sum-to-zero byte at
  // the checksum offset so an edited map still flashes with a valid ROM checksum.
  const bin = new Uint8Array(firmware.bytes);
  // 128KB Keihin (KWT-712): the bootloader gates commit (state 0x0F) on an 8-bit sum-to-zero over the
  // FIRST 64KB. The checksum slot is byte 0x0FFEF, which lives in the FF-padding zone (NOT map/tune
  // data), so balancing ONLY that byte keeps the tune 100% intact. Proven on hardware: ORI has
  // 0x0FFEF=FF (first-64KB sum=0x00) → commits 0x0F; INVICTA left 0x0FFEF=DB (sum=0x09) → stalls 0x0D
  // → recovery. NOTE: 0x1000F is the WRONG slot (2nd-64KB vector area) and corrupts firmware.
  const hx2 = (v) => v.toString(16).toUpperCase().padStart(2, '0');
  if (total === 0x20000) {
    const CS_SLOT = 0x0FFEF;
    let rest = 0;
    for (let i = 0; i < 0x10000; i++) { if (i === CS_SLOT) continue; rest = (rest + bin[i]) & 0xff; }
    const corrected = (0 - rest) & 0xff;
    const before = bin[CS_SLOT];
    bin[CS_SLOT] = corrected;
    if (before !== corrected) {
      onLog('INFO', `Checksum @0x0FFEF: ${hx2(before)} → ${hx2(corrected)}`);
    } else {
      onLog('INFO', 'Checksum @0x0FFEF: OK');
    }
  } else {
    const csAddr = parseInt(opts.checksumOffset || '0', 16);
    if (Number.isFinite(csAddr) && csAddr > 0 && csAddr < total) {
      const r = embedHonda8bitChecksum(bin, csAddr);
      onLog('INFO', `Checksum @0x${csAddr.toString(16).toUpperCase()}: ${r.changed ? `${hx2(r.before)} → ${hx2(r.corrected)}` : 'OK'}`);
    }
  }

  const ex = async (frame, { timeout = 800, pre = 0 } = {}) => {
    const r = await manager.sendFramed(frame, { timeout, pre });
    return r.reply && r.reply.length ? r.reply : r.raw;
  };
  const F = {
    pgm: [0x7D, 0x08, 0x01, 0x02, 0x50, 0x47, 0x4D, 0x94],       // "PGM"
    fi:  [0x7D, 0x08, 0x01, 0x03, 0x2D, 0x46, 0x49, 0xBB],       // "-FI"
    e02: [0x7E, 0x05, 0x01, 0x02, 0x7A],
    e03: [0x7E, 0x07, 0x01, 0x03, 0x00, 0x00, 0x77],
    e0B: [0x7E, 0x0B, 0x01, 0x0B, 0x00, 0x00, 0x00, 0xFF, 0xFF, 0xFF, 0x6E],
    e0E: [0x7E, 0x07, 0x01, 0x0E, 0x01, 0x90, 0xDB],
    e04: [0x7E, 0x06, 0x01, 0x04, 0xFF, 0x78],
    e05: [0x7E, 0x05, 0x01, 0x05, 0x77],
    sb62: [0x7E, 0x06, 0x01, 0xA0, 0x02, 0xD9],   // Fast Write: select 62500 baud
    ka:  [0x7E, 0x06, 0x01, 0x01, 0x00, 0x7A],
    f08: [0x7E, 0x05, 0x01, 0x08, 0x74],
    f09: [0x7E, 0x05, 0x01, 0x09, 0x73],
    f0A: [0x7E, 0x05, 0x01, 0x0A, 0x72],
    f0C: [0x7E, 0x05, 0x01, 0x0C, 0x70],
    f0D: [0x7E, 0x05, 0x01, 0x0D, 0x6F],
  };

  // ---- re-establish the ECU session (mirrors InitFtdiIso + wake at the start of
  // the reference WorkerThread loop). The Keihin session drops if it sits idle
  // between Connect and Write; without this, PGM gets no reply and erase fails. ----
  onPhase('handshake');
  onLog('UART', `${KL}: re-waking K-Line and re-initialising session...`);
  manager._rx = [];
  await manager.klineWake(70, 140);
  await ex([0xFE, 0x04, 0x72, 0x8C], { timeout: 400 });
  await ex([0x72, 0x05, 0x00, 0xF0, 0x99], { timeout: 400 });

  // ---- Security Access (128KB KWT-702 variants require a 27 unlock before erase).
  // the reference tool unlocks once at connect; a standalone Write must re-send it. Scoped to
  // 128KB so the proven 256KB (3FFF8) flow is unchanged. Password from the Read panel. ----
  if (total === 0x20000) {
    const pw = opts.password || 'HelloHowAreYou';
    const pwBytes = Array.from(pw).map((c) => c.charCodeAt(0) & 0xff);
    onLog('UART', `${KL}: Security Access unlock (${pw.length}-char password)...`);
    for (let off = 0; off < pwBytes.length; off += 7) {
      const seven = pwBytes.slice(off, off + 7);
      while (seven.length < 7) seven.push(0x00);
      const f = [0x27, 0x0B, 0xE0, ...seven];
      f.push(hondaChecksum(f));
      const r = await ex(f, { timeout: 800, pre: 200 });
      onLog('UART', `SecAccess -> RX ${r && r.length ? r.map(hex).join(' ') : '(none)'}`);
    }
  }

  // ---- open programming session (PGM / -FI) ----
  onLog('UART', `${KL}: entering programming mode (PGM / -FI)...`);
  let pgmOk = false;
  for (let a = 0; a < 4 && !pgmOk; a++) {
    const r = await ex(F.pgm, { timeout: 700 });
    if (r && r.length >= 4) pgmOk = true; else await sleep(500);
  }
  onLog(pgmOk ? 'SUCCESS' : 'WARN', pgmOk ? 'Programming mode ACK.' : 'No PGM ACK — continuing.');
  if (pgmOk) await ex(F.fi, { timeout: 500 });
  await ex(F.ka, { timeout: 400, pre: 200 });

  // 10-second settle before erase (reference countdown). Do NOT shorten: K93 short-settle test failed (erase uncertain, commit 0x12).
  onLog('INFO', 'Waiting 10s for the ECU before erase...');
  for (let s = 10; s > 0; s--) { if (manager.abort) return _abort(onLog); if (s % 2 === 0) onLog('INFO', `Erase in ${s}s...`); await sleep(1000); }

  // ---- erase ----
  onPhase('erasing');
  onLog('ERASE', 'Erasing flash...');
  await ex(F.e02, { timeout: 1500, pre: 250 });
  await ex(F.e03, { timeout: 800 });
  await ex(F.ka, { timeout: 400 });
  await ex(F.e0B, { timeout: 800 });
  await ex(F.ka, { timeout: 400 });
  await ex(F.e0E, { timeout: 800 });
  const r04 = await ex(F.e04, { timeout: 3000 });
  const erased1 = r04 && r04[3] === 0x00;
  await sleep(2000);
  const r05 = await ex(F.e05, { timeout: 3000, pre: 250 });
  const erased2 = r05 && r05[3] === 0x00;
  await ex(F.ka, { timeout: 400 });
  if (r05 && r05[3] === 250) { onLog('ERROR', 'ERASE FAILED (0xFA) — aborted.'); return { result: 'failed', duration_ms: Date.now() - t0 }; }
  onLog(erased1 && erased2 ? 'SUCCESS' : 'WARN', erased1 && erased2 ? 'Erase complete.' : 'Erase ACK uncertain — continuing; verify carefully.');
  onProgress('erasing', 100);

  // ---- Fast Write: switch K-Line to 62500 baud for the data transfer (bridge only) ----
  let fastActive = false;
  const endFast = async () => {
    if (fastActive) {
      onLog('INFO', 'Fast Write: restoring K-Line to 10400 baud...');
      await manager.setBaud(10400); await sleep(800); fastActive = false;
    }
  };
  if (fastWrite && manager.mode === 'bridge') {
    // Capability probe FIRST: a no-op setBaud(10400). We must NEVER tell the ECU to
    // switch to 62500 unless the bridge can actually follow — otherwise the ECU ends up
    // at 62500 while we're stuck at 10400 and every write block fails (erased ECU!).
    const canBaud = await manager.setBaud(10400);
    if (canBaud) {
      // K93 (SPC560 @3FFF8) uses 7E 06 01 A0 02 D9 → 0E 05 A0 00 (TunerForge K93 log). FF 25 path kept for K2F only.
      const isKeihinOther256 = total === 0x40000 && KL === 'Keihin RH850';
      if (isKeihinOther256) {
        // Keihin 256KB "KEIHINOTHER" (K2F RH850 @9FEF, K93 MPC560x @3FFF8): the reference the reference tool
        // flasher enters fast mode with FF 25 00 20 7C 00 (expects reply[1]=0x05, reply[3]=0x00),
        // then switches to 62500 baud. If the ECU does NOT ACK, we stay at 10400 (safe — no switch).
        onLog('WRITE', 'Fast Write (Keihin 256KB): requesting 62500 baud (TX FF 25 00 20 7C 00)...');
        const rsb = await ex([0xFF, 0x25, 0x00, 0x20, 0x7C, 0x00], { timeout: 800, pre: 200 });
        if (rsb && rsb[1] === 0x05 && rsb[3] === 0x00 && (await manager.setBaud(62500))) {
          await sleep(1000); fastActive = true;
          onLog('SUCCESS', 'Fast Write ENABLED @ 62500 baud (ECU ACKed).');
        } else {
          // K2F: FF 25 was never ACKed on hardware → try the K93-proven 7E 06 01 A0 02 D9 (no ACK = stay 10400, safe).
          onLog('WARN', `FF 25 not ACKed (RX ${rsb && rsb.length ? rsb.map(hex).join(' ') : '(none)'}) — trying 7E 06 01 A0 02 D9...`);
          const rsb2 = await ex(F.sb62, { timeout: 800, pre: 200 });
          if (rsb2 && rsb2[2] === 0xA0 && rsb2[3] === 0x00 && (await manager.setBaud(62500))) {
            await sleep(1000); fastActive = true;
            onLog('SUCCESS', 'Fast Write ENABLED @ 62500 baud (ECU ACKed 7E A0).');
          } else {
            await manager.setBaud(10400);
            onLog('WARN', `Fast Write not confirmed (RX ${rsb2 && rsb2.length ? rsb2.map(hex).join(' ') : '(none)'}). Writing safely at 10400.`);
          }
        }
      } else {
        onLog('WRITE', 'Fast Write: requesting 62500 baud (TX 7E 06 01 A0 02 D9)...');
        const rsb = await ex(F.sb62, { timeout: 800, pre: 200 });
        if (rsb && rsb[2] === 0xA0 && rsb[3] === 0x00 && (await manager.setBaud(62500))) {
          await sleep(1000); fastActive = true;
          onLog('SUCCESS', 'Fast Write ENABLED @ 62500 baud (ECU ACKed).');
        } else {
          await manager.setBaud(10400);
          onLog('WARN', `Fast Write not confirmed (RX ${rsb && rsb.length ? rsb.map(hex).join(' ') : '(none)'}). Writing at 10400.`);
        }
      }
    } else {
      onLog('WARN', 'Fast Write unsupported — update bridge. Writing at 10400.');
    }
  } else if (fastWrite) {
    onLog('WARN', 'Fast Write needs the native bridge (v1.2+). Writing at 10400.');
  }

  // ---- build the block plan (skip all-0xFF regions) ----
  // Precompute every 139-byte frame (address + payload + next-address + checksums)
  // exactly as the reference loop does, so we can either stream them all to the
  // native bridge (v1.6 batch) or fall back to the per-block loop.
  onPhase('writing');
  // 128KB recovery MUST write every block contiguously — blank-skip left the ID/tail region
  // unwritten so the ECU dropped back to recovery. Smaller images keep the fast FF-skip path.
  const contiguous = total === 0x20000;
  const isFF = contiguous ? () => false : (off) => { for (let j = 0; j < BLK; j++) if (bin[off + j] !== 0xFF) return false; return true; };
  if (contiguous) onLog('INFO', 'Keihin 128KB: full write.');
  const frames = [];
  {
    let num5 = Math.floor((parseInt(startOffset, 16) || 0) / 16);
    let i = 0;
    while (i < numBlocks) {
      const addrHi = (num5 >> 8) & 0xFF, addrLo = num5 & 0xFF;
      const dataOff = i * BLK;
      let num6 = num5 + STEP;
      for (let k = 1; k < numBlocks - i; k++) {
        if (!isFF((i + k) * BLK)) { num6 = num5 + k * STEP; i += (k - 1); break; }
        if (i + k + 1 === numBlocks) { i += k; }
      }
      if (i + 1 >= numBlocks) num6 = 0;
      const frame = new Array(139);
      frame[0] = 0x7E; frame[1] = 0x8B; frame[2] = 0x01; frame[3] = 0x06;
      frame[4] = addrHi; frame[5] = addrLo;
      for (let j = 0; j < BLK; j++) frame[6 + j] = (dataOff + j < total) ? bin[dataOff + j] : 0xFF;
      frame[134] = (num6 >> 8) & 0xFF; frame[135] = num6 & 0xFF;
      frame[136] = cs8(frame, 132, 4);
      frame[137] = cs8h(frame, 132, 4);
      frame[138] = cs8h(frame, 138, 0);
      frames.push(frame);
      num5 = num6; i++;
      if (num6 === 0) break;
    }
  }

  const canBatch = manager.mode === 'bridge' && parseFloat(manager.bridgeVersion) >= 1.6;
  let batchOk = false;
  if (canBatch) {
    // ---- native batch write: one message, no per-block round-trip (high-speed) ----
    onLog('WRITE', `Batch write: streaming ${frames.length} blocks to native bridge (${fastActive ? '62500' : '10400'} baud)...`);
    try {
      const framesHex = frames.map((f) => f.map((b) => b.toString(16).padStart(2, '0')).join('')).join('');
      await manager.bridge.flashBlocks(framesHex, { blocklen: 139, timeout: 1500, retries: 10 }, (m) => {
        const pct = Math.round((m.progress / m.total) * 100);
        onProgress('writing', pct);
        const kbps = (m.progress * BLK / 1024) / ((m.elapsed_ms || 1) / 1000);
        onSpeed && onSpeed(kbps);
        onLog('WRITE', `block ${m.progress}/${m.total} (${pct}%) ${kbps.toFixed(1)} KB/s`);
      });
      batchOk = true;
    } catch (e) {
      if (manager.abort) { await endFast(); return _abort(onLog); }
      const wasFast = fastActive;
      await endFast();
      if (wasFast && String(e.failIndex) === '0') {
        onLog('ERROR', '62500 baud failed — adapter not supported.');
        onLog('WARN', 'RECOVERY: cycle ignition, reconnect, WRITE with Fast Write OFF.');
      }
      // CH340: bridge batch reads the 139B echo in bursts and gives up at block 0 → per-block (same as Shindengen).
      if (!wasFast && String(e.failIndex) === '0') {
        onLog('WARN', `Batch write failed at block 0 — falling back to per-block write @10400.`);
      } else {
        onLog('ERROR', `Batch write failed: ${e.message}. Last RX: ${e.rx || '(none)'}`);
        return { result: 'failed', duration_ms: Date.now() - t0 };
      }
    }
    if (batchOk) onLog('WRITE', 'All data blocks written.');
  }
  if (!batchOk) {
    // ---- per-block loop (bridge < v1.6, Web Serial, or batch failed at block 0) ----
    if (manager.mode === 'bridge' && !canBatch) onLog('INFO', 'Per-block mode (update bridge for batch).');
    onLog('WRITE', `Writing (${KL}, FF-blocks skipped)...`);
    let lastTick = Date.now(), lastDone = 0, firstLogged = false;
    for (let bi = 0; bi < frames.length; bi++) {
      if (manager.abort) { await endFast(); return _abort(onLog); }
      const frame = frames[bi];
      let ok = false, failMsg = null, lastRx = null;
      for (let attempt = 0; attempt < 10 && !ok; attempt++) {
        if (manager.abort) { await endFast(); return _abort(onLog); }
        const rx = await ex(frame, { timeout: 1500, pre: 0 });
        lastRx = rx;
        if (!firstLogged) { onLog('UART', `TX 7E 8B 01 06 ${hex(frame[4])} ${hex(frame[5])} …(139B) → RX ${rx && rx.length ? rx.map(hex).join(' ') : '(none)'}`); firstLogged = true; }
        if (rx && rx[1] === 5) { ok = true; break; }
        if (rx && rx[1] === 7) {
          if (rx[4] === 0 && rx[5] === 0) { await sleep(50); continue; }
          // ECU already took this block (batch retries) and now expects the next address → accept.
          if (rx[4] === frame[134] && rx[5] === frame[135]) { onLog('INFO', `Block ${bi}: already accepted (ECU expects next).`); ok = true; break; }
          failMsg = `ECU rejected block (status ${hex(rx[4])} ${hex(rx[5])})`; break;
        }
        await sleep(120);
      }
      if (!ok) {
        const wasFast = fastActive;
        await endFast();
        if (wasFast && bi === 0) {
          onLog('ERROR', '62500 baud failed — adapter not supported.');
          onLog('WARN', 'RECOVERY: cycle ignition, reconnect, WRITE with Fast Write OFF.');
        }
        onLog('ERROR', `Write failed at block ${bi}/${frames.length}. ${failMsg || 'No ACK.'} Last RX: ${lastRx && lastRx.length ? lastRx.map(hex).join(' ') : '(none)'}`);
        return { result: 'failed', duration_ms: Date.now() - t0 };
      }
      const pct = Math.round(((bi + 1) / frames.length) * 100);
      onProgress('writing', pct);
      const now = Date.now();
      if (now - lastTick > 300) {
        const kbps = (((bi + 1 - lastDone) * BLK) / 1024) / ((now - lastTick) / 1000);
        onSpeed && onSpeed(kbps);
        onLog('WRITE', `block ${bi + 1}/${frames.length} (${pct}%) ${kbps.toFixed(1)} KB/s`);
        lastTick = now; lastDone = bi + 1;
      }
    }
    onLog('WRITE', 'All data blocks written.');
  }

  // ---- finalize / commit (MUST stay at 62500 — the ECU keeps the fast baud until
  // it is restarted; the reference tool runs post-write at 62500 too and gets the 0x0F ACK) ----
  onPhase('verifying');
  onLog('INFO', 'Finalizing / committing...');
  await ex(F.f08, { timeout: 800 }); await sleep(1000);
  await ex(F.ka, { timeout: 400 }); await ex(F.f09, { timeout: 800 }); await sleep(1000);
  await ex(F.ka, { timeout: 400 }); await ex(F.f0A, { timeout: 800 }); await sleep(1000);
  await ex(F.ka, { timeout: 400 }); await ex(F.f0C, { timeout: 800 }); await sleep(1000);
  // Poll keep-alive until the ECU state machine reaches 0x0F (ready to commit). Committing (7E..0D)
  // before 0x0F leaves the ECU in boot/recovery — so wait up to ~8s and only THEN send the commit.
  let st0f = null;
  for (let t = 0; t < 4; t++) {
    const rk = await ex(F.ka, { timeout: 800 });
    st0f = (rk && rk.length >= 4) ? rk[3] : null;
    if (st0f === 0x0F) break;
    await sleep(600);
  }
  // Always send the final commit (7E..0D). This file flashed OK before from 0x0D; some images/ECUs
  // finish from 0x0D and still boot. Report GOOD only on a 0x0F ACK, but never withhold the commit.
  const rd = await ex(F.f0D, { timeout: 800 });
  const good = !!(rd && rd[3] === 0x0F);
  if (!good) onLog('WARN', `Commit dikirim pada state 0x${st0f == null ? '??' : hex(st0f)} (ACK ${rd && rd.length ? rd.map(hex).join(' ') : '(none)'}). Restart ECU & cek apakah nyala / re-verify.`);

  await endFast(); // now restore 10400 for the next session (ECU resets its own baud on restart)

  onPhase('done');
  const dur = Date.now() - t0;
  onLog(good ? 'SUCCESS' : 'WARN', good
    ? `${KL} flash finished OK. Please restart the ECU. (${(dur / 1000).toFixed(1)}s)`
    : `${KL} flash written, but the final ACK was not 0x0F — restart the ECU and re-verify. (${(dur / 1000).toFixed(1)}s)`);
  return { result: 'success', duration_ms: dur };
}

// ------------------------------------------------------------------ Shindengen real write
function cs8(data, dataSize, startPos) {
  let s = 0;
  for (let k = startPos; k < startPos + dataSize; k++) s += data[k];
  return (255 - ((s - 1) >> 8)) & 0xff;
}
function cs8h(data, dataSize, startPos) {
  let s = 0;
  for (let k = startPos; k < startPos + dataSize; k++) s += data[k];
  return ((s ^ 0xff) + 1) & 0xff;
}
// Parse a manual 2-byte key string ("1A2B", "1a 2b", "0x1A2B") -> [b0, b1].
function parseManualKey(str) {
  const clean = String(str || '').replace(/0x/gi, '').replace(/[^0-9a-fA-F]/g, '');
  if (clean.length < 2) return null;
  const hxs = clean.length % 2 ? '0' + clean : clean;
  const b0 = parseInt(hxs.slice(0, 2), 16);
  const b1 = hxs.length >= 4 ? parseInt(hxs.slice(2, 4), 16) : 0;
  return [b0 & 0xff, b1 & 0xff];
}

// Parts whose blank-skip experiment finalized BAD this session — retry uses full contiguous write.
const _shindengenSkipBad = new Set();
async function runFlashShindengen(manager, firmware, opts, cb) {
  const { fastWrite = false } = opts;
  const { onLog, onProgress, onPhase, onSpeed } = cb;
  const startOffset = parseInt(opts.startOffset || '0', 16) || 0;
  // NOTE: In THIS app, KZRM V850 programs at block-addr base 0x0 (B21 is hardware-GOOD at 0x0).
  // Forcing 0x10000 to mimic Honda Flash Tools' "START OFFSET 10000" REGRESSED B21 → BAD (08→FB),
  // so it is reverted. The Honda tool's START OFFSET is NOT a plain block-address shift — do not
  // replicate it as one here. (B31 still fails at 0x0; needs a wire capture of the working tool.)
  onLog('INFO', `Write base 0x${startOffset.toString(16).toUpperCase()}`);
  const bin = firmware.bytes;
  const sleepMs = fastWrite ? 0 : 150;
  // Pre-write phases (seed/key + erase + finalize) ALWAYS run at normal timing —
  // Fast Write must not rush them (the ECU needs real time). Fast Write only
  // affects the write loop, which auto-ramps its speed up as blocks succeed.
  const w = (ms) => sleep(ms);
  manager.abort = false;
  const t0 = Date.now();
  onLog('INFO', fastWrite
    ? 'FAST WRITE — Seed/Key + erase at normal timing; write loop at 0ms delay (Honda Flash Tools fast write @10400).'
    : 'SAFE WRITE — steady 150ms pacing throughout.');
  // Faithful SendCommand: waits for echo + full reply frame + valid checksum.
  const send = async (cmd, timeout = 800) => {
    const r = await manager.sendFramed(cmd, { timeout, pre: sleepMs || KLINE.P2 });
    onLog('UART', `TX ${cmd.map(hex).join(' ')}  |  RX ${r.reply.length ? r.reply.map(hex).join(' ') : (r.raw.length ? '(raw ' + r.raw.map(hex).join(' ') + ')' : '(no reply)')}`);
    return r;
  };

  // ---- Phase 1: Seed/Key + Erase (ShindengenPreWriteFlash) ----
  onPhase('handshake');
  onLog('INFO', 'Shindengen pre-write: preparing Seed/Key...');
  // ---- Seed-Key identification (Shindengen security access) ----
  const skMcu = detectMcu(bin);
  const skWrite = findWriteConstants(bin);
  const skRec = findRecoveryConstants(bin);
  const h16 = (v) => '0x' + (v & 0xffff).toString(16).toUpperCase().padStart(4, '0');
  onLog('INFO', `Seed-Key ECU · ${skMcu === 'V850' ? 'V850/RH850' : 'unknown MCU'}`);
  if (skWrite) onLog('INFO', `  WRITE-key @0x${skWrite.sigOffset.toString(16).toUpperCase()} · C0=${h16(skWrite.C0)} C1=${h16(skWrite.C1)} C2=${h16(skWrite.C2)}`);
  else onLog('WARN', '  WRITE-key signature NOT found in loaded firmware.');
  if (skRec) onLog('INFO', `  RECOVERY-key @0x${skRec.sigOffset.toString(16).toUpperCase()} · C0=${h16(skRec.C0)} C1=${h16(skRec.C1)} C2=${h16(skRec.C2)}`);
  else onLog('INFO', '  RECOVERY-key signature not found (normal for a non-recovery bin).');
  // A locked/negative security reply is a 5-byte frame with status 0xFF, e.g. 0B 05 02 FF EF.
  const isSecLocked = (r) => !!(r && r.ok && r.reply[1] === 5 && r.reply[3] === 0xFF);
  const lockedGuidance = () => {
    onLog('ERROR', 'ECU SECURITY LOCKED — the ECU is rejecting security access (status FF).');
    onLog('WARN', 'Temporary lockout.');
    onLog('WARN', 'Power OFF 15s, then ON.');
    onLog('WARN', 'Retry ONCE only.');
  };

  // Full Shindengen security handshake -> returns a fresh seed (or a lock/fail signal).
  // The ECU needs real time to generate the seed: run the preamble ONCE, wait ~10s, then request
  // the seed ONCE. HARDWARE (2026-06, KZRM-B21): requesting at 3s returns status 0xF0 (seed NOT
  // ready), and re-sending the preamble to retry immediately locks the ECU (FF). So do NOT shorten
  // this wait and do NOT re-run the preamble — 10s single-shot is the proven-safe timing.
  const doHandshake = async (waitSec) => {
    const a = await send([0x7B, 0x08, 0x02, 0x02, 0x50, 0x19, 0x76, 0x9A]);
    const b = await send([0x7B, 0x08, 0x02, 0x03, 0x25, 0x37, 0x86, 0x96]);
    if (isSecLocked(a) && isSecLocked(b)) return { locked: true };
    for (let i = 0; i < waitSec; i++) {
      if (manager.abort) return { aborted: true };
      onLog('INFO', `Preparing Seed/Key (waiting ${waitSec - i}s)`);
      onProgress('handshake', Math.round((i / waitSec) * 100));
      await sleep(1000);
    }
    const sr = await send([0x7B, 0x05, 0x02, 0x04, 0x7A]);
    if (!(sr.ok && sr.reply[1] === 7 && sr.reply[3] === 0)) return { locked: isSecLocked(sr), failed: true };
    return { s: sr, seed: [sr.reply[4], sr.reply[5]] };
  };

  // Single-shot 10s wait — proven safe. Retrying (re-preamble) locks the ECU (hardware-confirmed),
  // and the seed is not ready before ~10s, so there is no safe speed gain here.
  let hs = await doHandshake(10);
  if (hs.aborted) return _abort(onLog);
  if (hs.locked || hs.failed) {
    if (hs.locked) lockedGuidance(); else onLog('ERROR', 'Failed generating Seed/Key.');
    return { result: 'failed', duration_ms: Date.now() - t0 };
  }
  let s = hs.s;
  let seed = hs.seed;
  onLog('UART', `Seed: ${seed.map(hex).join(' ')}`);
  const recovery = !!opts.recovery;
  const seedKeyBypass = !!opts.seedKeyBypass;
  const seed16 = ((seed[0] << 8) | seed[1]) & 0xffff;
  const kBytes = (v) => [(v >> 8) & 0xff, v & 0xff];
  const fwWrite = skWrite ? kBytes(computeKey(seed16, skWrite.C0, skWrite.C1, skWrite.C2)) : null;
  const fwRec = skRec ? kBytes(computeKey(seed16, skRec.C0, skRec.C1, skRec.C2)) : null;
  // Documented Honda/Shindengen V850 variant (XOR) — for K25G BEAT110 etc.
  const fwWriteV = skWrite ? kBytes(computeKeyHonda(seed16, skWrite.C0, skWrite.C1, skWrite.C2)) : null;
  const fwRecV = skRec ? kBytes(computeKeyHonda(seed16, skRec.C0, skRec.C1, skRec.C2)) : null;
  const manualKey = seedKeyBypass ? parseManualKey(opts.manualKey) : null;
  if (fwWrite) onLog('INFO', `Computed WRITE key (RH850): ${fwWrite.map(hex).join(' ')}`);
  if (fwRec) onLog('INFO', `Computed RECOVERY key (RH850): ${fwRec.map(hex).join(' ')}`);
  if (fwWriteV) onLog('INFO', `Computed WRITE key (V850 Honda XOR): ${fwWriteV.map(hex).join(' ')}`);
  if (fwRecV) onLog('INFO', `Computed RECOVERY key (V850 Honda XOR): ${fwRecV.map(hex).join(' ')}`);
  if (manualKey) onLog('INFO', `Manual key entered: ${manualKey.map(hex).join(' ')}`);

  // Build the ordered list of key candidates to challenge with.
  // NOTE: Shindengen seeds are SINGLE-USE — the ECU locks (status FF) after ONE wrong key.
  // So in bypass mode the user picks ONE key type per session (per power-cycle) via keyChoice.
  const keyChoice = opts.keyChoice || 'auto';
  const candidates = [];
  const pushC = (label, k) => { if (k && !candidates.some((c) => c.k[0] === k[0] && c.k[1] === k[1])) candidates.push({ label, k }); };
  if (seedKeyBypass) {
    if (keyChoice === 'recovery') pushC('Firmware Recovery', fwRec);
    else if (keyChoice === 'write') pushC('Firmware Write', fwWrite);
    else if (keyChoice === 'v850') pushC('V850 Write (Honda XOR)', fwWriteV);
    else if (keyChoice === 'v850r') pushC('V850 Recovery (Honda XOR)', fwRecV);
    else if (keyChoice === 'echo') pushC('Echo Bypass (key=seed)', [seed[0], seed[1]]);
    else if (keyChoice === 'manual') pushC('Manual', manualKey);
    else if (keyChoice === 'sweep') {
      // Honda Flash Tool "Buka Seed/Key" fixed-key candidates: N7 67 for N = 2..F.
      for (let hi = 0x27; hi <= 0xF7; hi += 0x10) pushC(`Honda ${hex(hi)} 67`, [hi, 0x67]);
    }
    else {
      // AUTO: automatically pick the single correct key (Shindengen seed is single-use,
      // so never cascade). recovery=true (blank app, 0000 id) -> recovery key, else write key.
      pushC(recovery ? 'Firmware Recovery' : 'Firmware Write', recovery ? fwRec : fwWrite);
    }
    onLog('INFO', `Seed-Key BYPASS on — key choice: ${keyChoice}.`);
  } else {
    pushC(recovery ? 'Firmware Recovery' : 'Firmware Write', recovery ? fwRec : fwWrite);
  }
  if (candidates.length === 0) {
    onLog('ERROR', 'No seed-key — load matching BIN or enter manual key.');
    return { result: 'failed', duration_ms: Date.now() - t0 };
  }

  const tryKey = async (label, keyBytes) => {
    const dKey = [0x7B, 0x07, 0x02, 0x05, keyBytes[0], keyBytes[1], 0x00];
    dKey[6] = cs8h(dKey, s.reply[1] - 1, 0);
    onLog('INFO', `Challenging Seed/Key with ${label} key ${keyBytes.map(hex).join(' ')}...`);
    const k = await send(dKey);
    const accepted = k.ok && k.reply[1] === 5 && k.reply[3] === 0;
    const locked = k.ok && k.reply[1] === 5 && k.reply[3] === 0xFF;
    return { accepted, locked };
  };

  const sweeping = keyChoice === 'sweep';
  let unlocked = false;
  let acceptedKey = null, acceptedLabel = null, acceptedSeed = null;
  for (let ci = 0; ci < candidates.length; ci++) {
    if (manager.abort) return _abort(onLog);
    // In SWEEP mode, re-establish a fresh security session before each new candidate
    // (the seed is single-use). If the ECU is locked (FF) we stop safely.
    if (sweeping && ci > 0) {
      onLog('INFO', `Re-handshake for candidate ${ci + 1}/${candidates.length}...`);
      const rh = await doHandshake(4);
      if (rh.aborted) return _abort(onLog);
      if (rh.locked) {
        onLog('ERROR', 'ECU LOCKED (FF) — power-cycle 15s, Sweep again.');
        break;
      }
      if (rh.failed) { onLog('ERROR', 'Seed re-generation failed — stopping sweep.'); break; }
      s = rh.s; seed = rh.seed;
      onLog('UART', `Seed: ${seed.map(hex).join(' ')}`);
    }
    const c = candidates[ci];
    const res = await tryKey(c.label, c.k);
    if (res.accepted) { onLog('SUCCESS', `Seed/Key accepted (${c.label}).`); unlocked = true; acceptedKey = c.k; acceptedLabel = c.label; acceptedSeed = seed; break; }
    if (res.locked) {
      if (sweeping) { onLog('WARN', `${c.label} rejected (FF) — next key…`); continue; }
      onLog('ERROR', `${c.label} key rejected — ECU is now LOCKED (status FF).`);
      onLog('WARN', 'Shindengen seed is single-use: the ECU locks after one wrong key.');
      onLog('WARN', 'Power-cycle 15s, then try another Key Type.');
      break;
    }
    onLog(seedKeyBypass ? 'WARN' : 'ERROR', `${c.label} key rejected by ECU (status F1 — wrong key).`);
    if (!sweeping && ci < candidates.length - 1) await sleep(200);
  }
  if (!unlocked) {
    onLog('ERROR', 'Failed challenging Seed/Key — no candidate was accepted.');
    if (seedKeyBypass) onLog('WARN', 'Power-cycle, then try another Key Type.');
    return { result: 'failed', duration_ms: Date.now() - t0 };
  }

  // SEED-KEY TEST mode: unlock succeeded — log the confirmed pair and STOP (no erase/write).
  // Safe to run repeatedly (e.g. on a known-good K1AA) to collect SEED->accepted-KEY reference pairs.
  if (opts.seedKeyTestOnly) {
    onLog('SUCCESS', `SEED-KEY TEST OK ✓  SEED=${acceptedSeed.map(hex).join('')} → KEY=${acceptedKey.map(hex).join('')}  (${acceptedLabel})`);
    onLog('INFO', 'Nothing written — firmware untouched.');
    onPhase('done');
    return { result: 'success', duration_ms: Date.now() - t0 };
  }

  onPhase('erasing');
  onLog('ERASE', 'Erasing flash...');
  await send([0x7C, 0x06, 0x02, 0x01, 0x00, 0x7B]);
  await send([0x7C, 0x0B, 0x02, 0x0B, 0x00, 0x00, 0x00, 0xFF, 0xFF, 0xFF, 0x6F]);
  await send([0x7C, 0x07, 0x02, 0x0E, 0x01, 0x00, 0x6C]);
  const er1 = await send([0x7C, 0x06, 0x02, 0x04, 0xFF, 0x79], 3000);
  if (!(er1.ok && er1.reply[3] === 0)) { onLog('ERROR', 'Erasing flash failed.'); return { result: 'failed', duration_ms: Date.now() - t0 }; }
  await w(2000);
  const er2 = await send([0x7C, 0x05, 0x02, 0x05, 0x78], 3000);
  if (!er2.ok || !er2.reply || er2.reply[3] === 0xFA) {
    onLog('ERROR', (er2.reply && er2.reply[3] === 0xFA) ? 'Erasing flash failed (block error).' : 'Erasing flash failed (no erase ACK).');
    return { result: 'failed', duration_ms: Date.now() - t0 };
  }
  // Some Shindengen ECUs (e.g. RH850 K60R) answer this final erase subcommand with a
  // non-zero busy/accepted status (0x01/0x10) instead of 0x00 — the earlier subcommands
  // in this same sequence already do (7C 06 02 01 -> 0x10). AlexMap proceeds to the first
  // write block right after 7C 05 02 05 regardless, so we accept any framed reply that is
  // not the 0xFA block-error and just give the ECU extra settle time when it reports busy.
  if (er2.reply[3] !== 0) {
    onLog('INFO', `Erase status 0x${er2.reply[3].toString(16).toUpperCase()} — settling…`);
    await w(2000);
  }
  onLog('SUCCESS', 'Flash erased.');
  // Recovery-mode ECUs can stay internally busy for a moment after the erase
  // sequence; give it time to settle before the first write block, otherwise the
  // ECU may reject block 0. A longer wait is always safe (it can never brick).
  await w(2500);

  // ---- Phase 2: Write 128-byte blocks (RH850) ----
  onPhase('writing');
  const num3 = 128, num4 = 8;
  const total = firmware.size;
  const numBlocks = Math.floor(total / num3);
  const startA0 = Math.floor(startOffset / 16);
  // Blank-skip plan — proven from the AlexMap capture: each 139-byte block carries its own
  // target + next address, so we transmit ONLY blocks with real data (erased flash is already
  // 0xFF) and chain across the gaps. Same physics @10400, just fewer blocks on the wire.
  // EXCEPTION: K1AA 256KB (0x40000) bootloader needs a full contiguous write — never skip it.
  // Blank-skip is ONLY proven safe on K59J (AlexMap capture). K1AA (256KB) AND K1ZG bootloaders
  // finalize BAD when blank blocks are skipped — hardware-confirmed: after a skipped write,
  // "7C 05 02 0A" onward goes silent (no reply) and finalize returns BAD. So we default to a full
  // contiguous write and ONLY skip for a confirmed K59J part/ID.
  const partStr = String(opts.part || opts.ecmId || '').toUpperCase();
  // blank-skip = write only non-0xFF blocks, chaining next-address across the gaps (fewer blocks on
  // the wire = faster at the SAME 10400 baud). AlexMap-proven GOOD on K59J AND KZRA-601 (capture
  // kline_1790188036612 + user hardware: 937/3072 blocks, finalize 0F GOOD, 02:39 vs ~7:50).
  // K1ZG/K1AA are hardware-CONFIRMED BAD with skip → ALWAYS contiguous, never skip.
  // User opt-in (2026-06): apply blank-skip to ALL 384KB (0x60000) Shindengen except the blocked
  // parts. Untested parts get a session auto-fallback: a BAD finalize disables skip so the retry is
  // full contiguous (recoverable, not a brick).
  // KZR family (KZRA, KZRM, …) + K59J are hardware-proven GOOD with blank-skip → ALWAYS skip
  // (fast) and never session-disabled. K1ZG/K1AA stay contiguous.
  const skipProven = /K59J|KZR/.test(partStr);
  const skipBlocked = /K1ZG|K1AA/.test(partStr);
  const is384 = firmware.size === 0x60000;
  // ---- MCU-tag routing (from the Simple Honda Flasher / Mototan source) -------------------------
  // The 384KB Shindengen bin carries an ASCII MCU tag that decides the write MODE:
  //   "SV850" (53 56 38 35 30) = Renesas V850  → blank-skip is SAFE (KZRM-B21 hardware-GOOD).
  //   "SH850" (53 48 38 35 30) = Renesas RH850  → MUST write CONTIGUOUS (no blank-skip). The
  //     reference sets flag=false for RH850, i.e. every block sequential. This is almost certainly
  //     why KZRM-B31 finalized BAD with blank-skip (0A→12, 0D→12) while B21 was GOOD — B31 is the
  //     RH850 revision. Both still program from base 0x10000 (skip first 64KB / vectors).
  const findAscii = (str) => {
    const t = [...str].map((c) => c.charCodeAt(0));
    for (let p = 0; p + t.length <= bin.length; p++) {
      let ok = true;
      for (let d = 0; d < t.length; d++) if (bin[p + d] !== t[d]) { ok = false; break; }
      if (ok) return p;
    }
    return -1;
  };
  const sigV850 = findAscii('SV850') >= 0;
  const sigRh850 = findAscii('SH850') >= 0;
  // RH850 tag (and NOT a V850 tag) forces a contiguous write for this 384KB image.
  const mcuForceContiguous = is384 && sigRh850 && !sigV850;
  if (is384 && (sigV850 || sigRh850))
    onLog('INFO', `MCU: ${sigV850 ? 'SV850 (blank-skip)' : 'SH850 (contiguous)'}`);
  // Blank-skip is tried first for ALL 384KB (fast), BUT a BAD finalize marks the part in
  // _shindengenSkipBad so the retry is full contiguous — for EVERY part incl. KZR. Hardware
  // (2026-06): KZRM-B31 finalized BAD even flashing the ORIGINAL valid bin with blank-skip
  // (0A→12, 0D→12) — the SAME failure mode as K1ZG/K1AA, so B31 needs a contiguous write.
  // KZRM-B31 (0101AE…): Mototan-style = base 0x10000 + VALID checksum (FIX CHECKSUM, == IronFony 0x882874B6 @0x1AFE8).
  // 0x0 base with fixed checksum and 0x10000 with the unfixed file were both 08→FB BAD (2026-10-12).
  const b31Contiguous = /KZRM-B31|0101AE/.test(partStr);
  if (b31Contiguous) onLog('WARN', 'KZRM-B31: base 0x10000, fast write (needs valid checksum).');
  const allowSkip = firmware.size !== 0x40000 && !skipBlocked && !_shindengenSkipBad.has(partStr) && (skipProven || is384) && !mcuForceContiguous;
  const skipUntested = allowSkip && !skipProven;
  const isFFblk = (idx) => { const o = idx * num3; for (let d = 0; d < num3; d++) if (bin[o + d] !== 0xFF) return false; return true; };
  // KZRM family (V850 384KB): the program region starts at byte 0x10000 — the first 64KB (vectors +
  // bootloader area) must NOT be written or the ECU rejects at finalize (08→FB). PROVEN from the
  // Mototan/Honda-Flash-Tools B21 wire capture (kline_1790451709555): first write block is addr word
  // 0x1000 (byte 0x10000) with IDENTITY mapping (flash addr == file offset; file[0x10000]=A5 A5 A5 A5).
  // B21 tolerated writing from 0x0 but B31 does NOT — so start the plan at block 0x10000/128 for ALL
  // KZRM. startA0 stays 0 so identity is preserved (block bi → addr bi*8 → byte bi*128 → file[bi*128]).
  const minBlk = (/KZRM/.test(partStr) && firmware.size === 0x60000) ? Math.floor(0x10000 / num3) : 0;
  if (minBlk) onLog('INFO', 'KZRM: base 0x10000');
  // Write from minBlk. Blank-skip (skip all-0xFF blocks) is fast; the last block is always written.
  // If the ECU finalizes BAD, the retry falls back to contiguous automatically.
  const plan = [];
  for (let bi = minBlk; bi < numBlocks; bi++) if (!allowSkip || !isFFblk(bi) || bi === numBlocks - 1) plan.push(bi);
  if (skipUntested)
    onLog('WARN', `⚠️ FAST WRITE 384KB: blank-skip unverified — auto-fallback if BAD.`);
  onLog('WRITE', allowSkip
    ? `Writing ${plan.length}/${numBlocks} blocks (blank-skip: ${numBlocks - plan.length} empty 0xFF blocks skipped @10400)…`
    : `Writing all ${numBlocks} blocks (contiguous @10400)…`);

  // Fast Write overclocks the FTDI port slightly for the data phase only. The ECU K-Line stays at
  // its native 10400; the overclock only trims the byte TX time a touch. NOTE (2026-06, hardware):
  // overclocking gives NO measurable throughput gain — the write rate stays ~0.9 KB/s because the
  // real floor is the ECU's per-block flash-program time + the 10400 wire, NOT our TX speed. This
  // is expected: the reference tool itself flashes at this rate. There is NO proven Shindengen baud-switch
  // (KWP2000 K-Line is 10400; the 125000-baud tricks online are for CAN ECUs, not Honda K-Line).
  // 10650 is the proven-GOOD conservative value (~2.4% over, inside per-byte UART tolerance).
  // Restored to 10400 before finalize. Bad decode -> per-block retry + re-erase fallback at 10400,
  // so overclocking can never brick.
  // Overclock DISABLED (2026-06): the 10650 experiment gave NO measurable throughput gain
  // (the floor is the ECU per-block flash time + 10400 wire) and risked byte corruption that
  // finalized BAD on KZRM-B21. Stay at the ECU-native 10400 for reliable writes.
  const FAST_BAUD = 10400;
  let fastBaud = false;

  // ---- Fast Write native batch (bridge >= 1.6): stream all 139-byte blocks in ONE
  // message; the bridge loops writing them with NO per-block WebSocket round-trip.
  // Shindengen block ACK (7C 8B ... -> rp[1]==0x05 accept / 0x07 busy) matches the
  // bridge FlashBlocks contract exactly. We stay at 10400 baud — Shindengen has NO
  // proven high-baud switch, and guessing one on an erased ECU can brick it. So this
  // maximises the 10400 wire rate but does NOT reach Keihin's 62500-baud speed. ----
  let batchDone = false, pacing = !fastWrite;
  const bvGE = (a, b, c) => { const p = String(manager.bridgeVersion || '').split('.').map((n) => parseInt(n, 10) || 0); return p[0] > a || (p[0] === a && (p[1] > b || (p[1] === b && (p[2] || 0) >= c))); };
  if (fastWrite && manager.mode === 'bridge' && manager.bridge && manager.bridge.flashBlocks
      && parseFloat(manager.bridgeVersion) >= 1.6) {
    if (!bvGE(2, 0, 12)) onLog('INFO', 'Tactrix Fast Write needs bridge v2.0.12+.');
    try {
      // AlexMap fast-write: skip all-0xFF blocks (erased flash is already 0xFF), so we only
      // transmit blocks that actually contain data. Proven from the AlexMap capture
      // (kline_1788948746932): K59J 384KB wrote 1052/3072 blocks for this VARIO bin, chaining
      // each block's next-address across the skipped gaps -> faster at the SAME 10400 baud.
      // Each 139-byte block CARRIES its own target address, so non-contiguous writes are fine.
      // The last block is always written so the image tail/end marker is present. If an ECU
      // rejects the skip (asks for a skipped addr), flashBlocks fails -> we fall back to the
      // contiguous per-block write below (re-erase + resync), so this can never brick.
      // EXCEPTION — K1AA 256KB (0x40000): the K1AA bootloader does NOT tolerate blank-block
      // skipping. Hardware-confirmed TWICE: a skipped K1AA write (1688/2048 blocks, 360 blank
      // skipped) finalizes BAD — 7C 05 02 0A onward goes silent (no reply). The AlexMap K1AA
      // capture writes ALL 2048 blocks contiguously and finalizes good. So for 256KB Shindengen
      // we ALWAYS write every block. Do NOT re-enable skip for 0x40000.
      const startA = startA0;
      const frames = [];
      for (let p = 0; p < plan.length; p++) {
        const i = plan[p];
        const a = startA + i * num4;
        const next = (p + 1 < plan.length) ? (startA + plan[p + 1] * num4) : 0;
        const fr = new Array(139);
        fr[0] = 0x7C; fr[1] = 0x8B; fr[2] = 0x02; fr[3] = 0x06;
        fr[4] = (a >> 8) & 0xff; fr[5] = a & 0xff;
        for (let d = 0; d < num3; d++) fr[6 + d] = bin[i * num3 + d];
        fr[134] = (next >> 8) & 0xff; fr[135] = next & 0xff;
        fr[136] = cs8(fr, num3 + 4, 4);
        fr[137] = cs8h(fr, num3 + 4, 4);
        fr[138] = cs8h(fr, 138, 0);
        for (const b of fr) frames.push(b);
      }
      const framesHex = frames.map((b) => b.toString(16).padStart(2, '0')).join('');
      const skipped = numBlocks - plan.length;
      onLog('WRITE', allowSkip
        ? `Native batch write (blank-skip): streaming ${plan.length}/${numBlocks} blocks (skipped ${skipped} blank 0xFF) to bridge @10400 baud...`
        : `Native batch write (contiguous): streaming all ${plan.length}/${numBlocks} blocks to bridge @10400 baud (no blank-skip — full write for reliable finalize)...`);
      let lastLogT = 0, lastLogP = 0;
      await manager.bridge.flashBlocks(framesHex, { blocklen: 139, timeout: 1500, retries: 12 }, (m) => {
        onProgress('writing', Math.round((m.progress / m.total) * 100));
        const kbps = ((m.progress * num3) / 1024) / ((m.elapsed_ms || 1) / 1000);
        onSpeed && onSpeed(kbps);
        const now = Date.now();
        if (now - lastLogT > 2000 || (m.progress - lastLogP) >= Math.ceil(m.total / 20)) {
          const pct = Math.round((m.progress / m.total) * 100);
          onLog('WRITE', `Writing block ${m.progress}/${m.total}  (${pct}%)  ${kbps.toFixed(1)} KB/s`);
          lastLogT = now; lastLogP = m.progress;
        }
      });
      onLog('WRITE', 'All blocks written (native batch).');
      batchDone = true;
    } catch (e) {
      if (manager.abort) return _abort(onLog);
      onLog('WARN', `Batch write failed at ${e.failIndex != null ? 'block ' + e.failIndex : 'setup'} (${e.message}).`);
      // If some blocks already went through, the ECU's write pointer advanced past block 0,
      // so a plain per-block retry from 0 would mismatch the address. Re-erase to reset it.
      if (e.failIndex && e.failIndex > 0) {
        onLog('WARN', 'Re-erasing to resync…');
        await send([0x7C, 0x06, 0x02, 0x01, 0x00, 0x7B]);
        await send([0x7C, 0x0B, 0x02, 0x0B, 0x00, 0x00, 0x00, 0xFF, 0xFF, 0xFF, 0x6F]);
        await send([0x7C, 0x07, 0x02, 0x0E, 0x01, 0x00, 0x6C]);
        const re1 = await send([0x7C, 0x06, 0x02, 0x04, 0xFF, 0x79], 3000);
        await w(2000);
        const re2 = await send([0x7C, 0x05, 0x02, 0x05, 0x78], 3000);
        if (!(re1.ok && re1.reply[3] === 0 && re2.ok && re2.reply[3] === 0)) {
          onLog('ERROR', 'Re-erase failed — restart the ECU and flash again with Fast Write OFF.');
          return { result: 'failed', duration_ms: Date.now() - t0 };
        }
        onLog('SUCCESS', 'Re-erased. Retrying with reliable paced per-block write...');
        await w(2500);
      }
      // Force steady pacing on the fallback — the batch failed because the link couldn't keep
      // up (common on Android FTDI), so per-block with 150ms pacing is far more reliable.
      pacing = true;
      onLog('INFO', 'Falling back to reliable per-block write (150ms pacing).');
    }
  }

  let lastTick = Date.now(), lastW = 0;
  for (let p = 0; !batchDone && p < plan.length; p++) {
    if (manager.abort) return _abort(onLog);
    const i = plan[p];
    const a = startA0 + i * num4;                                             // this block's flash address
    const nextA = (p + 1 < plan.length) ? (startA0 + plan[p + 1] * num4) : 0; // chain to the next NON-blank block
    const frame = new Array(139);
    frame[0] = 0x7C; frame[1] = 0x8B; frame[2] = 0x02; frame[3] = 0x06;
    frame[4] = (a >> 8) & 0xff; frame[5] = a & 0xff;
    for (let d = 0; d < num3; d++) frame[6 + d] = bin[i * num3 + d];
    frame[134] = (nextA >> 8) & 0xff; frame[135] = nextA & 0xff;
    frame[136] = cs8(frame, num3 + 4, 4);
    frame[137] = cs8h(frame, num3 + 4, 4);
    frame[138] = cs8h(frame, 138, 0);
    let ok = false;
    let lastRx = null;
    let resyncP = -1;
    for (let retry = 0; retry < 6; retry++) {
      const r = await manager.sendFramed(frame, { timeout: 1500, pre: sleepMs });
      const rp = r.reply;
      lastRx = r;
      if (r.ok && rp[1] === 5) { ok = true; break; }
      // Android FTDI bridge sometimes drops the leading byte(s) of the ACK. The write-block
      // accept is the CONSTANT frame 0C 05 06 02 E7, so accept ANY reply ending in that tail.
      if (rp.length >= 3 && rp[rp.length - 3] === 0x06 && rp[rp.length - 2] === 0x02 && rp[rp.length - 1] === 0xE7) {
        if (rp[1] !== 5) onLog('INFO', `Block ${i}: ACK recovered (bridge dropped leading byte: ${rp.map(hex).join(' ')}).`);
        ok = true; break;
      }
      // len-7 reply with next-addr 00 00 = ECU busy programming the flash page → resend the same block.
      if (r.ok && rp[1] === 7 && rp[4] === 0 && rp[5] === 0) {
        if (retry === 0) onLog('WARN', `Block ${i}: busy, retrying…`);
        if (pacing) await sleep(150);
        continue;
      }
      // Address-error reply: the ECU reports the block address it EXPECTS next (06 03 <hi> <lo>),
      // tolerant of dropped leading byte(s). Map it back onto the blank-skip plan to self-resync.
      let ai = -1;
      for (let j = 0; j + 3 < rp.length; j++) { if (rp[j] === 0x06 && rp[j + 1] === 0x03) { ai = j; break; } }
      if (ai >= 0) {
        const wantN5 = (rp[ai + 2] << 8) | rp[ai + 3];
        const wantBlock = Math.floor(wantN5 / num4);
        if (wantN5 === nextA || wantBlock === i + 1) { onLog('INFO', `Block ${i}: OK (garbled ACK)`); ok = true; break; }
        const wp = plan.indexOf(wantBlock);
        if (wp >= 0 && wp !== p) { onLog('WARN', `Resync → block ${wantBlock}`); resyncP = wp; ok = true; break; }
        // ECU still wants THIS block -> resend below.
      }
      onLog('WARN', `Block ${i}: unexpected reply ${rp.length ? rp.map(hex).join(' ') : '(none)'} — retry ${retry + 1}/6`);
      if (pacing) await sleep(150);
    }
    if (!ok) {
      onLog('ERROR', `Write failed at block ${i} (addr 0x${(a * 16).toString(16).toUpperCase()}). Last RX: ${lastRx && lastRx.raw.length ? lastRx.raw.map(hex).join(' ') : '(no reply)'}`);
      onLog('INFO', 'Tip: turn OFF Fast Write for a slower but more reliable write.');
      return { result: 'failed', duration_ms: Date.now() - t0 };
    }
    if (resyncP >= 0) { p = resyncP - 1; continue; }
    const written = (p + 1) * num3;
    const planTotal = plan.length * num3;
    onProgress('writing', Math.round((written / planTotal) * 100));
    const now = Date.now();
    if (now - lastTick > 400) {
      const kbps = ((written - lastW) / 1024) / ((now - lastTick) / 1000);
      onSpeed && onSpeed(kbps);
      onLog('WRITE', `block ${p + 1}/${plan.length} · 0x${(a * 16).toString(16).toUpperCase().padStart(6, '0')} (${Math.round((written / planTotal) * 100)}%) ${kbps.toFixed(1)} KB/s`);
      lastTick = now; lastW = written;
    }
  }
  onLog('WRITE', 'All blocks written.');

  // Restore native 10400 baud before finalize (finalize commits the image — keep it rock-solid).
  if (fastBaud) { try { await manager.setBaud(10400); onLog('INFO', 'Baud dikembalikan ke 10400 sebelum finalize.'); } catch (_) {} }
  await w(1500); // let the ECU settle after the last programmed page before the finalize/commit

  // ---- Phase 3: Finalize (Shindengen) ----
  onPhase('verifying');
  onLog('INFO', 'Finalizing...');
  // Proven single-shot 08/09/0A/0C/0D commit (K59J & KZRM-B21 reach 0x0F on the FIRST pass —
  // unchanged). Status 0x12 on 0D means the commit state machine wasn't ready yet (fired too
  // soon). Some units (hardware: KZRM-B31) need extra settling, so if the first pass is not 0x0F
  // we settle and REPEAT the finalize a few times. Finalize commands never brick.
  const finStep = async (fr, label, to) => {
    const r = await send(fr, to);
    onLog('WRITE', `fin ${label} → ${r.reply && r.reply.length ? r.reply.map(hex).join(' ') : '(no reply)'}`);
    return r;
  };
  const doFinalizeRound = async () => {
    await finStep([0x7C, 0x05, 0x02, 0x08, 0x75], '08'); await w(1000);
    await finStep([0x7C, 0x05, 0x02, 0x09, 0x74], '09'); await w(1000);
    await finStep([0x7C, 0x05, 0x02, 0x0A, 0x73], '0A'); await w(1000);
    await finStep([0x7C, 0x05, 0x02, 0x0C, 0x71], '0C'); await w(3000);
    return await finStep([0x7C, 0x05, 0x02, 0x0D, 0x70], '0D', 3000);
  };
  let fin = await doFinalizeRound();
  for (let extra = 0; extra < 1 && !(fin.ok && fin.reply.length >= 4 && fin.reply[3] === 15); extra++) {
    if (manager.abort) return _abort(onLog);
    onLog('WARN', `Finalize not ready (0x${fin.reply.length >= 4 ? hex(fin.reply[3]) : '??'}) — retrying…`);
    await w(3000);
    fin = await doFinalizeRound();
  }
  const dur = Date.now() - t0;
  if (fin.ok && fin.reply[3] === 15) {
    onPhase('done');
    onLog('SUCCESS', `WRITE OK (${(dur / 1000).toFixed(1)}s) — restart ECU.`);
    return { result: 'success', duration_ms: dur };
  }
  if (allowSkip) {
    _shindengenSkipBad.add(partStr);
    onLog('ERROR', `Result BAD — blank-skip OFF. Restart ECU (15s), reconnect, WRITE again.`);
  } else {
    onLog('ERROR', 'Result BAD — restart ECU and retry.');
  }
  return { result: 'failed', duration_ms: dur };
}


// ------------------------------------------------------------------ Live data / diagnostics
// Honda K-Line table read: 72 05 71 <table> <cs>. Reply frame: 02 <len> 71 <table> <data...> <cs>.
// Checksum is the two's-complement byte already used by hondaChecksum().
// Live-data read = read-memory-by-address, exactly as the reference tool:
//   72 07 72 <addrHi> <addrLo> <num> <cs>   (cs = hondaChecksum, proven by READ_ID)
// Reply: 02 <len> 72 <addrHi> <addrLo> <data...> <cs>  -> data starts at reply[5].
function liveReadCmd(addr16, num) {
  const b = [0x72, 0x07, 0x72, (addr16 >> 8) & 0xff, addr16 & 0xff, num & 0xff];
  b.push(hondaChecksum(b));
  return b;
}
function withCs(bytes) { const b = bytes.slice(); b.push(hondaChecksum(b)); return b; }

// Live-data RAM blocks extracted from REMAP_HONDA_KLINE.exe (address, length).
// 0x1100/20 is the main sensor block; others are fallbacks.
export const LIVE_TABLES = [
  { addr: 0x1700, num: 15 },
  { addr: 0x1100, num: 20 },
  { addr: 0x1300, num: 14 },
  { addr: 0xD100, num: 6 },
  { addr: 0xD000, num: 14 },
];
// Clear self-diagnosis (DTC) and ECU reset frames (from the reference tool).
export const CMD_CLEAR_DTC = withCs([0x72, 0x05, 0x60, 0x03]); // 72 05 60 03 26
export const CMD_CLEAR_DTC2 = withCs([0x72, 0x05, 0x60, 0x01]); // 72 05 60 01 28
export const CMD_RESET_ECM = withCs([0x72, 0x05, 0x60, 0x03]); // reset = clear self-diag

// Read one memory block by address. Returns { addr, data, raw } or null.
export async function readBlock(manager, addr, num) {
  if (!manager || !manager.connected || manager.simulated) return null;
  const r = await manager.sendFramed(liveReadCmd(addr, num), { timeout: 300, pre: KLINE.BLOCK_GAP });
  if (!(r.ok && r.reply.length >= 7 && r.reply[0] === 0x02)) return null;
  const rep = r.reply;
  const len = rep[1];
  // The reply MUST be the answer to THIS read: service echo 0x72 + the exact address
  // we asked for. Misaligned / foreign / partially-received frames fail this check and
  // are dropped — otherwise their bytes decode into ghost values (RPM climbing on its
  // own while the engine is idle or off). This is the fix for "rpm naik sendiri".
  if (rep[2] !== 0x72 || rep[3] !== ((addr >> 8) & 0xff) || rep[4] !== (addr & 0xff)) return null;
  if (len < 6 || rep.length < len) return null;
  return { addr, data: rep.slice(5, Math.min(len - 1, 5 + num)), raw: rep };
}

// Extra block polled alongside the primary sensor block for Ign Advance / Speed
// calibration (bytes still unknown — shown as RAW to the user, never faked).
// OFF by default to keep the K-Line light; user picks an address to scan.
export const SCAN_TABLES = [
  { addr: 0x1100, num: 20 },
  { addr: 0x1300, num: 14 },
  { addr: 0xD000, num: 14 },
  { addr: 0xD100, num: 6 },
  { addr: 0x2000, num: 16 },
];

// Read one live-data cycle. Returns the primary responding block, plus an
// optional extra block (scanAddr) for calibration. { addr, data, raw, extra }.
export async function readLiveData(manager, tables = LIVE_TABLES, { scanAddr = null, withO2 = false } = {}) {
  if (!manager || !manager.connected || manager.simulated) return null;
  // PRIMARY: Honda diagnostic-table live protocol (72 05 71 <table>) — decoded 1:1 from
  // the open-source Kline_Remap "3D Chart Graph" (Diag.cs). Works across Keihin AND
  // Shindengen Honda ECUs. If the ECU does not answer any diag table we fall back to the
  // legacy read-memory scan below.
  const diag = await readLiveDiag(manager, withO2);
  if (diag) return diag;
  let primary = null;
  // reference-style pacing: lock onto the ONE table that answered, and each tick read
  // ONLY that single block. Re-scanning all 5 tables every tick (the old behaviour)
  // floods the K-Line whenever the primary misses once, and that burst is what makes a
  // running engine hesitate ("brebet"). A transient miss is ignored (no rescan burst);
  // only after 3 consecutive misses do we give up the lock and rescan.
  if (manager._liveTable) {
    primary = await readBlock(manager, manager._liveTable.addr, manager._liveTable.num);
    if (primary) { manager._liveMiss = 0; }
    else {
      manager._liveMiss = (manager._liveMiss || 0) + 1;
      if (manager._liveMiss < 3) return null;       // transient miss — keep last reading, no burst
      manager._liveTable = null;                    // lost the lock — full rescan below
    }
  }
  if (!primary) {
    for (const t of tables) {
      const b = await readBlock(manager, t.addr, t.num);
      if (b) { primary = b; manager._liveTable = t; manager._liveMiss = 0; break; }
    }
  }
  if (!primary) return null;
  let extra = null;
  if (scanAddr != null && scanAddr !== primary.addr) {
    const num = (SCAN_TABLES.find((s) => s.addr === scanAddr) || {}).num || 16;
    extra = await readBlock(manager, scanAddr, num);
    if (!extra) extra = { addr: scanAddr, data: [] };
  }
  let o2 = null;
  if (withO2) {
    o2 = await readBlock(manager, 0x2000, 16);
    if (!o2) o2 = { addr: 0x2000, data: [] };
  }
  return { ...primary, extra, o2 };
}

// Decode the O2 / fuel-trim table 0x2000 (validated vs the reference tool/ECU Workbench):
//   b0 O2 sensor voltage (x5/256 V) · b1 short-term fuel trim (/128, 1.0 = no trim).
export function parseO2(data) {
  const b = (i) => (i < data.length ? data[i] : null);
  const usable = (v) => (v != null && v !== 0xFF) ? v : null;
  const o2v = usable(b(0)) != null ? +(b(0) * 5 / 256).toFixed(3) : null;
  const stft = usable(b(1)) != null ? +(b(1) / 128).toFixed(3) : null;
  return {
    o2v,
    stft,
    trimPct: stft != null ? +(((stft - 1) * 100)).toFixed(1) : null,
    dataLen: data.length,
  };
}

// Decode the 0x1700 live block (calibrated from real captures cross-checked with
// the reference tool on a Scoopy 110 / 30400-K93):
//   b0..b1 RPM(16-bit) · b2 MAP kPa · b3 TPS% · b4 O2(x5/256 V) · b5 eot(-40°C) ·
//   b6..b9 = FF (ect/iat sensor voltage = open on air-cooled) · b10 battery/10 ·
//   b11..b12 injector(/256 ms) · b13 spark_advance((b13-128)/2 °). Speed not in stream.
export function parseLiveData(data) {
  const d = (i) => (i < data.length ? data[i] : null);
  const v16 = (i) => (d(i) != null && d(i + 1) != null) ? ((d(i) << 8) | d(i + 1)) : null;
  const usable = (v) => (v != null && v !== 0xFF) ? v : null;
  const inj = (usable(d(11)) != null && usable(d(12)) != null) ? +(((d(11) << 8) | d(12)) / 256).toFixed(2) : null;
  // RPM plausibility gate: a real Honda bike never exceeds ~15000 rpm. Anything above
  // 20000 (incl. 0xFFFF from an empty/garbled block) is treated as "no reading" so the
  // gauge does not jump or climb on its own.
  const rawRpm = v16(0);
  return {
    rpm: (rawRpm != null && rawRpm >= 0 && rawRpm <= 20000) ? rawRpm : null,
    map: usable(d(2)) != null ? d(2) : null,
    mapV: null,
    tps: usable(d(3)) != null ? Math.max(0, Math.min(100, Math.round(d(3) * 100 / 255))) : null,
    tpsV: null,
    o2: null, // O2 comes from the dedicated 0x2000 table (parseO2) — NOT this Keihin-only byte
    stft: null,
    ect: usable(d(5)) != null ? d(5) - 40 : null,
    ectV: null,
    battery: usable(d(10)) != null ? +(d(10) / 10).toFixed(1) : null,
    injector: inj,
    iat: null,
    iatV: null,
    ignition: usable(d(13)) != null ? +((d(13) - 128) / 2).toFixed(1) : null,
    speed: null,
    dataLen: data.length,
  };
}

// ---------------- Honda diagnostic-table live data (72 05 71 <table>) ----------------
// Protocol + per-table byte layouts decoded 1:1 from the open-source Kline_Remap
// "3D Chart Graph" (Diag.cs). The ECU answers exactly ONE of tables 0x10/0x11/0x13/
// 0x16/0x17; a 5-byte reply (len byte == 0x05) means "table not supported". O2/AFR and
// fuel-trim come from table 0x20. Reply frame: 02 <len> 71 <table> <data...> <cs>, so
// data begins at reply[4] (RPM = reply[4]<<8 | reply[5]).
const DIAG_LIVE_TABLES = [0x11, 0x10, 0x13, 0x16, 0x17, 0x15];

export async function readDiagTable(manager, table) {
  if (!manager || !manager.connected || manager.simulated) return null;
  const r = await manager.sendFramed(withCs([0x72, 0x05, 0x71, table]), { timeout: 300, pre: KLINE.BLOCK_GAP });
  const rep = r.reply;
  if (!(r.ok && rep && rep.length >= 6 && rep[0] === 0x02)) return null;
  if (rep[1] === 0x05) return null;                 // 5-byte NAK => table not supported
  if (rep[2] !== 0x71 || rep[3] !== table) return null;
  return rep;
}

// Decode a diagnostic-table reply into the same shape as parseLiveData().
export function parseDiagLive(rep) {
  const table = rep[3];
  const U = (i) => { const v = rep[i]; return (v != null && v !== 0xFF) ? v : null; };
  const u16 = (i) => (rep[i] != null && rep[i + 1] != null) ? ((rep[i] << 8) | rep[i + 1]) : null;
  const volt = (v) => v == null ? null : +(v * 5 / 256).toFixed(2);
  const pct = (v) => v == null ? null : Math.max(0, Math.min(100, Math.round(v / 2)));
  const degC = (v) => v == null ? null : v - 40;
  const inj = (v) => v == null ? null : +(v / 200).toFixed(2);
  const adv = (v) => v == null ? null : +(v / 2 - 64).toFixed(1);
  const bat = (v) => v == null ? null : +(v / 10).toFixed(1);
  const rawRpm = u16(4);
  const out = {
    rpm: (rawRpm != null && rawRpm >= 0 && rawRpm <= 20000) ? rawRpm : null,
    map: null, mapV: null, tps: null, tpsV: null, o2: null, stft: null,
    ect: null, ectV: null, battery: null, injector: null, iat: null, iatV: null,
    ignition: null, speed: null, table, dataLen: rep.length,
  };
  if (table === 0x10 || table === 0x11 || table === 0x13) {
    out.tpsV = volt(U(6)); out.tps = pct(U(7));
    out.ectV = volt(U(8)); out.ect = degC(U(9));
    out.iatV = volt(U(10)); out.iat = degC(U(11));
    out.mapV = volt(U(12)); out.map = U(13);
    if (table === 0x13) {
      out.battery = bat(U(14)); out.injector = inj(u16(15)); out.ignition = adv(U(17));
    } else {
      out.battery = bat(U(16)); out.speed = U(17); out.injector = inj(u16(18)); out.ignition = adv(U(20));
    }
  } else if (table === 0x16) {
    out.tpsV = volt(U(10)); out.tps = pct(U(12));
    out.ectV = volt(U(15)); out.ect = degC(U(16));
    out.iatV = volt(U(17)); out.iat = degC(U(18));
    out.mapV = volt(U(19)); out.map = U(20);
    out.battery = bat(U(21)); out.speed = U(22); out.injector = inj(U(23)); out.ignition = adv(U(24));
  } else if (table === 0x17) {
    out.tpsV = volt(U(6)); out.tps = pct(U(7));
    const ectVb = (rep[12] != null && rep[12] !== 0xFF) ? rep[12] : rep[8];
    const ectCb = (rep[13] != null && rep[13] !== 0xFF) ? rep[13] : rep[9];
    out.ectV = (ectVb != null && ectVb !== 0xFF) ? +(ectVb * 5 / 256).toFixed(2) : null;
    out.ect = (ectCb != null && ectCb !== 0xFF) ? ectCb - 40 : null;
    out.battery = bat(U(14)); out.injector = inj(u16(15)); out.ignition = adv(U(17)); out.speed = U(19);
  } else if (table === 0x15) {
    // Denso Titan (K2G / 0104190802) 0x15 live block — offsets DECODED from the user's K-line
    // capture (LIVE DATA kline_capture_20261001, 934 frames) + HondaECU Lite reference meters:
    //   rep[4..5]=RPM · rep[6]=TPS V (0x14≈0.39V→0xDC≈4.30V) · rep[7]=throttle angle ·
    //   rep[10/11]=IAT V/°C · rep[12/13]=MAP V/kPa (idle 60 == tool 60) · rep[16]=battery(÷10,
    //   12.6V idle→14.5V revs) · rep[18..19]=injector u16 (→0 on decel fuel-cut) · rep[20]=ignition
    //   advance · rep[21]=ECT (+40). Same layout as the 0x10 HDS table except ECT lives at rep[21].
    out.ect = degC(U(21));
    out.iatV = volt(U(10)); out.iat = degC(U(11));
    out.mapV = volt(U(12)); out.map = U(13);
    out.battery = bat(U(16)); out.injector = inj(u16(18)); out.ignition = adv(U(20));
    if (rep[6] != null && rep[6] !== 0xFF) {
      out.tpsV = +(rep[6] * 5 / 256).toFixed(2);          // raw ADC → volts (×5/256)
      out.tps = Math.max(0, Math.min(100, Math.round((rep[6] - 20) / 2)));  // 0x14→0% .. 0xDC→100%
    }
  }
  return out;
}

// Decode table 0x20 (O2 / short-term fuel trim) — scaling from Kline_Remap dat20().
export function parseDiagO2(rep) {
  const b = (i) => (i < rep.length ? rep[i] : null);
  const o2v = b(4) != null ? +((b(4) & 0xFF) / 50.9937).toFixed(3) : null;
  const stft = b(5) != null ? +((b(5) & 0xFF) / 128.25651).toFixed(3) : null;
  return { o2v, stft, trimPct: stft != null ? +(((stft - 1) * 100)).toFixed(1) : null, dataLen: rep.length };
}

// Read one diagnostic-table live cycle. Locks onto the ONE table the ECU answers (like
// the reference tool disabling unsupported tables) so we never re-flood the K-Line.
async function readLiveDiag(manager, withO2) {
  if (manager._diagOff) return null;                 // ECU has no diag tables — use fallback
  let rep = null;
  if (manager._diagTable != null) {
    rep = await readDiagTable(manager, manager._diagTable);
    if (rep) { manager._diagMiss = 0; }
    else {
      manager._diagMiss = (manager._diagMiss || 0) + 1;
      if (manager._diagMiss < 3) return manager._diagLast || null;   // transient miss — keep last
      manager._diagTable = null;                     // lost lock — re-probe once below
    }
  }
  if (!rep && manager._diagTable == null) {
    for (const t of DIAG_LIVE_TABLES) {
      const r = await readDiagTable(manager, t);
      if (r) { rep = r; manager._diagTable = t; manager._diagMiss = 0; break; }
    }
    if (!rep) { manager._diagOff = true; return null; }   // one-shot probe failed — disable diag
  }
  const parsed = parseDiagLive(rep);
  let o2 = null;
  if (withO2) {
    const o = await readDiagTable(manager, 0x20);
    if (o) o2 = { addr: 0x20, data: [], diag: parseDiagO2(o) };
  }
  const res = { addr: rep[3], data: [], raw: rep, parsed, extra: null, o2 };
  manager._diagLast = res;
  return res;
}

// Decode DTC entries from a diagnostic table payload as [code, count] pairs.
function decodeDtc(data) {
  const out = [];
  for (let i = 0; i + 1 < data.length; i += 2) {
    const code = data[i];
    if (code !== 0x00 && code !== 0xff) out.push({ code, count: data[i + 1] });
  }
  return out;
}

// Read DTCs from Honda self-diagnosis tables 0x74 (current) and 0x73 (past),
// sub-blocks 1/2/3. Codes are stored as (code, count) byte pairs; 0,0 = end.
export async function readDtc(manager) {
  const out = { current: [], stored: [], raw: [] };
  if (!manager || !manager.connected || manager.simulated) return out;
  for (const [key, tbl] of [['current', 0x74], ['stored', 0x73]]) {
    let data = [];
    for (const sub of [1, 2, 3]) {
      const r = await manager.sendFramed(withCs([0x72, 0x05, tbl, sub]), { timeout: 500, pre: KLINE.P2 });
      if (r.reply && r.reply.length) out.raw.push(`${tbl.toString(16)}/${sub}: ${r.reply.map(hex).join(' ')}`);
      if (r.ok && r.reply.length > 5 && r.reply[0] === 0x02) data = data.concat(Array.from(r.reply.slice(4, r.reply[1] - 1)));
    }
    out[key] = decodeDtc(data);
  }
  return out;
}

async function sendAck(manager, cmd, timeout = 1000) {
  if (!manager || !manager.connected || manager.simulated) return { ok: false, reply: [], raw: [] };
  const r = await manager.sendFramed(cmd, { timeout, pre: KLINE.P3 });
  return { ok: !!r.ok, reply: r.reply || [], raw: r.raw || [], hex: (r.raw || []).map(hex).join(' ') };
}

// Clear stored DTCs (Honda self-diagnosis clear: 60 03 then 60 01).
export async function clearDtc(manager) {
  const r1 = await sendAck(manager, CMD_CLEAR_DTC);
  const r2 = await sendAck(manager, CMD_CLEAR_DTC2);
  return { ok: r1.ok || r2.ok, reply: r2.reply, raw: r2.raw, hex: `${r1.hex} | ${r2.hex}` };
}
// Reset the ECM. Mirrors the AutoShop Shindengen capture: a full session is opened
// first (BREAK → FE 04 72 8C wake → 72 05 00 F0 99 StartComm) and only THEN the reset
// frame 72 05 60 03 26 is sent — sending it bare (no handshake) is silently ignored by
// Shindengen. The ECU acknowledges with a 02 05 60 xx frame, then reboots (cycle
// ignition OFF/ON afterwards).
export async function resetEcm(manager) {
  if (!manager || !manager.connected || manager.simulated) return { ok: false, reply: [], raw: [], hex: '' };
  try { await manager.klineWake(70, 140); } catch (_) {}
  await manager.sendFramed(CMD.WAKEUP, { timeout: 400, pre: KLINE.P3 }).catch(() => {});
  await manager.sendFramed(CMD.INIT_ECM, { timeout: 400, pre: KLINE.P2 }).catch(() => {});
  const r = await manager.sendFramed(CMD_RESET_ECM, { timeout: 1000, pre: KLINE.P3 });
  const reply = r.reply || [];
  const raw = r.raw || [];
  const ok = reply.length >= 3 && reply[0] === 0x02 && reply[2] === 0x60;
  return { ok, reply, raw, hex: raw.map(hex).join(' ') };
}

// Smart Key: EEPROM read handshake (wake + init + ISO security + 82 82 10 06 page reads).
// Returns collected raw bytes. Hardware-dependent; frames from Honda Flash Tools.
// Faithful Honda immobiliser EEPROM read (SPECIALIST_PH_TUNER SMK.eeprom): wake +
// init + two ISO security frames, then 256 reads collecting 2 data bytes each = a
// 512-byte EEPROM dump. The frames already carry their own trailing checksum, so
// they are sent raw. The native FTDI bridge returns only the ECU reply; Web Serial
// mirrors the TX bytes on the bus, so the leading echo is stripped when present.
export async function smartKeyRead(manager, onLog, onProgress) {
  if (!manager || !manager.connected || manager.simulated) throw new Error('Connect an ECU first');

  const rawExchange = async (cmd, timeout = 120) => {
    if (manager.mode === 'bridge') return Array.from(await manager.bridge.writeRead(cmd, timeout));
    manager._rx = [];
    await manager.write(cmd);
    const start = Date.now();
    while (Date.now() - start < timeout) {
      if (manager._rx.length >= cmd.length + 6) break;
      await sleep(4);
    }
    let raw = manager._rx.slice();
    if (raw.length >= cmd.length && raw.slice(0, cmd.length).every((b, i) => b === cmd[i])) raw = raw.slice(cmd.length);
    return raw;
  };

  const WAKE = [0xFE, 0x04, 0x72, 0x8C];
  const INIT = [0x72, 0x05, 0x00, 0xF0, 0x99];
  const SEC1 = [0x91, 0x91, 0x0D, 0xDF, 0x9E, 0x8D, 0x9A, 0x86, 0x90, 0x8A, 0x8C, 0x9B, 0x88];
  const SEC2 = [0x91, 0x91, 0x0D, 0xDF, 0x92, 0x9E, 0x86, 0x96, 0x8B, 0x8D, 0x86, 0xC0, 0x6A];
  // Shindengen read: 91 91 07 40 <page> 00 <cksum>, cksum = (0x100 - (0x07+0x40+page)) & 0xFF.
  const readCmd = (page) => [0x91, 0x91, 0x07, 0x40, page & 0xFF, 0x00, (0x100 - ((0x07 + 0x40 + page) & 0xFF)) & 0xFF];

  onLog('UART', 'Smart Key: K-Line hardware wake (BREAK)...');
  manager._rx = [];
  await manager.klineWake(70, 140);

  onLog('UART', 'Smart Key: wake + init + ISO security handshake...');
  for (const [c, gap] of [[WAKE, 150], [INIT, 150], [SEC1, 150], [SEC2, 0]]) {
    const r = await rawExchange(c, 400);
    onLog('UART', `TX ${c.map(hex).join(' ')} | RX ${r.length ? r.map(hex).join(' ') : '(none)'}`);
    if (gap) await sleep(gap);
  }

  const bytes = [];
  let valid = 0;
  const samples = [];
  for (let i = 0; i < 256; i++) {
    if (manager.abort) break;
    const cmd = readCmd(i);
    const r = await rawExchange(cmd, 150);
    if (i < 3) {
      const line = `Read #${i} (TX ${cmd.map(hex).join(' ')}): ${r.length ? r.map(hex).join(' ') : '(none)'}`;
      samples.push(line);
      onLog('UART', `${line} → data [4][5]=${r.length >= 6 ? hex(r[4]) + ' ' + hex(r[5]) : 'n/a'}`);
    }
    if (r.length >= 6) { bytes.push(r[4], r[5]); valid++; }
    else bytes.push(0, 0);
    await sleep(30);
    onProgress && onProgress(Math.round(((i + 1) / 256) * 100));
  }
  onLog('INFO', `Smart Key EEPROM: ${valid}/256 (${bytes.length} B)`);
  return { bytes, valid, mode: manager.mode, samples };
}
